import React, { useState, useEffect, useContext, createContext, useRef, useImperativeHandle } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, Navigate } from "react-router-dom";
import { FiHome, FiDollarSign, FiPieChart, FiRefreshCw, FiTrendingUp, FiTrendingDown, FiPlusCircle, FiMinusCircle, FiTrash2, FiLogOut, FiDatabase, FiEdit, FiUser, FiClock, FiPlus, FiChevronLeft, FiChevronRight, FiSettings, FiSave, FiCheck, FiX, FiArchive, FiFileText } from "react-icons/fi";
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line as RechartsLine, Tooltip as RechartsTooltip, Legend as RechartsLegend } from "recharts";
import axios from "axios";
import { Chart, registerables } from 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import { de } from 'date-fns/locale';
import zoomPlugin from 'chartjs-plugin-zoom';
import ResetZoomButton from './Button';
import "./App.css";
import "./animations.css";

// Register Chart.js components and plugins
Chart.register(...registerables, zoomPlugin);

// Auth context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const [loading, setLoading] = useState(true);
  const [adminPermissions, setAdminPermissions] = useState(null); // Store admin-configured permissions

  // Fetch admin permissions for current user's role
  const fetchUserPermissions = async (authToken, userRole) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/user/permissions`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      const userPermissions = response.data.permissions || [];
      
      console.log("User permissions loaded:", { userRole, userPermissions });
      setAdminPermissions(userPermissions);
      return userPermissions;
    } catch (error) {
      console.error("Error fetching user permissions:", error);
      // Fallback to default permissions if endpoint fails
      const defaultPermissions = getDefaultPermissions(userRole);
      setAdminPermissions(defaultPermissions);
      return defaultPermissions;
    }
  };

  // Default permissions fallback
  const getDefaultPermissions = (roleLevel) => {
    const defaults = {
      "admin": ["dashboard", "transactions", "debt", "defi_position", "vault_tracking", "indicators", "trade_journal", "marktstruktur", "profile", "preis_alarme"],
      "vip": ["dashboard", "transactions", "debt", "defi_position", "vault_tracking", "indicators", "trade_journal", "marktstruktur", "profile"],
      "vip_plus": ["dashboard", "transactions", "debt", "defi_position", "vault_tracking", "indicators", "trade_journal", "marktstruktur", "profile"],
      "vip_star": ["dashboard", "transactions", "debt", "defi_position", "vault_tracking", "indicators", "trade_journal", "marktstruktur", "profile"],
      "vip_plus_plus": ["dashboard", "transactions", "debt", "defi_position", "vault_tracking", "indicators", "trade_journal", "marktstruktur", "profile"],
      "dash_t1": ["dashboard", "transactions", "debt", "defi_position", "vault_tracking", "profile"],
      "dash_t2": ["dashboard", "transactions", "debt", "defi_position", "vault_tracking", "indicators", "trade_journal", "marktstruktur", "profile"],
      "dashboard_t1": ["dashboard", "transactions", "debt", "defi_position", "vault_tracking", "profile"],
      "dashboard_t2": ["dashboard", "transactions", "debt", "defi_position", "vault_tracking", "indicators", "trade_journal", "marktstruktur", "profile"]
    };
    
    return defaults[roleLevel] || ["dashboard", "transactions", "debt", "defi_position", "vault_tracking", "profile"];
  };

  const verifyDiscordUser = async (authToken) => {
    try {
      console.log("Verifying Discord user roles...");
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/discord/verify`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      console.log("Discord verification successful:", response.data);
      const userData = {
        ...response.data.user,
        role_level: response.data.role_level,
        permissions: response.data.permissions
      };
      
      setUser(userData);
      setIsAuthenticated(true);
      
      // Fetch admin-configured permissions for this user's role
      await fetchUserPermissions(authToken, response.data.role_level);
      
      return true;
    } catch (error) {
      console.error("Discord verification failed:", error);
      console.error("Error response:", error.response?.data);
      
      if (error.response?.status === 403) {
        console.log("User no longer has required Discord roles, logging out...");
      } else if (error.response?.status === 400) {
        console.log("Not a Discord user, falling back to regular user fetch...");
        return false; // Not a Discord user, try regular user fetch
      }
      
      // Clear auth state for failed verification
      localStorage.removeItem("token");
      setToken(null);
      setIsAuthenticated(false);
      setUser(null);
      setAdminPermissions(null);
      return true; // Return true to prevent further auth attempts
    }
  };

  const fetchCurrentUser = async (authToken) => {
    try {
      console.log("Fetching current user with token:", authToken ? "Token present" : "No token");
      
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      console.log("User fetch successful:", response.data);
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      console.error("Error fetching user:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      
      if (error.response?.status === 403) {
        console.log("User is not a Discord user or doesn't have permission, logging out...");
      }
      
      // Clear auth state for any error
      localStorage.removeItem("token");
      setToken(null);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    
    // Check for Discord callback token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    const urlError = urlParams.get('error');
    
    if (urlToken) {
      console.log("Discord token received:", urlToken.substring(0, 20) + "...");
      
      // Remove token from URL immediately
      window.history.replaceState({}, document.title, window.location.pathname);
      
      localStorage.setItem("token", urlToken);
      setToken(urlToken);
      setIsAuthenticated(true);
      
      // Create a dummy user object for Discord users to prevent errors
      setUser({
        email: "discord_user@discord.com",
        name: "Discord User",
        auth_provider: "discord"
      });
      
      setLoading(false);
      
      // Force immediate navigation to dashboard
      console.log("Navigating to dashboard...");
      window.location.replace("/");
      return;
    }
    
    if (urlError) {
      // Handle Discord authentication errors
      let errorMessage = "Authentifizierung fehlgeschlagen";
      switch (urlError) {
        case 'insufficient_permissions':
          errorMessage = "Keine Berechtigung: Du benötigst die erforderliche Rolle im Discord Server";
          break;
        case 'discord_auth_failed':
          errorMessage = "Discord-Authentifizierung fehlgeschlagen";
          break;
        case 'no_code':
          errorMessage = "Keine Autorisierung von Discord erhalten";
          break;
        case 'token_exchange_failed':
          errorMessage = "Token-Austausch mit Discord fehlgeschlagen";
          break;
        case 'user_info_failed':
          errorMessage = "Benutzerinformationen von Discord konnten nicht abgerufen werden";
          break;
        case 'internal_error':
          errorMessage = "Interner Server-Fehler";
          break;
        default:
          errorMessage = `Authentifizierung fehlgeschlagen: ${urlError}`;
      }
      
      console.error("Discord auth error:", errorMessage);
      // Clear the URL
      window.history.replaceState({}, document.title, window.location.pathname);
      setLoading(false);
      return;
    }
    
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      setToken(storedToken);
      setIsAuthenticated(true);
      
      // Try Discord verification first, then fallback to regular user fetch
      verifyDiscordUser(storedToken)
        .then(isDiscordUser => {
          if (!isDiscordUser) {
            // Not a Discord user, try regular user fetch but expect failure
            console.log("Checking if this is a non-Discord user...");
            fetchCurrentUser(storedToken);
          }
          // If isDiscordUser is true, Discord verification was successful
        })
        .catch(err => {
          console.log("User verification failed, clearing auth...", err);
          
          // Clear auth state - this will redirect to login
          localStorage.removeItem("token");
          setToken(null);
          setIsAuthenticated(false);
          setUser(null);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const formData = new FormData();
      formData.append("username", email);
      formData.append("password", password);

      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/token`, formData);
      const { access_token } = response.data;
      
      if (!access_token) {
        console.error("No access token received");
        return false;
      }
      
      localStorage.setItem("token", access_token);
      setToken(access_token);
      
      await verifyDiscordUser(access_token);
      return true;
    } catch (error) {
      console.error("Login error:", error);
      return false;
    }
  };

  const googleLogin = async (tokenId) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google`, {
        token: tokenId
      });
      
      const { access_token } = response.data;
      
      if (!access_token) {
        console.error("No access token received from Google authentication");
        return false;
      }
      
      localStorage.setItem("token", access_token);
      setToken(access_token);
      
      await fetchCurrentUser(access_token);
      return true;
    } catch (error) {
      console.error("Google login error:", error);
      return false;
    }
  };

  const discordLogin = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/discord`);
      
      if (response.data.authorization_url) {
        // Redirect to Discord OAuth
        window.location.href = response.data.authorization_url;
        return true;
      } else {
        console.error("No authorization URL received from Discord");
        return false;
      }
    } catch (error) {
      console.error("Discord login error:", error);
      return false;
    }
  };

  const register = async (email, password, confirmPassword) => {
    try {
      if (password !== confirmPassword) {
        console.error("Passwords do not match");
        return false;
      }
      
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        email,
        password,
        confirm_password: confirmPassword
      });
      
      // Check if registration was successful
      if (!response.data || !response.data.id) {
        console.error("Registration failed: Invalid response data");
        return false;
      }
      
      // Automatically log in after successful registration
      return await login(email, password);
    } catch (error) {
      console.error("Registration error:", error);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setAdminPermissions(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, googleLogin, discordLogin, register, logout, loading, adminPermissions }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for auth
const useAuth = () => useContext(AuthContext);

// Private route component
const PrivateRoute = ({ element }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div className="flex items-center justify-center h-screen">Wird geladen...</div>;
  }
  
  return isAuthenticated ? element : <Navigate to="/login" />;
};

// Login Page
const Login = () => {
  const { discordLogin } = useAuth();
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleDiscordLogin = async () => {
    setError("");
    setIsLoading(true);
    
    const success = await discordLogin();
    setIsLoading(false);
    
    if (!success) {
      setError("Discord-Authentifizierung fehlgeschlagen");
    }
    // Note: If successful, user will be redirected to Discord, then back with token
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="max-w-md w-full space-y-8 p-10 bg-gray-800 rounded-xl shadow-lg scale-in hover-lift">
        <div className="text-center">
          <img src="/images/mk_logo.png" alt="M&K Logo" className="h-24 mb-4 fade-in mx-auto" />
          <h2 className="text-4xl font-extrabold text-white mb-2 fade-in">
            M&K
          </h2>
          <div className="w-full h-1 bg-yellow-600 mb-4 pulse-gold"></div>
          <h2 className="text-2xl font-extrabold text-white mb-6 fade-in">
            CRYPTO DASHBOARD
          </h2>
          <p className="text-gray-400 text-sm mb-6">
            Melde dich mit Discord an
          </p>
        </div>

        {error && (
          <div className="bg-red-900 border-l-4 border-red-500 text-red-300 p-4 mb-6 slide-in">
            <p>{error}</p>
          </div>
        )}

        <div className="flex justify-center">
          <button
            onClick={handleDiscordLogin}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
            Mit Discord anmelden
          </button>
        </div>

        <div className="text-center">
          <p className="text-sm text-gray-400">
            Nur Mitglieder mit VIP-Status haben Zugang zu diesem Dashboard.
          </p>
        </div>
      </div>
    </div>
  );
};

// Register Page - Redirect to Login (Discord-only)
const Register = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Redirect to login page since only Discord login is allowed
    navigate("/login");
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
        <p className="text-white mt-4">Weiterleitung zur Anmeldung...</p>
      </div>
    </div>
  );
};

// Sidebar Component with responsive burger menu and collapsible desktop sidebar
const Sidebar = ({ active }) => {
  const { logout, user, adminPermissions } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopCollapsed, setIsDesktopCollapsed] = useState(() => {
    // Initialize from localStorage
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [restrictedFeature, setRestrictedFeature] = useState("");
  const [rolesWithAccessToFeature, setRolesWithAccessToFeature] = useState([]);
  const [upgradeModalSettings, setUpgradeModalSettings] = useState(null);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleDesktopSidebar = () => {
    const newState = !isDesktopCollapsed;
    setIsDesktopCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', newState.toString());
    
    // Update CSS custom property to adjust content margin
    document.documentElement.style.setProperty(
      '--sidebar-width', 
      newState ? '4rem' : '16rem'
    );
  };

  // Set initial CSS custom property
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-width', 
      isDesktopCollapsed ? '4rem' : '16rem'
    );
  }, [isDesktopCollapsed]);

  // Check if user has access to a feature
  const hasAccess = (feature) => {
    // If admin permissions are loaded, use them; otherwise fall back to user.permissions
    if (adminPermissions) {
      return adminPermissions.includes(feature);
    }
    
    // Fallback to original permissions for non-Discord users or while loading
    if (!user || !user.permissions) return true; // Default allow for non-Discord users
    return user.permissions.includes(feature);
  };

  // Check if profile should be completely hidden
  const isProfileHidden = () => {
    if (adminPermissions) {
      return adminPermissions.includes("profile_hidden");
    }
    // Default: don't hide profile
    return false;
  };

  // Check if vault tracking should be completely hidden
  const isVaultTrackingHidden = () => {
    if (adminPermissions) {
      return adminPermissions.includes("vault_tracking_hidden");
    }
    // Default: don't hide vault tracking
    return false;
  };

  // Check if preis-alarme should be completely hidden
  const isPreisAlarmeHidden = () => {
    if (adminPermissions) {
      return adminPermissions.includes("preis_alarme_hidden");
    }
    // Default: don't hide preis-alarme
    return false;
  };

  // Check if user is admin
  const isAdmin = () => {
    return user && user.role_level === "admin";
  };

  // Handle restricted feature click
  const handleRestrictedClick = async (featureName) => {
    setRestrictedFeature(featureName);
    setShowUpgradeModal(true);
    
    // Fetch roles with access to this feature
    const roles = await getRolesWithAccess(featureName);
    setRolesWithAccessToFeature(roles);
    
    // Load upgrade modal settings if not already loaded
    if (!upgradeModalSettings) {
      await loadUpgradeModalSettings();
    }
  };

  // Close upgrade modal
  const closeUpgradeModal = () => {
    setShowUpgradeModal(false);
    setRestrictedFeature("");
    setRolesWithAccessToFeature([]);
  };

  // Get current user's role name for display
  const getCurrentRoleName = () => {
    if (!user || !user.role_level) return "Unbekannt";
    
    const roleNames = {
      "admin": "Admin",
      "vip": "VIP", 
      "vip_plus": "VIP+",
      "vip_star": "VIP*",
      "vip_plus_plus": "VIP++",
      "dash_t1": "Dash T1",
      "dash_t2": "Dash T2", 
      "dashboard_t1": "Dashboard T1",
      "dashboard_t2": "Dashboard T2"
    };
    
    return roleNames[user.role_level] || user.role_level;
  };

  // Get current user's permissions as readable list
  const getCurrentUserPermissions = () => {
    const featureNames = {
      "dashboard": "Dashboard",
      "transactions": "Transaktionen", 
      "debt": "Schulden-Tracker",
      "defi_position": "DeFi Position",
      "vault_tracking": "Vault Tracking",
      "indicators": "Indikatoren",
      "trade_journal": "Trade Journal",
      "marktstruktur": "Marktstruktur", 
      "profile": "Profil",
      "preis_alarme": "Preis-Alarme"
    };
    
    if (adminPermissions) {
      return adminPermissions
        .filter(permission => featureNames[permission]) // Filter out non-UI features like profile_hidden
        .map(permission => featureNames[permission] || permission);
    }
    
    // Fallback to default permissions for display
    if (!user || !user.permissions) {
      return ["Dashboard", "Transaktionen", "Schulden-Tracker", "DeFi Position", "Profil"];
    }
    
    return user.permissions
      .filter(permission => featureNames[permission])
      .map(permission => featureNames[permission] || permission);
  };

  // Get roles that have access to a specific feature (from cache database)
  const getRolesWithAccess = async (featureName) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/feature-roles/${encodeURIComponent(featureName)}`);
      const roles = response.data.roles || [];
      
      console.log(`Roles with access to ${featureName}:`, roles);
      return roles;
    } catch (error) {
      console.error("Error fetching roles with access:", error);
      // Fallback: return only VIP roles
      return ["VIP++"];
    }
  };

  // Load upgrade modal settings from admin panel
  const loadUpgradeModalSettings = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/settings/upgrade-modal`);
      const settings = response.data.settings || {};
      
      console.log("Upgrade modal settings loaded:", settings);
      setUpgradeModalSettings(settings);
      return settings;
    } catch (error) {
      console.error("Error loading upgrade modal settings:", error);
      // Fallback settings
      const fallbackSettings = {
        membership_url: "https://ebel2x.com",
        dashboard_t2_url: "",
        membership_space_url: "https://vip.ebel2x.com/",
        upgrade_modal_title: "🔒 Upgrade erforderlich",
        upgrade_modal_description: "Der Bereich ist nur für höhere Level verfügbar.",
        membership_button_text: "Jetzt Membership beitreten",
        dashboard_t2_button_text: "Dashboard T2 Vollen Zugang"
      };
      setUpgradeModalSettings(fallbackSettings);
      return fallbackSettings;
    }
  };

  // Get next upgrade role for current user
  const getNextUpgradeRole = () => {
    if (!user || !user.role_level) return null;
    
    const upgradeMap = {
      "vip": "VIP+",
      "vip_plus": "VIP++", // Skip VIP* go directly to VIP++
      "vip_star": "VIP++",
      "dashboard_t1": "Dashboard T2",
      "dash_t1": "Dashboard T2" // Legacy Dash T1 -> Dashboard T2
    };
    
    return upgradeMap[user.role_level] || null;
  };

  // Get minimum VIP role needed for access to the restricted feature
  const getMinimumVIPRole = (rolesWithAccess) => {
    const vipHierarchy = ["VIP", "VIP+", "VIP++"];
    
    for (const role of vipHierarchy) {
      if (rolesWithAccess.includes(role)) {
        return role;
      }
    }
    return "VIP+"; // Default fallback
  };

  // Get minimum Dashboard role needed for access
  const getMinimumDashboardRole = (rolesWithAccess) => {
    if (rolesWithAccess.includes("Dashboard T1")) {
      return "Dashboard T1";
    }
    if (rolesWithAccess.includes("Dashboard T2")) {
      return "Dashboard T2";
    }
    return "Dashboard T2"; // Default fallback
  };

  // Check if user should see membership button
  const shouldShowMembershipButton = () => {
    return true; // Always show for all users
  };

  // Check if user should see dashboard T2 button  
  const shouldShowDashboardButton = () => {
    return true; // Always show for all users
  };

  return (
    <>
      {/* Mobile Burger Button - only visible on mobile */}
      <button
        onClick={toggleMobileMenu}
        className="md:hidden fixed top-4 left-4 z-50 bg-gray-900 text-white p-2 rounded-lg shadow-lg hover:bg-gray-800 transition-colors"
      >
        <div className="w-6 h-6 flex flex-col justify-center items-center">
          <span className={`bg-white block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${isMobileMenuOpen ? 'rotate-45 translate-y-1' : '-translate-y-0.5'}`}></span>
          <span className={`bg-white block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm my-0.5 ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
          <span className={`bg-white block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${isMobileMenuOpen ? '-rotate-45 -translate-y-1' : 'translate-y-0.5'}`}></span>
        </div>
      </button>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={closeMobileMenu}
        ></div>
      )}

      {/* Sidebar */}
      <div className={`
        h-screen bg-gray-900 text-white p-4 fixed left-0 top-0 z-40 transition-all duration-300 ease-in-out
        md:translate-x-0 
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        ${isDesktopCollapsed ? 'md:w-16' : 'md:w-64'} w-64
      `}>
        <div className="flex flex-col h-full">
          {/* Header with toggle button for desktop */}
          <div className="flex items-center justify-between mb-8 mt-6">
            <div className={`flex flex-col items-center fade-in ml-4 ${isDesktopCollapsed ? 'md:hidden' : ''}`}>
              <h1 className="text-4xl font-bold text-white mb-1">M&K</h1>
              <div className="w-full h-1 bg-yellow-600 mb-2 pulse-gold"></div>
              <h2 className="text-xl font-bold text-white">CRYPTO</h2>
              <h2 className="text-xl font-bold text-white">DASHBOARD</h2>
            </div>
            
            {/* Collapsed logo for desktop */}
            <div className={`flex flex-col items-center fade-in ${isDesktopCollapsed ? 'md:block' : 'md:hidden'} hidden`}>
              <h1 className="text-2xl font-bold text-white mb-1">M&K</h1>
              <div className="w-full h-1 bg-yellow-600 pulse-gold"></div>
            </div>
            
            {/* Desktop Toggle Button */}
            <button
              onClick={toggleDesktopSidebar}
              className="hidden md:block text-gray-400 hover:text-white transition-colors p-1 rounded"
              title={isDesktopCollapsed ? "Sidebar erweitern" : "Sidebar einklappen"}
            >
              {isDesktopCollapsed ? <FiChevronRight size={20} /> : <FiChevronLeft size={20} />}
            </button>
          </div>
          
          <div className="mb-6 mt-4 flex-1">
            <nav>
              <ul className="space-y-2">
                <li>
                  {hasAccess("dashboard") ? (
                    <Link
                      to="/"
                      onClick={closeMobileMenu}
                      className={`flex items-center p-2 rounded hover-glow transition-all ${
                        active === "dashboard" ? "bg-yellow-600 text-white" : "text-gray-400 hover:bg-gray-800 hover:text-white"
                      }`}
                      title="Dashboard"
                    >
                      <FiHome className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                      <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Dashboard</span>
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleRestrictedClick("Dashboard")}
                      className="flex items-center p-2 rounded hover-glow transition-all text-gray-500 hover:bg-gray-800 hover:text-gray-400 w-full text-left"
                      title="Dashboard (Upgrade erforderlich)"
                    >
                      <FiHome className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                      <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Dashboard</span>
                      <span className={`ml-auto text-xs text-yellow-500 ${isDesktopCollapsed ? 'md:hidden' : ''}`}>🔒</span>
                    </button>
                  )}
                </li>
                <li>
                  {hasAccess("transactions") ? (
                    <Link
                      to="/transactions"
                      onClick={closeMobileMenu}
                      className={`flex items-center p-2 rounded hover-glow transition-all ${
                        active === "transactions" ? "bg-yellow-600 text-white" : "text-gray-400 hover:bg-gray-800 hover:text-white"
                      }`}
                      title="Transaktionen"
                    >
                      <FiDollarSign className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                      <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Transaktionen</span>
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleRestrictedClick("Transaktionen")}
                      className="flex items-center p-2 rounded hover-glow transition-all text-gray-500 hover:bg-gray-800 hover:text-gray-400 w-full text-left"
                      title="Transaktionen (Upgrade erforderlich)"
                    >
                      <FiDollarSign className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                      <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Transaktionen</span>
                      <span className={`ml-auto text-xs text-yellow-500 ${isDesktopCollapsed ? 'md:hidden' : ''}`}>🔒</span>
                    </button>
                  )}
                </li>
                <li>
                  {hasAccess("debt") ? (
                    <Link
                      to="/debt"
                      onClick={closeMobileMenu}
                      className={`flex items-center p-2 rounded hover-glow transition-all ${
                        active === "debt" ? "bg-yellow-600 text-white" : "text-gray-400 hover:bg-gray-800 hover:text-white"
                      }`}
                      title="Schulden-Tracker"
                    >
                      <FiTrendingDown className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                      <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Schulden-Tracker</span>
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleRestrictedClick("Schulden-Tracker")}
                      className="flex items-center p-2 rounded hover-glow transition-all text-gray-500 hover:bg-gray-800 hover:text-gray-400 w-full text-left"
                      title="Schulden-Tracker (Upgrade erforderlich)"
                    >
                      <FiTrendingDown className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                      <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Schulden-Tracker</span>
                      <span className={`ml-auto text-xs text-yellow-500 ${isDesktopCollapsed ? 'md:hidden' : ''}`}>🔒</span>
                    </button>
                  )}
                </li>
                <li>
                  {hasAccess("defi_position") ? (
                    <Link
                      to="/defi-position"
                      onClick={closeMobileMenu}
                      className={`flex items-center p-2 rounded hover-glow transition-all ${
                        active === "defi-position" ? "bg-yellow-600 text-white" : "text-gray-400 hover:bg-gray-800 hover:text-white"
                      }`}
                      title="DeFi Position"
                    >
                      <FiDatabase className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                      <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>DeFi Position</span>
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleRestrictedClick("DeFi Position")}
                      className="flex items-center p-2 rounded hover-glow transition-all text-gray-500 hover:bg-gray-800 hover:text-gray-400 w-full text-left"
                      title="DeFi Position (Upgrade erforderlich)"
                    >
                      <FiDatabase className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                      <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>DeFi Position</span>
                      <span className={`ml-auto text-xs text-yellow-500 ${isDesktopCollapsed ? 'md:hidden' : ''}`}>🔒</span>
                    </button>
                  )}
                </li>
                <li>
                  {hasAccess("indicators") ? (
                    <Link
                      to="/indicators"
                      onClick={closeMobileMenu}
                      className={`flex items-center p-2 rounded hover-glow transition-all ${
                        active === "indicators" ? "bg-yellow-600 text-white" : "text-gray-400 hover:bg-gray-800 hover:text-white"
                      }`}
                      title="Indikatoren"
                    >
                      <FiTrendingUp className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                      <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Indikatoren</span>
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleRestrictedClick("Indikatoren")}
                      className="flex items-center p-2 rounded hover-glow transition-all text-gray-500 hover:bg-gray-800 hover:text-gray-400 w-full text-left"
                      title="Indikatoren (Upgrade erforderlich)"
                    >
                      <FiTrendingUp className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                      <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Indikatoren</span>
                      <span className={`ml-auto text-xs text-yellow-500 ${isDesktopCollapsed ? 'md:hidden' : ''}`}>🔒</span>
                    </button>
                  )}
                </li>
                <li>
                  {hasAccess("marktstruktur") ? (
                    <Link
                      to="/marktstruktur"
                      onClick={closeMobileMenu}
                      className={`flex items-center p-2 rounded hover-glow transition-all ${
                        active === "marktstruktur" ? "bg-yellow-600 text-white" : "text-gray-400 hover:bg-gray-800 hover:text-white"
                      }`}
                      title="Marktstruktur"
                    >
                      <FiTrendingUp className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                      <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Marktstruktur</span>
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleRestrictedClick("Marktstruktur")}
                      className="flex items-center p-2 rounded hover-glow transition-all text-gray-500 hover:bg-gray-800 hover:text-gray-400 w-full text-left"
                      title="Marktstruktur (Upgrade erforderlich)"
                    >
                      <FiTrendingUp className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                      <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Marktstruktur</span>
                      <span className={`ml-auto text-xs text-yellow-500 ${isDesktopCollapsed ? 'md:hidden' : ''}`}>🔒</span>
                    </button>
                  )}
                </li>
                <li>
                  {hasAccess("trade_journal") ? (
                    <Link
                      to="/trade-journal"
                      onClick={closeMobileMenu}
                      className={`flex items-center p-2 rounded hover-glow transition-all ${
                        active === "trade-journal" ? "bg-yellow-600 text-white" : "text-gray-400 hover:bg-gray-800 hover:text-white"
                      }`}
                      title="Trade Journal"
                    >
                      <FiEdit className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                      <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Trade Journal</span>
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleRestrictedClick("Trade Journal")}
                      className="flex items-center p-2 rounded hover-glow transition-all text-gray-500 hover:bg-gray-800 hover:text-gray-400 w-full text-left"
                      title="Trade Journal (Upgrade erforderlich)"
                    >
                      <FiEdit className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                      <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Trade Journal</span>
                      <span className={`ml-auto text-xs text-yellow-500 ${isDesktopCollapsed ? 'md:hidden' : ''}`}>🔒</span>
                    </button>
                  )}
                </li>
                {!isPreisAlarmeHidden() && (
                <li>
                  {hasAccess("preis_alarme") ? (
                    <Link
                      to="/preis-alarme"
                      onClick={closeMobileMenu}
                      className={`flex items-center p-2 rounded hover-glow transition-all ${
                        active === "preis-alarme" ? "bg-yellow-600 text-white" : "text-gray-400 hover:bg-gray-800 hover:text-white"
                      }`}
                      title="Preis-Alarme"
                    >
                      <FiClock className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                      <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Preis-Alarme</span>
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleRestrictedClick("Preis-Alarme")}
                      className="flex items-center p-2 rounded hover-glow transition-all text-gray-500 hover:bg-gray-800 hover:text-gray-400 w-full text-left"
                      title="Preis-Alarme (Upgrade erforderlich)"
                    >
                      <FiClock className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                      <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Preis-Alarme</span>
                      <span className={`ml-auto text-xs text-yellow-500 ${isDesktopCollapsed ? 'md:hidden' : ''}`}>🔒</span>
                    </button>
                  )}
                </li>
                )}
                {!isVaultTrackingHidden() && (
                <li>
                  {hasAccess("vault_tracking") ? (
                    <Link
                      to="/vault-tracking"
                      onClick={closeMobileMenu}
                      className={`flex items-center p-2 rounded hover-glow transition-all ${
                        active === "vault-tracking" ? "bg-yellow-600 text-white" : "text-gray-400 hover:bg-gray-800 hover:text-white"
                      }`}
                      title="Vault Tracking"
                    >
                      <FiArchive className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                      <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Vault Tracking</span>
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleRestrictedClick("Vault Tracking")}
                      className="flex items-center p-2 rounded hover-glow transition-all text-gray-500 hover:bg-gray-800 hover:text-gray-400 w-full text-left"
                      title="Vault Tracking (Upgrade erforderlich)"
                    >
                      <FiArchive className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                      <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Vault Tracking</span>
                      <span className={`ml-auto text-xs text-yellow-500 ${isDesktopCollapsed ? 'md:hidden' : ''}`}>🔒</span>
                    </button>
                  )}
                </li>
                )}
                {isAdmin() && (
                  <li>
                    <Link
                      to="/admin"
                      onClick={closeMobileMenu}
                      className={`flex items-center p-2 rounded hover-glow transition-all ${
                        active === "admin" ? "bg-yellow-600 text-white" : "text-gray-400 hover:bg-gray-800 hover:text-white"
                      }`}
                      title="Admin Panel"
                    >
                      <FiSettings className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                      <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Admin Panel</span>
                    </Link>
                  </li>
                )}
                {!isProfileHidden() && (
                  <li>
                    {hasAccess("profile") ? (
                      <Link
                        to="/profile"
                        onClick={closeMobileMenu}
                        className={`flex items-center p-2 rounded hover-glow transition-all ${
                          active === "profile" ? "bg-yellow-600 text-white" : "text-gray-400 hover:bg-gray-800 hover:text-white"
                        }`}
                        title="Profil"
                      >
                        <FiUser className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                        <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Profil</span>
                      </Link>
                    ) : (
                      <button
                        onClick={() => handleRestrictedClick("Profil")}
                        className="flex items-center p-2 rounded hover-glow transition-all text-gray-500 hover:bg-gray-800 hover:text-gray-400 w-full text-left"
                        title="Profil (Upgrade erforderlich)"
                      >
                        <FiUser className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
                        <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Profil</span>
                        <span className={`ml-auto text-xs text-yellow-500 ${isDesktopCollapsed ? 'md:hidden' : ''}`}>🔒</span>
                      </button>
                    )}
                  </li>
                )}
              </ul>
            </nav>
          </div>
          <div className="mt-auto">
            {/* Membership Space Button */}
            <button
              onClick={() => {
                window.open('https://vip.ebel2x.com/', '_blank');
                closeMobileMenu();
              }}
              className={`flex items-center p-2 w-full text-left rounded mb-2 bg-yellow-600 text-white font-medium hover:bg-yellow-700 transition-all ${
                isDesktopCollapsed ? 'md:justify-center' : ''
              }`}
              title="Membership Space"
            >
              <span>🏆</span>
              <span className={`ml-2 ${isDesktopCollapsed ? 'md:hidden' : ''}`}>Membership Space</span>
            </button>
            
            <button
              onClick={() => {
                logout();
                closeMobileMenu();
              }}
              className={`flex items-center p-2 w-full text-left rounded text-gray-400 hover:bg-gray-800 hover:text-white hover-glow transition-all ${
                isDesktopCollapsed ? 'md:justify-center' : ''
              }`}
              title="Abmelden"
            >
              <FiLogOut className={`${isDesktopCollapsed ? 'md:mx-auto' : 'mr-2'}`} />
              <span className={`${isDesktopCollapsed ? 'md:hidden' : ''}`}>Abmelden</span>
            </button>
          </div>
        </div>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">
                {upgradeModalSettings?.upgrade_modal_title || "🔒 Upgrade erforderlich"}
              </h3>
              <button
                onClick={closeUpgradeModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-300 mb-4">
                {upgradeModalSettings?.upgrade_modal_description 
                  ? upgradeModalSettings.upgrade_modal_description.replace("{feature}", restrictedFeature)
                  : `Der Bereich "${restrictedFeature}" ist nur für höhere Level verfügbar.`}
              </p>
              
              {/* Single Column Layout - Only show required access */}
              <div className="bg-gray-700 rounded-lg p-4 mb-4">
                <p className="text-gray-400 text-sm mb-3">
                  <strong>Zugang zu "{restrictedFeature}" mit:</strong>
                </p>
                <ul className="text-gray-400 text-sm list-disc list-inside space-y-1">
                  {rolesWithAccessToFeature.length > 0 ? (
                    rolesWithAccessToFeature.map((role, index) => (
                      <li key={index} className="text-yellow-500 font-medium">{role}</li>
                    ))
                  ) : (
                    <li>Lade...</li>
                  )}
                </ul>
              </div>

              <p className="text-yellow-500 text-sm font-medium mb-4">
                Für vollen Zugang:
              </p>
            </div>

            <div className="space-y-3 mb-4">
              <div className="flex justify-center">
                {/* Membership Button - only option */}
                <button
                  onClick={() => {
                    const membershipUrl = upgradeModalSettings?.membership_url || "https://ebel2x.com";
                    window.open(membershipUrl, '_blank');
                    closeUpgradeModal();
                  }}
                  className="w-full bg-yellow-600 text-white px-4 py-3 rounded hover:bg-yellow-700 transition-colors font-medium text-center"
                >
                  <div className="text-sm font-bold">
                    {upgradeModalSettings?.membership_button_text || "Jetzt Membership beitreten"}
                  </div>
                  <div className="text-xs opacity-90">Mindest Upgrade auf {getMinimumVIPRole(rolesWithAccessToFeature)}</div>
                </button>
              </div>
            </div>

            <div className="flex justify-center">
              <button
                onClick={closeUpgradeModal}
                className="bg-gray-700 text-white px-6 py-2 rounded hover:bg-gray-600 transition-colors text-sm"
              >
                Später
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Vault Tracking Component - Multi-Chain Support with Ethereum and Arbitrum
const VaultTracking = () => {
  const [address, setAddress] = useState('');
  const [selectedChain, setSelectedChain] = useState('ethereum'); // Default to Ethereum
  const [supportedChains, setSupportedChains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wallets, setWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  
  // Reference to update positions in background
  const allWalletPositionsRef = useRef(null);
  const [positions, setPositions] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [portfolioData, setPortfolioData] = useState(null);
  const [loadingPortfolio, setLoadingPortfolio] = useState(false);
  const [allPortfolios, setAllPortfolios] = useState(null);
  const [showAddWalletModal, setShowAddWalletModal] = useState(false); // Add modal state

  // Load wallets and supported chains on component mount
  useEffect(() => {
    loadWallets();
    loadAllPortfolios();
    loadSupportedChains();
  }, []);

  // Load supported chains from backend (fallback to hardcoded if API fails)
  const loadSupportedChains = async () => {
    try {
      // Fallback to hardcoded chains since API endpoint has issues
      setSupportedChains([
        {
          key: 'ethereum',
          name: 'Ethereum Mainnet',
          icon: '🔵',
          currency: 'ETH',
          moralis_chain: 'eth'
        },
        {
          key: 'arbitrum', 
          name: 'Arbitrum One',
          icon: '🔵',
          currency: 'ETH',
          moralis_chain: 'arbitrum'
        }
      ]);
      // Set default chain
      if (!selectedChain) {
        setSelectedChain('ethereum');
      }
    } catch (error) {
      console.error('Error loading supported chains:', error);
      // Use default hardcoded chains
      setSupportedChains([
        {
          key: 'ethereum',
          name: 'Ethereum Mainnet',
          icon: '🔵',
          currency: 'ETH'
        },
        {
          key: 'arbitrum', 
          name: 'Arbitrum One',
          icon: '🔵',
          currency: 'ETH'
        }
      ]);
    }
  };

  // Auto-refresh every 60 seconds for live price updates - Optimized for smooth UX
  useEffect(() => {
    const interval = setInterval(async () => {
      console.log("🔄 Auto-refreshing vault data and prices (60s interval)");
      
      // First update prices from backend (this is fast)
      try {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/vault/update-prices`, {}, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        console.log("✅ Prices updated automatically");
      } catch (error) {
        console.error('❌ Auto price update failed:', error);
      }
      
      // Then perform background refresh of data (smooth, no full reload)
      // Only refresh portfolio overview silently
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vault/portfolio/overview`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.data) {
          // Update portfolio overview smoothly without full reload
          setAllPortfolios(response.data);
        }
      } catch (error) {
        console.error('❌ Silent portfolio refresh failed:', error);
      }
      
      // If AllWalletPositions is visible, trigger its background update
      if (allWalletPositionsRef.current && allWalletPositionsRef.current.updateInBackground) {
        allWalletPositionsRef.current.updateInBackground();
      }
      
    }, 60000); // 60 seconds

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, []); // Remove selectedWallet dependency to avoid unnecessary re-creates

  const loadWallets = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vault/wallets`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setWallets(response.data.wallets || []);
    } catch (error) {
      console.error('Error loading wallets:', error);
    }
  };

  const loadAllPortfolios = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vault/portfolio`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setAllPortfolios(response.data);
    } catch (error) {
      console.error('Error loading all portfolios:', error);
    }
  };

  const loadPortfolio = async (walletId) => {
    setLoadingPortfolio(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vault/wallets/${walletId}/portfolio`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setPortfolioData(response.data);
    } catch (error) {
      console.error('Error loading portfolio:', error);
      alert('Fehler beim Laden des Portfolios: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingPortfolio(false);
    }
  };

  const updatePrices = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/vault/update-prices`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      alert('Preise erfolgreich aktualisiert!');
      
      // Refresh data
      loadAllPortfolios();
      if (selectedWallet) {
        loadPositions(selectedWallet.wallet_id);
        loadPortfolio(selectedWallet.wallet_id);
      }
    } catch (error) {
      console.error('Error updating prices:', error);
      alert('Fehler beim Aktualisieren der Preise: ' + (error.response?.data?.detail || error.message));
    }
  };

  const loadPositions = async (walletId) => {
    setLoadingPositions(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vault/wallets/${walletId}/positions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setPositions(response.data.positions || []);
      // Set selectedWallet to match wallet structure
      const wallet = wallets.find(w => w.id === walletId);
      setSelectedWallet(wallet);
      
      // Also load portfolio data for this wallet
      loadPortfolio(walletId);
    } catch (error) {
      console.error('Error loading positions:', error);
      alert('Fehler beim Laden der Positionen: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingPositions(false);
    }
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    if (!address || !selectedChain) return;

    // Validate ETH address format
    if (!address.match(/^0x[a-fA-F0-9]{40}$/)) {
      alert('Bitte geben Sie eine gültige Ethereum-Adresse ein (0x...)');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/vault/add-wallet`, {
        address: address,
        chain: selectedChain
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const selectedChainName = supportedChains.find(c => c.key === selectedChain)?.name || selectedChain;
      alert(`Wallet erfolgreich zu ${selectedChainName} hinzugefügt! Snapshot wird erstellt...`);
      setAddress(''); // Clear input
      setShowAddWalletModal(false); // Close modal
      loadWallets(); // Refresh wallet list
      loadAllPortfolios(); // Refresh portfolio overview
      
      // If snapshot was successful, show the results
      if (response.data.snapshot) {
        alert(`Snapshot erfolgreich erstellt! ${response.data.snapshot.positions_count} Positionen gefunden.`);
      }
    } catch (error) {
      console.error('Error adding wallet:', error);
      alert('Fehler beim Hinzufügen der Wallet: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const createSnapshot = async (walletId) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/vault/wallets/${walletId}/snapshot`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      alert(`Snapshot erfolgreich erstellt! ${response.data.positions_count} Positionen gefunden.`);
      loadWallets(); // Refresh to show updated timestamps
      loadAllPortfolios(); // Refresh portfolio overview
      
      // If this wallet is currently selected, refresh positions
      if (selectedWallet && selectedWallet.wallet_id === walletId) {
        loadPositions(walletId);
      }
    } catch (error) {
      console.error('Error creating snapshot:', error);
      alert('Fehler beim Erstellen des Snapshots: ' + (error.response?.data?.detail || error.message));
    }
  };

  const deleteWallet = async (walletId) => {
    if (!confirm('Sind Sie sicher, dass Sie diese Wallet löschen möchten? Alle Positionen werden ebenfalls gelöscht.')) {
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/vault/wallets/${walletId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      alert('Wallet erfolgreich gelöscht!');
      loadWallets();
      loadAllPortfolios(); // Refresh portfolio overview
      
      // Clear selected wallet if it was deleted
      if (selectedWallet && selectedWallet.wallet_id === walletId) {
        setSelectedWallet(null);
        setPositions([]);
      }
    } catch (error) {
      console.error('Error deleting wallet:', error);
      alert('Fehler beim Löschen der Wallet: ' + (error.response?.data?.detail || error.message));
    }
  };

  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nie';
    return new Date(dateString).toLocaleString('de-DE');
  };

  const formatNumber = (number) => {
    if (!number) return '0';
    return Number(number).toLocaleString('de-DE', { maximumFractionDigits: 6 });
  };

  const formatUSD = (number) => {
    if (!number) return '$0.00';
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  const formatPercentage = (number) => {
    if (!number) return '0%';
    return `${Number(number).toFixed(2)}%`;
  };

  // AllWalletPositions Component - Central overview of all lending positions
  // Optimized with React.memo and forwardRef for better performance during price updates
  const AllWalletPositions = React.memo(React.forwardRef(({ wallets }, ref) => {
    const [allPortfolioData, setAllPortfolioData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false); // For background updates

    useEffect(() => {
      if (wallets && wallets.length > 0) {
        loadAllPositions();
      }
    }, [wallets]);

    const loadAllPositions = async (isBackgroundUpdate = false) => {
      if (isBackgroundUpdate) {
        setIsUpdating(true);
      } else {
        setLoading(true);
      }
      
      try {
        const allData = [];
        for (const wallet of wallets) {
          try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vault/wallets/${wallet.id}/portfolio`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });
            if (response.data && response.data.positions) {
              allData.push({
                wallet,
                portfolioData: response.data
              });
            }
          } catch (error) {
            console.error(`Error loading positions for wallet ${wallet.id}:`, error);
          }
        }
        setAllPortfolioData(allData);
      } catch (error) {
        console.error('Error loading all positions:', error);
      } finally {
        setLoading(false);
        setIsUpdating(false);
      }
    };

    // Expose background update method via imperative handle
    useImperativeHandle(ref, () => ({
      updateInBackground: () => {
        if (wallets && wallets.length > 0) {
          loadAllPositions(true); // Background update
        }
      }
    }), [wallets]);

    if (loading) {
      return (
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4 text-white">Lending Positionen</h2>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-yellow-500 border-t-transparent"></div>
            <span className="ml-3 text-gray-400">Lade alle Positionen...</span>
          </div>
        </div>
      );
    }

    // Create grouped lending positions by wallet (Multi-Collateral support)
    const allLendingPairs = [];
    allPortfolioData.forEach(({ wallet, portfolioData }) => {
      if (portfolioData.positions && portfolioData.positions.length > 0) {
        const collateralPositions = portfolioData.positions.filter(position => 
          position.position_side !== 'debt' && position.position_type !== 'debt'
        );
        const debtPositions = portfolioData.positions.filter(position => 
          position.position_side === 'debt' || position.position_type === 'debt'
        );

        // Group all collateral and debt positions for this wallet into one position
        if (collateralPositions.length > 0 || debtPositions.length > 0) {
          allLendingPairs.push({
            collateralPositions: collateralPositions, // Array of all collateral assets
            debtPositions: debtPositions, // Array of all debt assets
            wallet,
            id: `${wallet.id}-multi-position`
          });
        }
      }
    });

    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Aave Positionen</h2>
          {isUpdating && (
            <div className="flex items-center gap-2 bg-blue-900/30 px-3 py-1 rounded-full border border-blue-700/30">
              <div className="animate-spin rounded-full h-3 w-3 border border-blue-400 border-t-transparent"></div>
              <span className="text-xs text-blue-400">Aktualisiere...</span>
            </div>
          )}
        </div>
        
        {allLendingPairs.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p>Keine Aave Positionen gefunden</p>
            <p className="text-xs text-gray-500 mt-1">Fügen Sie Wallets hinzu und erstellen Sie Snapshots</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allLendingPairs.map((position) => {
              // Calculate total collateral and debt values
              const totalCollateralValue = position.collateralPositions.reduce((sum, pos) => sum + pos.total_usd, 0);
              const totalDebtValue = position.debtPositions.reduce((sum, pos) => sum + Math.abs(pos.total_usd), 0);
              const netValue = totalCollateralValue - totalDebtValue;
              
              // Determine if this is a multi-asset position
              const isMultiAsset = position.collateralPositions.length > 1 || position.debtPositions.length > 1;
              
              // Create asset summary for header
              const collateralSymbols = position.collateralPositions.map(pos => 
                pos.token_info.underlying_symbol || pos.token_info.symbol
              ).join(', ');
              const debtSymbols = position.debtPositions.map(pos => 
                pos.token_info.underlying_symbol || pos.token_info.symbol
              ).join(', ');
              
              return (
                <div
                  key={position.id}
                  className="bg-gray-900 rounded-xl p-6 border-2 border-gray-600/30 hover:border-gray-500/50 transition-all hover:shadow-lg relative"
                >
                  {/* Delete Button - Top Right */}
                  <button
                    onClick={() => deleteWallet(position.wallet.id)}
                    className="absolute top-4 right-4 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full transition-colors opacity-70 hover:opacity-100"
                    title="Wallet löschen"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>

                  {/* Position Header */}
                  <div className="text-center mb-6 pr-12">
                    <div className="text-white font-bold text-lg">
                      {isMultiAsset ? 'Multi-Asset Position' : 'Lending Position'}
                    </div>
                    <div className="text-gray-400 text-sm">
                      {position.collateralPositions.length > 0 && position.debtPositions.length > 0 
                        ? `${collateralSymbols} ⟷ ${debtSymbols}`
                        : position.collateralPositions.length > 0 
                          ? `${collateralSymbols} Collateral`
                          : `${debtSymbols} Debt`
                      }
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {formatAddress(position.wallet.address)}
                    </div>
                  </div>

                  {/* Collateral vs Debt - Side by Side */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {/* Collateral Side */}
                    <div className="bg-gradient-to-br from-blue-900/20 to-blue-800/10 border border-blue-700/30 rounded-lg p-4">
                      <div className="text-center mb-3">
                        <span className="bg-blue-900 text-blue-300 px-3 py-1 rounded-full text-xs font-medium">
                          Collateral ({position.collateralPositions.length})
                        </span>
                      </div>
                      
                      {position.collateralPositions.length > 0 ? (
                        <>
                          {/* Total Value */}
                          <div className="text-center mb-3">
                            <div className="text-xl font-bold text-blue-400">
                              {formatUSD(totalCollateralValue)}
                            </div>
                            <div className="text-xs text-gray-400">Gesamt</div>
                          </div>
                          
                          {/* Individual Assets */}
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {position.collateralPositions.map((asset, index) => (
                              <div key={index} className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-1">
                                  <div className="w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center font-bold text-white text-xs">
                                    {(asset.token_info.underlying_symbol || asset.token_info.symbol).slice(0, 1)}
                                  </div>
                                  <span className="text-blue-300 font-medium">
                                    {asset.token_info.underlying_symbol || asset.token_info.symbol}
                                  </span>
                                </div>
                                <div className="text-right">
                                  <div className="text-blue-400 font-medium">{formatUSD(asset.total_usd)}</div>
                                  <div className="text-gray-400">{formatNumber(asset.total_amount)}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Progress Bar */}
                          <div className="mt-3">
                            <div className="w-full bg-gray-700 rounded-full h-2">
                              <div 
                                className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all"
                                style={{ width: '100%' }}
                              ></div>
                            </div>
                            <div className="flex justify-between text-xs text-gray-400 mt-1">
                              <span>0</span>
                              <span>100%</span>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <div className="text-sm">Kein</div>
                          <div className="text-sm">Collateral</div>
                        </div>
                      )}
                    </div>

                    {/* Debt Side */}
                    <div className="bg-gradient-to-br from-red-900/20 to-red-800/10 border border-red-700/30 rounded-lg p-4">
                      <div className="text-center mb-3">
                        <span className="bg-red-900 text-red-300 px-3 py-1 rounded-full text-xs font-medium">
                          Debt ({position.debtPositions.length})
                        </span>
                      </div>
                      
                      {position.debtPositions.length > 0 ? (
                        <>
                          {/* Total Value */}
                          <div className="text-center mb-3">
                            <div className="text-xl font-bold text-red-400">
                              -{formatUSD(totalDebtValue)}
                            </div>
                            <div className="text-xs text-gray-400">Gesamt</div>
                          </div>
                          
                          {/* Individual Assets */}
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {position.debtPositions.map((asset, index) => (
                              <div key={index} className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-1">
                                  <div className="w-4 h-4 bg-red-600 rounded-full flex items-center justify-center font-bold text-white text-xs">
                                    {(asset.token_info.underlying_symbol || asset.token_info.symbol).slice(0, 1)}
                                  </div>
                                  <span className="text-red-300 font-medium">
                                    {asset.token_info.underlying_symbol || asset.token_info.symbol}
                                  </span>
                                </div>
                                <div className="text-right">
                                  <div className="text-red-400 font-medium">-{formatUSD(Math.abs(asset.total_usd))}</div>
                                  <div className="text-gray-400">{formatNumber(asset.total_amount)}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Progress Bar */}
                          <div className="mt-3">
                            <div className="w-full bg-gray-700 rounded-full h-2">
                              <div 
                                className="h-2 rounded-full bg-gradient-to-r from-red-500 to-red-400 transition-all"
                                style={{ width: `${Math.min((totalDebtValue / totalCollateralValue) * 100, 100)}%` }}
                              ></div>
                            </div>
                            <div className="flex justify-between text-xs text-gray-400 mt-1">
                              <span>0</span>
                              <span>{totalCollateralValue > 0 ? Math.round((totalDebtValue / totalCollateralValue) * 100) : 0}%</span>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <div className="text-sm">Keine</div>
                          <div className="text-sm">Schulden</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Net Position Value */}
                  <div className="bg-gradient-to-br from-green-900/20 to-green-800/10 border border-green-700/30 rounded-lg p-4 mt-4">
                    <div className="text-center mb-3">
                      <span className="bg-green-900 text-green-300 px-3 py-1 rounded-full text-xs font-medium">
                        Gesamtwert
                      </span>
                    </div>
                    
                    <div className="text-center">
                      <div className={`text-3xl font-bold ${netValue >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatUSD(netValue)}
                      </div>
                      <div className="text-sm text-gray-400 mt-1">Position</div>
                    </div>
                  </div>

                  {/* Health Factor & Liquidation Risk */}
                  {position.collateralPositions.length > 0 && position.debtPositions.length > 0 && (
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      {/* Health Factor */}
                      <div className="bg-gradient-to-br from-purple-900/20 to-purple-800/10 border border-purple-700/30 rounded-lg p-4">
                        <div className="text-center mb-2">
                          <span className="bg-purple-900 text-purple-300 px-2 py-1 rounded-full text-xs font-medium">
                            Health Factor
                          </span>
                        </div>
                        <div className="text-center">
                          {(() => {
                            const liquidationThreshold = 0.85; // 85% LTV for most assets on Aave
                            const healthFactor = totalDebtValue > 0 ? (totalCollateralValue * liquidationThreshold) / totalDebtValue : 999;
                            
                            let colorClass = 'text-green-400';
                            let emoji = '🟢';
                            
                            if (healthFactor < 1.1) {
                              colorClass = 'text-red-400';
                              emoji = '🔴';
                            } else if (healthFactor < 1.5) {
                              colorClass = 'text-yellow-400';
                              emoji = '🟡';
                            }
                            
                            return (
                              <>
                                <div className={`text-xl font-bold ${colorClass}`}>
                                  {emoji} {healthFactor > 999 ? '∞' : healthFactor.toFixed(2)}
                                </div>
                                <div className="text-xs text-gray-400 mt-1">
                                  {healthFactor < 1 ? 'Liquidation Risk!' : 
                                   healthFactor < 1.1 ? 'Sehr riskant' :
                                   healthFactor < 1.5 ? 'Riskant' : 'Sicher'}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Liquidation Price (Single-Asset) or Liquidation Risk (Multi-Asset) */}
                      <div className="bg-gradient-to-br from-orange-900/20 to-orange-800/10 border border-orange-700/30 rounded-lg p-4">
                        <div className="text-center mb-2">
                          <span className="bg-orange-900 text-orange-300 px-2 py-1 rounded-full text-xs font-medium">
                            {isMultiAsset ? 'Liquidation Risk' : 'Liquidationspreis'}
                          </span>
                        </div>
                        <div className="text-center">
                          {isMultiAsset ? (
                            // Multi-Asset: Show Liquidation Risk %
                            (() => {
                              const liquidationThreshold = 0.85;
                              const currentLTV = totalCollateralValue > 0 ? totalDebtValue / totalCollateralValue : 0;
                              const liquidationLTV = liquidationThreshold;
                              const riskPercentage = currentLTV / liquidationLTV * 100;
                              
                              let colorClass = 'text-green-400';
                              let riskLevel = 'Niedrig';
                              
                              if (riskPercentage > 90) {
                                colorClass = 'text-red-400';
                                riskLevel = 'Kritisch';
                              } else if (riskPercentage > 75) {
                                colorClass = 'text-yellow-400';
                                riskLevel = 'Hoch';
                              } else if (riskPercentage > 50) {
                                colorClass = 'text-blue-400';
                                riskLevel = 'Mittel';
                              }
                              
                              return (
                                <>
                                  <div className={`text-lg font-bold ${colorClass}`}>
                                    {riskPercentage.toFixed(1)}%
                                  </div>
                                  <div className="text-xs text-gray-400 mt-1">
                                    {riskLevel}
                                  </div>
                                </>
                              );
                            })()
                          ) : (
                            // Single-Asset: Show Liquidation Price
                            (() => {
                              const collateralAmount = position.collateralPositions[0]?.total_amount || 0;
                              const liquidationThreshold = 0.85;
                              
                              if (collateralAmount > 0 && totalDebtValue > 0) {
                                const liquidationPrice = totalDebtValue / (collateralAmount * liquidationThreshold);
                                const collateralSymbol = position.collateralPositions[0]?.token_info.underlying_symbol || position.collateralPositions[0]?.token_info.symbol;
                                
                                return (
                                  <>
                                    <div className="text-lg font-bold text-orange-400">
                                      ${liquidationPrice.toFixed(2)}
                                    </div>
                                    <div className="text-xs text-gray-400 mt-1">
                                      {collateralSymbol}
                                    </div>
                                  </>
                                );
                              } else {
                                return (
                                  <>
                                    <div className="text-lg font-bold text-gray-400">
                                      -
                                    </div>
                                    <div className="text-xs text-gray-400 mt-1">
                                      N/A
                                    </div>
                                  </>
                                );
                              }
                            })()
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }));

  return (
    <div className="dashboard-container">
      <Sidebar active="vault-tracking" />
      <div className="dashboard-content">
        <div className="p-6">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Vault Tracking</h1>
                <p className="text-gray-400">
                  Verfolgen Sie Ihre DeFi-Positionen über Multi-Chain-Adressen mit Live-Preisen.
                </p>
              </div>
              <button
                onClick={() => setShowAddWalletModal(true)}
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 shadow-lg hover:shadow-xl"
              >
                <FiPlus className="w-5 h-5" />
                Multi-Chain Wallet hinzufügen
              </button>
            </div>
          </div>

          {/* Portfolio Overview */}
          {allPortfolios && (
            <div className="bg-gray-800 rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold mb-4 text-white">Portfolio Übersicht</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-400">
                    {formatUSD(allPortfolios.portfolio_summary.total_value_usd)}
                  </div>
                  <div className="text-sm text-gray-400">Gesamt Portfolio Wert</div>
                </div>
                
                <div className="bg-gray-700 rounded-lg p-4">
                  <div className="text-2xl font-bold text-yellow-400">
                    {allPortfolios.portfolio_summary.wallets_count}
                  </div>
                  <div className="text-sm text-gray-400">Tracked Wallets</div>
                </div>
              </div>

              {/* Wallet Aufschlüsselung - ausgeblendet für kompakteres Layout */}
              {/* 
              {allPortfolios.wallets.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-white mb-3">Wallet Aufschlüsselung</h3>
                  <div className="space-y-2">
                    {allPortfolios.wallets.map((wallet) => (
                      <div key={wallet.id} className="flex items-center justify-between bg-gray-700 rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          <span className="text-white font-mono text-sm">{formatAddress(wallet.address)}</span>
                          <span className="text-xs text-gray-400">{wallet.positions_count} Positionen</span>
                        </div>
                        <div className="text-right">
                          <div className="text-white font-medium">{formatUSD(wallet.total_value_usd)}</div>
                          <div className="text-xs text-gray-400">{formatDate(wallet.last_snapshot)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              */}
            </div>
          )}

          {/* Aave Positionen - Direkt unter Portfolio Übersicht für kompaktes Layout */}
          <AllWalletPositions ref={allWalletPositionsRef} wallets={wallets} />

          {/* Add Wallet Modal */}
          {showAddWalletModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-gray-800 rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">Multi-Chain Wallet hinzufügen</h2>
                  <button
                    onClick={() => setShowAddWalletModal(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <FiX className="w-6 h-6" />
                  </button>
                </div>
                
                {/* Chain Selection */}
                {supportedChains.length > 0 && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-300 mb-3">
                      Netzwerk auswählen:
                    </label>
                    <div className="space-y-3">
                      {supportedChains.map((chain) => (
                        <button
                          key={chain.key}
                          onClick={() => setSelectedChain(chain.key)}
                          className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${
                            selectedChain === chain.key
                              ? 'border-yellow-500 bg-yellow-600/20 text-white shadow-lg transform scale-[1.02]'
                              : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500 hover:bg-gray-600'
                          }`}
                        >
                          <span className="text-2xl">{chain.icon}</span>
                          <div className="text-left flex-1">
                            <div className="font-semibold text-lg">{chain.name}</div>
                            <div className="text-sm opacity-75">Currency: {chain.currency}</div>
                          </div>
                          {selectedChain === chain.key && (
                            <FiCheck className="w-5 h-5 text-yellow-400" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                <form onSubmit={handleAddressSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Wallet-Adresse:
                    </label>
                    <input
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="0x... Ethereum Adresse eingeben"
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                      disabled={loading}
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowAddWalletModal(false)}
                      className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="submit"
                      disabled={loading || !address || !selectedChain}
                      className="flex-1 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Wird hinzugefügt...
                        </>
                      ) : (
                        <>
                          <FiPlus className="w-4 h-4" />
                          Hinzufügen
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
const AdminPanel = () => {
  const [rolePermissions, setRolePermissions] = useState({});
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  const token = localStorage.getItem("token");

  // Available features that can be managed
  const availableFeatures = [
    { key: "dashboard", name: "Dashboard", description: "Haupt-Dashboard Ansicht" },
    { key: "transactions", name: "Transaktionen", description: "Crypto-Transaktionen verwalten" },
    { key: "debt", name: "Schulden-Tracker", description: "Schulden und Verbindlichkeiten" },
    { key: "defi_position", name: "DeFi Position", description: "DeFi-Positionen und Aave" },
    { key: "vault_tracking", name: "Vault Tracking", description: "Vault Tracking für Ethereum DeFi Positionen" },
    { key: "vault_tracking_hidden", name: "Vault Tracking Verstecken", description: "Vault Tracking-Tab komplett ausblenden" },
    { key: "indicators", name: "Indikatoren", description: "Bitcoin-Indikatoren und Charts" },
    { key: "trade_journal", name: "Trade Journal", description: "Trading-Tagebuch" },
    { key: "marktstruktur", name: "Marktstruktur", description: "Marktstruktur-Analyse" },
    { key: "profile", name: "Profil", description: "Benutzer-Profil" },
    { key: "profile_hidden", name: "Profil Verstecken", description: "Profil-Tab komplett ausblenden" },
    { key: "preis_alarme", name: "Preis-Alarme", description: "Crypto-Preis Alarme und Benachrichtigungen" },
    { key: "preis_alarme_hidden", name: "Preis-Alarme Verstecken", description: "Preis-Alarme-Tab komplett ausblenden" }
  ];

  // Available roles
  const availableRoles = [
    { key: "admin", name: "Admin", description: "Vollzugriff auf alles" },
    { key: "vip", name: "VIP", description: "Standard VIP Mitgliedschaft" },
    { key: "vip_plus", name: "VIP+", description: "VIP Plus Mitgliedschaft" },
    { key: "vip_star", name: "VIP*", description: "VIP Star Mitgliedschaft" },
    { key: "vip_plus_plus", name: "VIP++", description: "VIP Plus Plus Mitgliedschaft" },
    { key: "dash_t1", name: "Dash T1", description: "Legacy Dash Tier 1 (eingeschränkt)" },
    { key: "dash_t2", name: "Dash T2", description: "Legacy Dash Tier 2 (vollzugang)" },
    { key: "dashboard_t1", name: "Dashboard T1", description: "Dashboard Tier 1 (eingeschränkt)" },
    { key: "dashboard_t2", name: "Dashboard T2", description: "Dashboard Tier 2 (vollzugang)" }
  ];

  // Fetch current permissions and settings
  const fetchAdminData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch role permissions
      const permissionsResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/role-permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Fetch settings (texts, links)
      const settingsResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setRolePermissions(permissionsResponse.data.permissions || {});
      setSettings(settingsResponse.data.settings || {});
    } catch (error) {
      console.error("Error fetching admin data:", error);
      setError("Fehler beim Laden der Admin-Daten: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Save permissions
  const savePermissions = async () => {
    try {
      setSaving(true);
      setError(null);

      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/role-permissions`, {
        permissions: rolePermissions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccessMessage("Berechtigungen erfolgreich gespeichert!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error saving permissions:", error);
      setError("Fehler beim Speichern: " + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  // Save settings
  const saveSettings = async () => {
    try {
      setSaving(true);
      setError(null);

      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`, {
        settings: settings
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccessMessage("Einstellungen erfolgreich gespeichert!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error saving settings:", error);
      setError("Fehler beim Speichern: " + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  // Toggle permission for a role/feature combination
  const togglePermission = (roleKey, featureKey) => {
    setRolePermissions(prev => {
      const newPermissions = { ...prev };
      if (!newPermissions[roleKey]) {
        newPermissions[roleKey] = [];
      }
      
      const currentPermissions = [...newPermissions[roleKey]];
      const index = currentPermissions.indexOf(featureKey);
      
      if (index > -1) {
        currentPermissions.splice(index, 1);
      } else {
        currentPermissions.push(featureKey);
      }
      
      newPermissions[roleKey] = currentPermissions;
      return newPermissions;
    });
  };

  // Update setting value
  const updateSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  useEffect(() => {
    fetchAdminData();
  }, []);

  if (loading) {
    return (
      <div className="dashboard-container">
        <Sidebar active="admin" />
        <div className="dashboard-content">
          <div className="p-6">
            <h1 className="text-2xl font-bold mb-6">Admin Panel</h1>
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-500"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Sidebar active="admin" />
      <div className="dashboard-content">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">Admin Panel</h1>
            <div className="flex gap-2">
              <button
                onClick={fetchAdminData}
                disabled={loading}
                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 flex items-center gap-2"
              >
                <FiRefreshCw className={loading ? 'animate-spin' : ''} />
                Aktualisieren
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-900 border-l-4 border-red-500 text-red-300 p-4 mb-6 flex justify-between items-center">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="text-red-300 hover:text-white">
                <FiX />
              </button>
            </div>
          )}

          {successMessage && (
            <div className="bg-green-900 border-l-4 border-green-500 text-green-300 p-4 mb-6 flex justify-between items-center">
              <span>{successMessage}</span>
              <FiCheck />
            </div>
          )}

          <div className="space-y-6">
            
            {/* Rollen-Berechtigungen */}
            <div className="bg-gray-800 rounded-lg p-6 xl:col-span-2">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-white">Rollen-Berechtigungen</h2>
                  <p className="text-sm text-gray-400 mt-1">Verwalten Sie die Zugriffe für jede Benutzerrolle</p>
                </div>
                <button
                  onClick={savePermissions}
                  disabled={saving}
                  className="bg-yellow-600 text-white px-6 py-2 rounded hover:bg-yellow-700 flex items-center gap-2 disabled:opacity-50 transition-colors"
                >
                  <FiSave />
                  {saving ? 'Speichern...' : 'Speichern'}
                </button>
              </div>
              
              {/* Feature-Gruppen */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {availableRoles.map(role => (
                  <div key={role.key} className="bg-gray-900 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-white text-lg">{role.name}</h3>
                        <p className="text-sm text-gray-400">{role.description}</p>
                      </div>
                      <div className="text-xs text-gray-500">
                        {rolePermissions[role.key]?.length || 0} / {availableFeatures.length} Features
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      {/* Core Features */}
                      <div className="border-b border-gray-700 pb-2">
                        <h4 className="text-sm font-medium text-gray-300 mb-2">🏠 Hauptfunktionen</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {availableFeatures.filter(f => ['dashboard', 'transactions', 'debt', 'defi_position'].includes(f.key)).map(feature => (
                            <label key={feature.key} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-800 rounded p-2 transition-colors">
                              <input
                                type="checkbox"
                                checked={rolePermissions[role.key]?.includes(feature.key) || false}
                                onChange={() => togglePermission(role.key, feature.key)}
                                className="w-4 h-4 text-yellow-600 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500"
                              />
                              <span className="text-sm text-gray-300">{feature.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      {/* Advanced Features */}
                      <div className="border-b border-gray-700 pb-2">
                        <h4 className="text-sm font-medium text-gray-300 mb-2">📊 Erweiterte Funktionen</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {availableFeatures.filter(f => ['vault_tracking', 'indicators', 'trade_journal', 'marktstruktur'].includes(f.key)).map(feature => (
                            <label key={feature.key} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-800 rounded p-2 transition-colors">
                              <input
                                type="checkbox"
                                checked={rolePermissions[role.key]?.includes(feature.key) || false}
                                onChange={() => togglePermission(role.key, feature.key)}
                                className="w-4 h-4 text-yellow-600 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500"
                              />
                              <span className="text-sm text-gray-300">{feature.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      {/* Alerts & Profile */}
                      <div className="border-b border-gray-700 pb-2">
                        <h4 className="text-sm font-medium text-gray-300 mb-2">🔔 Benachrichtigungen & Profil</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {availableFeatures.filter(f => ['preis_alarme', 'profile'].includes(f.key)).map(feature => (
                            <label key={feature.key} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-800 rounded p-2 transition-colors">
                              <input
                                type="checkbox"
                                checked={rolePermissions[role.key]?.includes(feature.key) || false}
                                onChange={() => togglePermission(role.key, feature.key)}
                                className="w-4 h-4 text-yellow-600 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500"
                              />
                              <span className="text-sm text-gray-300">{feature.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      {/* Visibility Settings */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-2">👁️ Sichtbarkeitseinstellungen</h4>
                        <div className="grid grid-cols-1 gap-2">
                          {availableFeatures.filter(f => f.key.includes('_hidden')).map(feature => (
                            <label key={feature.key} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-800 rounded p-2 transition-colors">
                              <input
                                type="checkbox"
                                checked={rolePermissions[role.key]?.includes(feature.key) || false}
                                onChange={() => togglePermission(role.key, feature.key)}
                                className="w-4 h-4 text-red-600 bg-gray-700 border-gray-600 rounded focus:ring-red-500"
                              />
                              <span className="text-sm text-gray-300">{feature.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Einstellungen */}
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-white">Anwendungseinstellungen</h2>
                  <p className="text-sm text-gray-400 mt-1">Konfigurieren Sie Links und Texte für die Anwendung</p>
                </div>
                <button
                  onClick={saveSettings}
                  disabled={saving}
                  className="bg-yellow-600 text-white px-6 py-2 rounded hover:bg-yellow-700 flex items-center gap-2 disabled:opacity-50 transition-colors"
                >
                  <FiSave />
                  {saving ? 'Speichern...' : 'Speichern'}
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Links Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-white border-b border-gray-700 pb-2">🔗 Links</h3>
                  
                  {/* Membership Link */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Membership Link
                    </label>
                    <input
                      type="url"
                      value={settings.membership_url || "https://ebel2x.com"}
                      onChange={(e) => updateSetting("membership_url", e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-yellow-500 focus:border-yellow-500"
                      placeholder="https://ebel2x.com"
                    />
                  </div>

                  {/* Dashboard T2 Link */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Dashboard T2 Link
                    </label>
                    <input
                      type="url"
                      value={settings.dashboard_t2_url || ""}
                      onChange={(e) => updateSetting("dashboard_t2_url", e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-yellow-500 focus:border-yellow-500"
                      placeholder="https://..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Leer lassen für "Link wird bald verfügbar sein!" Meldung
                    </p>
                  </div>

                  {/* Membership Space Link */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Membership Space Link
                    </label>
                    <input
                      type="url"
                      value={settings.membership_space_url || "https://vip.ebel2x.com/"}
                      onChange={(e) => updateSetting("membership_space_url", e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-yellow-500 focus:border-yellow-500"
                      placeholder="https://vip.ebel2x.com/"
                    />
                  </div>
                </div>

                {/* Texte Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-white border-b border-gray-700 pb-2">📝 Texte & Beschreibungen</h3>
                  
                  {/* Upgrade Modal Titel */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Upgrade Modal Titel
                    </label>
                    <input
                      type="text"
                      value={settings.upgrade_modal_title || "🔒 Upgrade erforderlich"}
                      onChange={(e) => updateSetting("upgrade_modal_title", e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-yellow-500 focus:border-yellow-500"
                    />
                  </div>

                  {/* Upgrade Modal Beschreibung */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Upgrade Modal Beschreibung
                    </label>
                    <textarea
                      value={settings.upgrade_modal_description || "Der Bereich ist nur für höhere Level verfügbar."}
                      onChange={(e) => updateSetting("upgrade_modal_description", e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-yellow-500 focus:border-yellow-500"
                      rows="3"
                    />
                  </div>

                  {/* Membership Button Text */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Membership Button Text
                    </label>
                    <input
                      type="text"
                      value={settings.membership_button_text || "Jetzt Membership beitreten"}
                      onChange={(e) => updateSetting("membership_button_text", e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-yellow-500 focus:border-yellow-500"
                    />
                  </div>

                  {/* Dashboard T2 Button Text */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Dashboard T2 Button Text
                    </label>
                    <input
                      type="text"
                      value={settings.dashboard_t2_button_text || "Dashboard T2 Vollen Zugang"}
                      onChange={(e) => updateSetting("dashboard_t2_button_text", e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-yellow-500 focus:border-yellow-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Marktstruktur Component with Google Sheets Integration
const Marktstruktur = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // New state for market situation and charts
  const [marketData, setMarketData] = useState({});
  const [marketLoading, setMarketLoading] = useState(true);
  const [marketError, setMarketError] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ url: '', title: '' });

  // Google Sheets CSV URLs
  const SHEET_URL = "https://docs.google.com/spreadsheets/d/1RofUqPvRtBbfG-qgnbbpD51qlqsoacoLeXZUfTWprj0/export?format=csv";
  const MARKET_SHEET_URL = "https://docs.google.com/spreadsheets/d/1N_10EQ_TpQLnJWtpoJxAqhcdPd5wTOQ7CqcnAIQBMKo/export?format=csv";

  const fetchSheetData = async () => {
    try {
      setLoading(true);
      const response = await fetch(SHEET_URL);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const csvText = await response.text();
      
      // Parse CSV - first column is timeframe, second is status
      const lines = csvText.split('\n');
      
      const parsedData = [];
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(',').map(value => value.trim().replace(/"/g, ''));
          if (values.length >= 2) {
            parsedData.push({
              timeframe: values[0],
              status: values[1].toUpperCase()
            });
          }
        }
      }
      
      setData(parsedData);
      setLastUpdate(new Date().toLocaleString());
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching sheet data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch market situation and charts data
  const fetchMarketData = async () => {
    try {
      setMarketLoading(true);
      const response = await fetch(MARKET_SHEET_URL);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const csvText = await response.text();
      const lines = csvText.split('\n');
      
      const parsedMarketData = {};
      const timeframeData = [];
      
      // Parse all rows
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(',').map(value => value.trim().replace(/"/g, ''));
          if (values.length >= 2 && values[0] && values[1]) {
            
            // Parse timeframes (A2:B8 - rows 1-7 in 0-based index)
            if (i >= 1 && i <= 7) {
              timeframeData.push({
                timeframe: values[0],
                status: values[1].toUpperCase()
              });
            }
            
            // Store individual rows for other purposes (charts, etc.)
            parsedMarketData[`row${i + 1}`] = {
              title: values[0],
              content: values[1]
            };
          }
        }
      }
      
      setMarketData({ ...parsedMarketData, timeframes: timeframeData });
      setMarketError(null);
    } catch (err) {
      setMarketError(err.message);
      console.error('Error fetching market data:', err);
    } finally {
      setMarketLoading(false);
    }
  };

  useEffect(() => {
    fetchSheetData();
    fetchMarketData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchSheetData();
      fetchMarketData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Function to open image modal
  const openImageModal = (imageUrl, title) => {
    setSelectedImage({ url: imageUrl, title });
    setShowImageModal(true);
  };

  // Function to close image modal
  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage({ url: '', title: '' });
  };

  // Risk Meter Logic - Improved with Higher Timeframe Priority
  const calculateRiskLevel = () => {
    if (data.length === 0) return { level: 'NEUTRAL', color: '#6B7280', description: 'Keine Daten', intensity: 50 };
    
    // Create timeframe map
    const timeframes = {};
    data.forEach(item => {
      timeframes[item.timeframe] = item.status;
    });
    
    const week = timeframes['Week'] || timeframes['W'];
    const daily = timeframes['2D'] || timeframes['D'] || timeframes['1D'];
    const threeDays = timeframes['3D'];
    const twelveHours = timeframes['12H'];
    const eightHours = timeframes['8H'];
    const fourHours = timeframes['4H'];
    const oneHour = timeframes['1H'];
    
    // Calculate weighted score (higher timeframes get more weight)
    let bullishScore = 0;
    let bearishScore = 0;
    
    // Assign weights: Week=5, 3D=4, D=3, 12H=2, 8H=2, 4H=1, 1H=1
    const weights = {
      Week: 5, W: 5,
      '3D': 4,
      '2D': 3, D: 3, '1D': 3,
      '12H': 2,
      '8H': 2,
      '4H': 1,
      '1H': 1
    };
    
    // Calculate scores
    Object.entries(timeframes).forEach(([tf, status]) => {
      const weight = weights[tf] || 1;
      if (status === 'BULL') {
        bullishScore += weight;
      } else if (status === 'BEAR') {
        bearishScore += weight;
      }
    });
    
    // Main Risk Logic - Higher timeframes dominate
    
    // Extreme danger: Week AND 3D both BEAR
    if ((week === 'BEAR') && (threeDays === 'BEAR')) {
      return { 
        level: 'BEAR MARKET', 
        color: '#DC2626', 
        description: 'Extrem gefährlich - Week und 3D beide im Bärenmarkt',
        intensity: 100 
      };
    }
    
    // High danger: 3D is BEAR (but Week might still be BULL)
    if (threeDays === 'BEAR') {
      return { 
        level: 'BEAR MARKET', 
        color: '#EF4444', 
        description: 'Hohe Gefahr - 3D Timeframe im Bärenmarkt',
        intensity: 90 
      };
    }
    
    // Warning: Daily is BEAR but higher timeframes still BULL
    if (daily === 'BEAR' && threeDays === 'BULL' && (week === 'BULL' || !week)) {
      return { 
        level: 'AUFPASSEN', 
        color: '#F97316', 
        description: 'Möglicher Bärenmarkt - Daily bearish, höhere Timeframes noch bullish',
        intensity: 75 
      };
    }
    
    // NEW: Strong bullish when Week, 3D, D are BULL (even if lower timeframes are BEAR)
    if ((week === 'BULL' || !week) && threeDays === 'BULL' && (daily === 'BULL' || !daily)) {
      // Count lower timeframe issues
      let lowerBearCount = 0;
      if (twelveHours === 'BEAR') lowerBearCount++;
      if (eightHours === 'BEAR') lowerBearCount++;
      if (fourHours === 'BEAR') lowerBearCount++;
      if (oneHour === 'BEAR') lowerBearCount++;
      
      // If only 1-2 lower timeframes are BEAR, still consider it bullish
      if (lowerBearCount <= 2) {
        if (fourHours === 'BULL' && eightHours === 'BULL') {
          return { 
            level: 'STRONG BULL', 
            color: '#10B981', 
            description: 'Starke Bullish Struktur - Gute Trading Bedingungen',
            intensity: 25 
          };
        } else {
          return { 
            level: 'BULL MARKET', 
            color: '#22C55E', 
            description: 'Höhere Timeframes bullish - Übergeordnet bullish',
            intensity: 35 
          };
        }
      }
      
      // If 3+ lower timeframes are BEAR, show trading chance
      return { 
        level: 'TRADING CHANCE', 
        color: '#F59E0B', 
        description: 'Höhere TF bullish, niedrigere bearish - Vorsichtige Chance',
        intensity: 55 
      };
    }
    
    // Warning: 12H is BEAR but higher timeframes still BULL
    if (twelveHours === 'BEAR' && daily === 'BULL' && threeDays === 'BULL' && (week === 'BULL' || !week)) {
      return { 
        level: 'BULL MARKET', 
        color: '#22C55E', 
        description: 'Übergeordnet bullish - 12H Korrektur',
        intensity: 40 
      };
    }
    
    // Use weighted scoring for remaining cases
    if (bullishScore > bearishScore * 1.5) {
      return { 
        level: 'BULL MARKET', 
        color: '#22C55E', 
        description: 'Überwiegend bullish',
        intensity: 35 
      };
    } else if (bearishScore > bullishScore * 1.5) {
      return { 
        level: 'AUFPASSEN', 
        color: '#F97316', 
        description: 'Überwiegend bearish',
        intensity: 75 
      };
    }
    
    return { 
      level: 'NEUTRAL', 
      color: '#6B7280', 
      description: 'Gemischte Signale - Abwarten',
      intensity: 50 
    };
  };

  const riskAssessment = calculateRiskLevel();

  // Timeframe hierarchy for display
  const timeframeOrder = ['Week', 'W', '3D', '2D', 'D', '1D', '12H', '8H', '4H', '1H'];
  const sortedData = data.sort((a, b) => {
    const indexA = timeframeOrder.indexOf(a.timeframe);
    const indexB = timeframeOrder.indexOf(b.timeframe);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });

  if (loading && data.length === 0) {
    return (
      <div className="dashboard-container">
        <Sidebar active="marktstruktur" />
        <div className="dashboard-content">
          <div className="p-6">
            <h1 className="text-2xl font-bold mb-6">Marktstruktur</h1>
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-500"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Sidebar active="marktstruktur" />
      <div className="dashboard-content">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">Marktstruktur Analysis</h1>
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  fetchSheetData();
                  fetchMarketData();
                }}
                className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 flex items-center gap-2"
                disabled={loading || marketLoading}
              >
                <FiRefreshCw className={`${(loading || marketLoading) ? 'animate-spin' : ''}`} />
                Aktualisieren
              </button>
              {lastUpdate && (
                <span className="text-sm text-gray-400">
                  Letzte Aktualisierung: {lastUpdate}
                </span>
              )}
            </div>
          </div>

          {error && (
            <div className="bg-red-900 border-l-4 border-red-500 text-red-300 p-4 mb-6">
              <p>Fehler beim Laden der Daten: {error}</p>
            </div>
          )}

          {data.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              
              {/* Risk Meter */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-6 text-center">Risk Meter</h2>
                
                {/* Circular Risk Gauge */}
                <div className="flex justify-center mb-6">
                  <div className="relative w-48 h-48">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                      {/* Background circle */}
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        stroke="#374151"
                        strokeWidth="8"
                        fill="none"
                      />
                      {/* Risk level arc */}
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        stroke={riskAssessment.color}
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={`${(riskAssessment.intensity / 100) * 251.2} 251.2`}
                        strokeLinecap="round"
                        className="transition-all duration-1000 ease-in-out"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div 
                          className="text-lg font-bold"
                          style={{ color: riskAssessment.color }}
                        >
                          {riskAssessment.level}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Risk Description */}
                <div className="text-center">
                  <p className="text-gray-300 text-sm mb-4">
                    {riskAssessment.description}
                  </p>
                  
                  {/* Risk Level Indicator */}
                  <div className="flex justify-center space-x-2 text-xs">
                    <div className={`px-2 py-1 rounded ${riskAssessment.level === 'BEAR MARKET' ? 'bg-red-600' : 'bg-gray-700'}`}>
                      BEAR
                    </div>
                    <div className={`px-2 py-1 rounded ${riskAssessment.level === 'AUFPASSEN' ? 'bg-orange-600' : 'bg-gray-700'}`}>
                      WARNUNG
                    </div>
                    <div className={`px-2 py-1 rounded ${riskAssessment.level === 'TRADING CHANCE' ? 'bg-yellow-600' : 'bg-gray-700'}`}>
                      CHANCE
                    </div>
                    <div className={`px-2 py-1 rounded ${riskAssessment.level.includes('BULL') ? 'bg-green-600' : 'bg-gray-700'}`}>
                      BULL
                    </div>
                  </div>
                </div>
              </div>

              {/* Timeframe Status */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-6">Timeframe Analysis</h2>
                
                <div className="space-y-4">
                  {sortedData.map((item, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-700 rounded">
                      <div className="flex items-center space-x-3">
                        <span className="font-medium text-white w-16">
                          {item.timeframe}
                        </span>
                        <div 
                          className={`px-3 py-1 rounded text-sm font-medium ${
                            item.status === 'BULL' 
                              ? 'bg-green-600 text-white' 
                              : 'bg-red-600 text-white'
                          }`}
                        >
                          {item.status}
                        </div>
                      </div>
                      
                      {/* Status Icon */}
                      <div className="text-2xl">
                        {item.status === 'BULL' ? '🐂' : '🐻'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Trading Rules */}
              <div className="bg-gray-800 rounded-lg p-6 lg:col-span-2">
                <h2 className="text-xl font-semibold mb-4">Trading Rules</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-red-900 bg-opacity-50 border border-red-500 rounded p-4">
                    <h3 className="font-semibold text-red-300 mb-2">🚨 BEAR MARKET</h3>
                    <p className="text-sm text-gray-300">
                      3D Timeframe und höher BEAR - Hohe Vorsicht, keine Long Positionen
                    </p>
                  </div>
                  
                  <div className="bg-orange-900 bg-opacity-50 border border-orange-500 rounded p-4">
                    <h3 className="font-semibold text-orange-300 mb-2">⚠️ AUFPASSEN</h3>
                    <p className="text-sm text-gray-300">
                      Daily bearish, höhere Timeframes bullish → Möglicher Bärenmarkt, Vorsicht
                    </p>
                  </div>
                  
                  <div className="bg-yellow-900 bg-opacity-50 border border-yellow-500 rounded p-4">
                    <h3 className="font-semibold text-yellow-300 mb-2">⚡ TRADING CHANCE</h3>
                    <p className="text-sm text-gray-300">
                      4H bullish, 8H bearish → Vorsichtige Long-Chancen
                    </p>
                  </div>
                  
                  <div className="bg-green-900 bg-opacity-50 border border-green-500 rounded p-4">
                    <h3 className="font-semibold text-green-300 mb-2">🚀 STRONG BULL</h3>
                    <p className="text-sm text-gray-300">
                      4H + 8H bullish bei höheren Timeframes bull → Optimale Long-Bedingungen
                    </p>
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* New Market Situation and Charts Section */}
          <div className="mt-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* Box 1: Market Situation */}
              {marketData.row1 && (
                <div className="bg-gray-800 rounded-lg p-6">
                  <h2 className="text-xl font-semibold mb-4 text-yellow-500">
                    {marketData.row1.title}
                  </h2>
                  <div className="text-gray-300 whitespace-pre-wrap">
                    {marketData.row1.content}
                  </div>
                </div>
              )}

              {/* Box 2: Chart buttons only */}
              {(marketData.row2 || marketData.row3 || marketData.row4) && (
                <div className="bg-gray-800 rounded-lg p-6">
                  <h2 className="text-xl font-semibold mb-4 text-yellow-500">
                    Charts
                  </h2>
                  
                  {/* Three buttons in a row */}
                  <div className="grid grid-cols-3 gap-2">
                    {marketData.row2 && (
                      <button
                        onClick={() => openImageModal(marketData.row2.content, marketData.row2.title)}
                        className="bg-yellow-600 text-white px-2 py-2 rounded hover:bg-yellow-700 transition-colors flex items-center justify-center gap-1 text-xs"
                      >
                        <FiTrendingUp size={14} />
                        {marketData.row2.title}
                      </button>
                    )}

                    {marketData.row3 && (
                      <button
                        onClick={() => openImageModal(marketData.row3.content, marketData.row3.title)}
                        className="bg-gray-700 text-white px-2 py-2 rounded hover:bg-gray-600 transition-colors flex items-center justify-center gap-1 text-xs"
                      >
                        <FiTrendingUp size={14} />
                        {marketData.row3.title}
                      </button>
                    )}

                    {marketData.row4 && (
                      <button
                        onClick={() => openImageModal(marketData.row4.content, marketData.row4.title)}
                        className="bg-gray-700 text-white px-2 py-2 rounded hover:bg-gray-600 transition-colors flex items-center justify-center gap-1 text-xs"
                      >
                        <FiTrendingUp size={14} />
                        {marketData.row4.title}
                      </button>
                    )}
                  </div>
                </div>
              )}

            </div>

            {/* Market Data Loading State */}
            {marketLoading && (
              <div className="mt-6 bg-gray-800 rounded-lg p-6">
                <div className="flex justify-center items-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
                  <span className="ml-3 text-gray-300">Lade Marktdaten...</span>
                </div>
              </div>
            )}

            {/* Market Data Error State */}
            {marketError && (
              <div className="mt-6 bg-red-900 border-l-4 border-red-500 text-red-300 p-4">
                <p>Fehler beim Laden der Marktdaten: {marketError}</p>
              </div>
            )}
          </div>

          {data.length === 0 && !loading && !error && (
            <div className="bg-gray-800 rounded-lg p-8 text-center">
              <p className="text-gray-400">Keine Marktstruktur-Daten verfügbar</p>
            </div>
          )}

          {/* Image Modal */}
          {showImageModal && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
              <div className="bg-gray-800 rounded-lg max-w-4xl max-h-full overflow-auto">
                <div className="flex justify-between items-center p-4 border-b border-gray-700">
                  <h3 className="text-lg font-semibold text-white">{selectedImage.title}</h3>
                  <button
                    onClick={closeImageModal}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="p-4">
                  <img
                    src={selectedImage.url}
                    alt={selectedImage.title}
                    className="max-w-full h-auto rounded"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div className="hidden text-gray-400 text-center py-8">
                    <p>Bild konnte nicht geladen werden</p>
                    <p className="text-sm mt-2">URL: {selectedImage.url}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
const Dashboard = () => {
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [currentHoldingsPage, setCurrentHoldingsPage] = useState(1);
  const holdingsPerPage = 6; // Number of holdings to show per page - set to 6 as per requirements
  
  // Currency toggle state
  const [showEUR, setShowEUR] = useState(() => {
    return localStorage.getItem('showEUR') === 'true';
  });
  const [exchangeRate, setExchangeRate] = useState(null);
  const [loadingExchangeRate, setLoadingExchangeRate] = useState(false);
  
  // DeFi Position Closing state
  const [showClosePositionModal, setShowClosePositionModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [closeType, setCloseType] = useState('partial'); // 'partial' or 'full'
  const [closingPosition, setClosingPosition] = useState(false);
  const [loadingReset, setLoadingReset] = useState(false);
  const [loadingSpotReset, setLoadingSpotReset] = useState(false);
  
  const token = localStorage.getItem("token");

  // Calculate paginated holdings and total pages
  const regularHoldings = portfolio?.holdings.filter(h => h.type === 'regular') || [];
  const totalPages = Math.ceil(regularHoldings.length / holdingsPerPage);
  const paginatedHoldings = regularHoldings.slice(
    (currentHoldingsPage - 1) * holdingsPerPage,
    currentHoldingsPage * holdingsPerPage
  );

  const fetchPortfolio = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/portfolio`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setPortfolio(response.data);
      setLastUpdated(new Date());
      setError(null);
    } catch (error) {
      console.error("Error fetching portfolio:", error);
      setError("Failed to load portfolio data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch exchange rate
  const fetchExchangeRate = async () => {
    if (loadingExchangeRate) return; // Prevent multiple calls
    
    try {
      setLoadingExchangeRate(true);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/exchange-rate/usd-eur`);
      setExchangeRate(response.data.rate);
      console.log('USD/EUR exchange rate loaded:', response.data.rate);
    } catch (error) {
      console.error("Error fetching exchange rate:", error);
      // Use fallback rate if API fails
      setExchangeRate(0.85);
    } finally {
      setLoadingExchangeRate(false);
    }
  };

  // Toggle currency display
  const toggleCurrency = () => {
    const newShowEUR = !showEUR;
    setShowEUR(newShowEUR);
    localStorage.setItem('showEUR', newShowEUR.toString());
    
    // Load exchange rate if switching to EUR and don't have it yet
    if (newShowEUR && !exchangeRate) {
      fetchExchangeRate();
    }
  };

  // Format currency value
  const formatCurrency = (value, forceUSD = false) => {
    if (!value) return forceUSD ? '$0.00' : (showEUR ? '€0,00' : '$0.00');
    
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    if (showEUR && !forceUSD && exchangeRate) {
      const eurValue = numValue * exchangeRate;
      return new Intl.NumberFormat('de-DE', { 
        style: 'currency', 
        currency: 'EUR'
      }).format(eurValue);
    } else {
      return new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: 'USD'
      }).format(numValue);
    }
  };

  // DeFi Position closing functions
  const handleClosePosition = (position) => {
    console.log('Trying to close position:', position);
    console.log('Position ID:', position.position_id);
    setSelectedPosition(position);
    setShowClosePositionModal(true);
  };

  const fetchClosedPositions = async () => {
    // Removed - no longer needed
  };

  const resetDefiPnL = async () => {
    try {
      setLoadingReset(true);
      console.log('Resetting DeFi P&L...');
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/defi/positions/reset-pnl`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('DeFi P&L reset successfully:', response.data);
      
      // Refresh portfolio to show updated values
      await fetchPortfolio();
      
      alert(`DeFi P&L zurückgesetzt! ${response.data.deleted_entries} Einträge gelöscht.`);
      
    } catch (error) {
      console.error('Error resetting DeFi P&L:', error);
      alert('Fehler beim Zurücksetzen des DeFi P&L: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingReset(false);
    }
  };

  const resetSpotPnL = async () => {
    try {
      setLoadingSpotReset(true);
      console.log('Resetting Spot P&L...');
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/transactions/reset-pnl`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('Spot P&L reset successfully:', response.data);
      
      // Refresh portfolio to show updated values
      await fetchPortfolio();
      
      alert(`Spot P&L zurückgesetzt! ${response.data.deleted_entries} Einträge gelöscht.`);
      
    } catch (error) {
      console.error('Error resetting Spot P&L:', error);
      alert('Fehler beim Zurücksetzen des Spot P&L: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingSpotReset(false);
    }
  };

  const closePosition = async () => {
    if (!selectedPosition) return;
    
    try {
      setClosingPosition(true);
      
      // Prepare close data
      const closeData = {
        close_type: closeType,
        collateral_closed: selectedPosition.collateral_assets.map(asset => ({
          asset_id: asset.coin_id,
          amount_closed: closeType === 'full' ? asset.amount : asset.amount * 0.5, // For demo, 50% partial
          current_price: asset.current_price
        })),
        debt_repaid: selectedPosition.debt_assets.map(asset => ({
          asset_id: asset.coin_id,
          amount_repaid: closeType === 'full' ? Math.abs(asset.amount) : Math.abs(asset.amount) * 0.5, // For demo, 50% partial
          current_price: asset.current_price
        })),
        notes: `${closeType === 'full' ? 'Fully' : 'Partially'} closed position via dashboard`
      };
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/defi/positions/${selectedPosition.position_id}/close`,
        closeData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('Position closed successfully:', response.data);
      
      // Refresh portfolio and closed positions
      await fetchPortfolio();
      await fetchClosedPositions();
      
      // Close modal
      setShowClosePositionModal(false);
      setSelectedPosition(null);
      
    } catch (error) {
      console.error('Error closing position:', error);
      alert('Error closing position: ' + (error.response?.data?.detail || error.message));
    } finally {
      setClosingPosition(false);
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchPortfolio();
    
    // Load exchange rate if EUR display is enabled
    if (showEUR) {
      fetchExchangeRate();
    }
    
    // Set up auto-refresh every 60 seconds
    const intervalId = setInterval(() => {
      fetchPortfolio();
    }, 60000); // 60 seconds
    
    // Clean up on component unmount
    return () => clearInterval(intervalId);
  }, []);

  if (loading && !refreshing) {
    return (
      <div className="dashboard-container">
        <Sidebar active="dashboard" />
        <div className="dashboard-content">
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-400">Dashboard wird geladen...</div>
          </div>
        </div>
      </div>
    );
  }

  const COLORS = [
    "#FF8042",
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#8884d8",
    "#8dd1e1",
    "#82ca9d",
    "#a4de6c",
    "#d0ed57",
    "#ffc658"
  ];

  return (
    <div className="dashboard-container">
      <Sidebar active="dashboard" />
      <div className="dashboard-content">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-white">Dashboard</h1>
            <div className="flex items-center">
              {refreshing ? (
                <div className="flex items-center bg-gray-700 text-white px-3 py-1 rounded">
                  <div className="loading-spinner mr-2" style={{width: '12px', height: '12px'}}></div>
                  <span className="text-gray-300 text-sm">Wird aktualisiert...</span>
                </div>
              ) : lastUpdated ? (
                <div className="flex items-center bg-gray-800 text-gray-300 px-3 py-1 rounded border border-gray-700">
                  <FiClock className="mr-2 text-gray-400" size={14} />
                  <span className="text-gray-300 text-sm">
                    Aktualisiert: {lastUpdated.toLocaleTimeString('de-DE')}
                    <span className="text-gray-500 text-xs ml-2">(Auto-Update alle 60s)</span>
                  </span>
                </div>
              ) : null}
            </div>
          </div>

          {error ? (
            <div className="bg-red-500 text-white p-4 rounded mb-6">
              {error}
            </div>
          ) : portfolio ? (
            <div>
              {/* Currency Toggle Button */}
              <div className="mb-6 flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={toggleCurrency}
                    disabled={showEUR && loadingExchangeRate}
                    className="flex items-center space-x-2 bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg transition-all duration-200 disabled:opacity-50"
                    title="Zwischen USD und EUR wechseln"
                  >
                    <span className="text-white font-medium">
                      {showEUR ? 'EUR' : 'USD'}
                    </span>
                    <div className="relative w-12 h-6">
                      <div className={`absolute inset-0 bg-gray-600 rounded-full transition-colors duration-200 ${showEUR ? 'bg-yellow-600' : ''}`}></div>
                      <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${showEUR ? 'transform translate-x-6' : ''}`}></div>
                    </div>
                    {showEUR && loadingExchangeRate && (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-yellow-500 border-t-transparent"></div>
                    )}
                  </button>
                  
                  {showEUR && exchangeRate && (
                    <span className="text-gray-400 text-sm">
                      1 USD = {exchangeRate.toFixed(4)} EUR
                    </span>
                  )}
                </div>
                
                <div className="text-gray-400 text-sm">
                  {lastUpdated && (
                    <span>Letztes Update: {lastUpdated.toLocaleTimeString('de-DE')}</span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-gray-800 p-6 rounded-lg shadow hover-lift slide-in" style={{animationDelay: "0s"}}>
                  <h2 className="text-gray-400 text-sm mb-2">Portfolio-Wert</h2>
                  <p className="text-3xl font-bold text-white">
                    {formatCurrency(portfolio.total_value)}
                  </p>
                </div>
                <div className="bg-gray-800 p-6 rounded-lg shadow hover-lift slide-in" style={{animationDelay: "0.1s"}}>
                  <h2 className="text-gray-400 text-sm mb-2">Unrealisierte P&L</h2>
                  <p className={`text-3xl font-bold ${portfolio.total_profit_loss >= 0 ? "text-green-500" : "text-red-500"}`}>
                    {formatCurrency(portfolio.total_profit_loss)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Offene Positionen</p>
                </div>
                <div className="bg-gray-800 p-6 rounded-lg shadow hover-lift slide-in" style={{animationDelay: "0.15s"}}>
                  <h2 className="text-gray-400 text-sm mb-2">Spot P&L</h2>
                  <p className={`text-3xl font-bold ${(portfolio.realized_profit_loss || 0) >= 0 ? "text-green-500" : "text-red-500"}`}>
                    {formatCurrency(portfolio.realized_profit_loss || 0)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {portfolio.realized_pnl_trades || 0} geschlossene Trade{(portfolio.realized_pnl_trades || 0) !== 1 ? 's' : ''}
                    <button
                      onClick={resetSpotPnL}
                      disabled={loadingSpotReset}
                      className="ml-2 text-red-400 hover:text-red-300 underline text-xs disabled:opacity-50"
                    >
                      {loadingSpotReset ? 'Zurücksetzung...' : 'Anzeige zurücksetzen'}
                    </button>
                  </p>
                </div>
                <div className="bg-gray-800 p-6 rounded-lg shadow hover-lift slide-in" style={{animationDelay: "0.2s"}}>
                  <h2 className="text-gray-400 text-sm mb-2">DeFi P&L</h2>
                  <p className={`text-3xl font-bold ${(portfolio.defi_realized_profit_loss || 0) >= 0 ? "text-green-500" : "text-red-500"}`}>
                    {formatCurrency(portfolio.defi_realized_profit_loss || 0)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {portfolio.defi_realized_trades || 0} geschlossene Position{(portfolio.defi_realized_trades || 0) !== 1 ? 'en' : ''}
                    <button
                      onClick={resetDefiPnL}
                      disabled={loadingReset}
                      className="ml-2 text-red-400 hover:text-red-300 underline text-xs disabled:opacity-50"
                    >
                      {loadingReset ? 'Zurücksetzung...' : 'Anzeige zurücksetzen'}
                    </button>
                  </p>
                </div>
              </div>
              
              {/* Second Row with Total and Net Worth */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gray-800 p-6 rounded-lg shadow hover-lift slide-in" style={{animationDelay: "0.25s"}}>
                  <h2 className="text-gray-400 text-sm mb-2">Gesamt P&L</h2>
                  <p className={`text-3xl font-bold ${(portfolio.total_combined_profit_loss || 0) >= 0 ? "text-green-500" : "text-red-500"}`}>
                    {formatCurrency(portfolio.total_combined_profit_loss || 0)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    ({portfolio.profit_loss_percentage.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}%)
                  </p>
                </div>
                <div className="bg-gray-800 p-6 rounded-lg shadow hover-lift slide-in" style={{animationDelay: "0.3s"}}>
                  <h2 className="text-gray-400 text-sm mb-2">Schulden gesamt</h2>
                  <p className="text-3xl font-bold text-red-500">
                    {formatCurrency(portfolio.total_debt)}
                  </p>
                </div>
                <div className="bg-gray-800 p-6 rounded-lg shadow hover-lift slide-in" style={{animationDelay: "0.35s"}}>
                  <h2 className="text-gray-400 text-sm mb-2">Nettovermögen</h2>
                  <p className={`text-3xl font-bold ${portfolio.net_worth >= 0 ? "text-white" : "text-red-500"}`}>
                    {formatCurrency(portfolio.net_worth)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-800 p-6 rounded-lg shadow">
                  <h2 className="text-xl font-bold mb-4">Portfolio-Aufteilung</h2>
                  <div className="grid grid-cols-1 gap-6">
                    {/* Spot Bestände Pie Chart */}
                    <div>
                      <h3 className="text-md font-semibold mb-2 text-gray-300">Spot Bestände</h3>
                      {portfolio.holdings.filter(h => h.type === 'regular').length > 0 ? (
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={portfolio.holdings.filter(h => h.type === 'regular')}
                                cx="50%"
                                cy="50%"
                                labelLine={true}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="current_value"
                                nameKey="coin_symbol"
                                label={({ coin_symbol, percent }) => `${coin_symbol} ${(percent * 100).toLocaleString('de-DE', {minimumFractionDigits: 0, maximumFractionDigits: 0})}%`}
                              >
                                {portfolio.holdings.filter(h => h.type === 'regular').map((entry, index) => (
                                  <Cell key={`regular-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip
                                formatter={(value, name, props) => [`$${value.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, props.payload.coin_name]}
                                contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151', borderRadius: '0.5rem' }}
                                itemStyle={{ color: '#e5e7eb' }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-40 text-gray-400">
                          Keine Spot Bestände gefunden
                        </div>
                      )}
                    </div>

                    {/* DeFi Positions Pie Chart */}
                    <div>
                      <h3 className="text-md font-semibold mb-2 text-gray-300">DeFi Positionen</h3>
                      {portfolio.holdings.filter(h => h.type === 'defi_collateral' || h.type === 'defi_debt').length > 0 ? (
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={Object.values(
                                  portfolio.holdings
                                    .filter(h => h.type === 'defi_collateral' || h.type === 'defi_debt')
                                    .reduce((acc, item) => {
                                      // Create a unique key for each position
                                      const key = item.position_name;
                                      
                                      // If we haven't seen this position before, initialize it
                                      if (!acc[key]) {
                                        acc[key] = {
                                          position_name: item.position_name,
                                          current_value: 0,
                                          health_factor: item.health_factor
                                        };
                                      }
                                      
                                      // Add this item's value to the position
                                      if (item.type === 'defi_collateral') {
                                        acc[key].current_value += Math.abs(item.current_value || 0);
                                      } else if (item.type === 'defi_debt') {
                                        acc[key].current_value -= Math.abs(item.current_value || 0);
                                      }
                                      
                                      return acc;
                                    }, {})
                                ).filter(position => position.current_value > 0)}
                                cx="50%"
                                cy="50%"
                                labelLine={true}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="current_value"
                                nameKey="position_name"
                                label={({ position_name, percent }) => `${position_name} ${(percent * 100).toLocaleString('de-DE', {minimumFractionDigits: 0, maximumFractionDigits: 0})}%`}
                              >
                                {Object.values(
                                  portfolio.holdings
                                    .filter(h => h.type === 'defi_collateral' || h.type === 'defi_debt')
                                    .reduce((acc, item) => {
                                      const key = item.position_name;
                                      if (!acc[key]) {
                                        acc[key] = {
                                          position_name: item.position_name,
                                          current_value: 0,
                                          health_factor: item.health_factor
                                        };
                                      }
                                      if (item.type === 'defi_collateral') {
                                        acc[key].current_value += Math.abs(item.current_value || 0);
                                      } else if (item.type === 'defi_debt') {
                                        acc[key].current_value -= Math.abs(item.current_value || 0);
                                      }
                                      return acc;
                                    }, {})
                                )
                                .filter(position => position.current_value > 0)
                                .map((entry, index) => (
                                  <Cell key={`defi-cell-${index}`} fill={COLORS[(index + 4) % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip
                                formatter={(value) => [`$${value.toFixed(2)}`, "Positionswert"]}
                                contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151', borderRadius: '0.5rem' }}
                                itemStyle={{ color: '#e5e7eb' }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-40 text-gray-400">
                          Keine DeFi Positionen gefunden
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Regular Spot Bestände with Pagination */}
                <div className="bg-gray-800 p-6 rounded-lg shadow">
                  <h2 className="text-xl font-bold mb-4">Spot Bestände</h2>
                  {portfolio.holdings.filter(h => h.type === 'regular').length > 0 ? (
                    <>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-700">
                          <thead>
                            <tr>
                              <th
                                scope="col"
                                className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                              >
                                Coin
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                              >
                                Menge
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                              >
                                Wert
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                              >
                                G/V
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-700">
                            {paginatedHoldings.map((coin) => (
                              <tr key={`${coin.type}-${coin.coin_id}`}>
                                <td className="px-3 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    {coin.coin_image && (
                                      <img
                                        src={coin.coin_image}
                                        alt={coin.coin_name}
                                        className="w-6 h-6 mr-2 rounded-full"
                                      />
                                    )}
                                    <div>
                                      <div className="font-medium text-white">{coin.coin_symbol}</div>
                                      <div className="text-xs text-gray-400">{coin.coin_name}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-3 py-4 whitespace-nowrap">
                                  <div className="text-sm text-white">{coin.amount.toLocaleString('de-DE', {minimumFractionDigits: 6, maximumFractionDigits: 6})}</div>
                                  <div className="text-xs text-gray-400">{formatCurrency(coin.current_price, true)}</div>
                                </td>
                                <td className="px-3 py-4 whitespace-nowrap">
                                  <div className="text-sm text-white">{formatCurrency(coin.current_value)}</div>
                                </td>
                                <td className="px-3 py-4 whitespace-nowrap">
                                  <div className={`flex items-center text-sm ${coin.profit_loss >= 0 ? "text-green-500" : "text-red-500"}`}>
                                    {coin.profit_loss >= 0 ? <FiTrendingUp className="mr-1" /> : <FiTrendingDown className="mr-1" />}
                                    <span>
                                      ${Math.abs(coin.profit_loss).toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})} 
                                      ({Math.abs(coin.profit_loss_percentage).toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}%)
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Pagination Controls */}
                      {totalPages > 1 && (
                        <div className="flex justify-center items-center mt-4 space-x-2">
                          <button
                            onClick={() => setCurrentHoldingsPage(p => Math.max(1, p - 1))}
                            disabled={currentHoldingsPage === 1}
                            className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            &lt;
                          </button>
                          
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                            <button
                              key={page}
                              onClick={() => setCurrentHoldingsPage(page)}
                              className={`px-3 py-1 rounded ${
                                currentHoldingsPage === page
                                  ? 'bg-yellow-600 text-white'
                                  : 'bg-gray-700 text-white hover:bg-gray-600'
                              }`}
                            >
                              {page}
                            </button>
                          ))}
                          
                          <button
                            onClick={() => setCurrentHoldingsPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentHoldingsPage === totalPages}
                            className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            &gt;
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-gray-400">
                      Keine Spot Bestände gefunden
                    </div>
                  )}
                </div>
              </div>
              
              {/* DeFi Positions Section */}
              {(portfolio.holdings.filter(h => h.type === 'defi_collateral' || h.type === 'defi_debt').length > 0) && (
                <div className="bg-gray-800 p-6 rounded-lg shadow mb-6">
                  <h2 className="text-xl font-bold mb-4">DeFi Positionen</h2>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-700">
                      <thead>
                        <tr>
                          <th
                            scope="col"
                            className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                          >
                            Position
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                          >
                            Gesundheitsfaktor
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                          >
                            Collateral
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                          >
                            Debt
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                          >
                            PNL
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider"
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {/* Group holdings by position name */}
                        {Object.values(
                          portfolio.holdings
                            .filter(h => h.type === 'defi_collateral' || h.type === 'defi_debt')
                            .reduce((acc, item) => {
                              // Create a unique key for each position
                              const key = item.position_name;
                              
                              // If we haven't seen this position before, initialize it
                              if (!acc[key]) {
                                acc[key] = {
                                  position_name: item.position_name,
                                  position_id: item.position_id, // Add position_id for closing
                                  health_factor: item.health_factor,
                                  purchase_date: item.purchase_date,
                                  collateral_assets: [],
                                  debt_assets: [],
                                  total_collateral_value: 0,
                                  total_debt_value: 0,
                                  total_profit_loss: 0,
                                };
                              }
                              
                              // Add this item to the appropriate asset list
                              if (item.type === 'defi_collateral') {
                                acc[key].collateral_assets.push(item);
                                acc[key].total_collateral_value += Math.abs(item.current_value || 0);
                                acc[key].total_profit_loss += (item.profit_loss || 0);
                                // Copy position_id from the first collateral item
                                if (!acc[key].position_id && item.position_id) {
                                  acc[key].position_id = item.position_id;
                                }
                              } else if (item.type === 'defi_debt') {
                                acc[key].debt_assets.push(item);
                                acc[key].total_debt_value += Math.abs(item.current_value || 0);
                                // Copy position_id from the first debt item if not set
                                if (!acc[key].position_id && item.position_id) {
                                  acc[key].position_id = item.position_id;
                                }
                              }
                              
                              return acc;
                            }, {})
                        ).sort((a, b) => a.position_name.localeCompare(b.position_name))
                        .map((position) => (
                          <tr key={position.position_name}>
                            {/* Position Name */}
                            <td className="px-3 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div>
                                  <div className="font-medium text-white">{position.position_name}</div>
                                  {position.purchase_date && (
                                    <div className="text-xs text-gray-400">
                                      Created: {new Date(position.purchase_date).toLocaleDateString()}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Health Factor */}
                            <td className="px-3 py-4 whitespace-nowrap">
                              <div className={`font-medium text-lg ${
                                position.health_factor >= 1.5 ? "text-green-400" : 
                                position.health_factor >= 1.0 ? "text-yellow-400" : 
                                "text-red-400"
                              }`}>
                                {position.health_factor.toFixed(2)}
                              </div>
                            </td>

                            {/* Collateral */}
                            <td className="px-3 py-4 whitespace-nowrap">
                              <div className="text-sm text-white font-medium">
                                ${position.total_collateral_value.toFixed(2)}
                              </div>
                              <div className="text-xs text-gray-400 mt-1">
                                {position.collateral_assets.map((asset, index) => (
                                  <div key={`collateral-${asset.coin_id}`} className="flex items-center mt-1">
                                    {asset.coin_image && (
                                      <img
                                        src={asset.coin_image}
                                        alt={asset.coin_name}
                                        className="w-5 h-5 mr-1 rounded-full"
                                      />
                                    )}
                                    <span className="text-green-400">
                                      {asset.amount.toFixed(4)} {asset.coin_symbol}
                                    </span>
                                    <span className="ml-1">({formatCurrency(asset.current_value)})</span>
                                    {index < position.collateral_assets.length - 1 ? ', ' : ''}
                                  </div>
                                ))}
                              </div>
                            </td>

                            {/* Debt */}
                            <td className="px-3 py-4 whitespace-nowrap">
                              <div className="text-sm text-white font-medium">
                                ${position.total_debt_value.toFixed(2)}
                              </div>
                              <div className="text-xs text-gray-400 mt-1">
                                {position.debt_assets.map((asset, index) => (
                                  <div key={`debt-${asset.coin_id}`} className="flex items-center mt-1">
                                    {asset.coin_image && (
                                      <img
                                        src={asset.coin_image}
                                        alt={asset.coin_name}
                                        className="w-5 h-5 mr-1 rounded-full"
                                      />
                                    )}
                                    <span className="text-red-400">
                                      {Math.abs(asset.amount).toFixed(4)} {asset.coin_symbol}
                                    </span>
                                    <span className="ml-1">({formatCurrency(Math.abs(asset.current_value))})</span>
                                    {index < position.debt_assets.length - 1 ? ', ' : ''}
                                  </div>
                                ))}
                              </div>
                            </td>

                            {/* PNL */}
                            <td className="px-3 py-4 whitespace-nowrap">
                              <div className="text-sm text-white font-medium">
                                ${(position.total_collateral_value - position.total_debt_value).toFixed(2)}
                              </div>
                              <div className={`flex items-center text-xs mt-1 ${
                                position.total_profit_loss >= 0 ? "text-green-500" : "text-red-500"
                              }`}>
                                {position.total_profit_loss >= 0 ? (
                                  <FiTrendingUp className="mr-1" />
                                ) : (
                                  <FiTrendingDown className="mr-1" />
                                )}
                                <span>
                                  ${Math.abs(position.total_profit_loss).toFixed(2)}
                                </span>
                              </div>
                            </td>
                            
                            {/* Actions */}
                            <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                onClick={() => handleClosePosition(position)}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs transition-colors"
                                title="Position schließen"
                              >
                                Position schließen
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gray-800 p-6 rounded-lg shadow">
              <div className="text-gray-400">No portfolio data available</div>
            </div>
          )}

        </div>
      </div>
      
      {/* Close Position Modal */}
      {showClosePositionModal && selectedPosition && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-lg font-bold text-white mb-4">Position schließen</h3>
            
            <div className="mb-4">
              <p className="text-gray-300 mb-2">Position: {selectedPosition.position_name}</p>
              <p className="text-gray-300 mb-4">
                Aktueller Wert: ${(selectedPosition.total_collateral_value - selectedPosition.total_debt_value).toFixed(2)}
              </p>
              
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Schließungsart:</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="closeType"
                      value="partial"
                      checked={closeType === 'partial'}
                      onChange={(e) => setCloseType(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-white">Partiell schließen (50%)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="closeType"
                      value="full"
                      checked={closeType === 'full'}
                      onChange={(e) => setCloseType(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-white">Vollständig schließen (100%)</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowClosePositionModal(false);
                  setSelectedPosition(null);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                disabled={closingPosition}
              >
                Abbrechen
              </button>
              <button
                onClick={closePosition}
                disabled={closingPosition}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {closingPosition ? 'Schließe...' : 'Position schließen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Transactions Component
const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [coins, setCoins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [transactionType, setTransactionType] = useState("buy");
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split('T')[0]);
  const [amount, setAmount] = useState("");
  const [pricePerCoin, setPricePerCoin] = useState("");
  const [totalSpent, setTotalSpent] = useState("");
  const [notes, setNotes] = useState("");
  const [taxExemptionEnabled, setTaxExemptionEnabled] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Edit states
  const [isEditing, setIsEditing] = useState(false);
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  
  // Notes popup states
  const [isNotesPopupOpen, setIsNotesPopupOpen] = useState(false);
  const [selectedTransactionNotes, setSelectedTransactionNotes] = useState("");
  
  // Notes popup functions
  const handleOpenNotesPopup = (transaction) => {
    setSelectedTransactionNotes(transaction.notes || "Keine Notizen verfügbar");
    setIsNotesPopupOpen(true);
  };

  const handleCloseNotesPopup = () => {
    setIsNotesPopupOpen(false);
    setSelectedTransactionNotes("");
  };
  
  const token = localStorage.getItem("token");

  // Helper function to display tax exemption status with colors
  const getTaxExemptionStatus = (transaction) => {
    if (!transaction.tax_exemption_enabled || !transaction.date) {
      return (
        <span className="px-2 py-1 text-xs rounded bg-gray-600 text-gray-300">
          -
        </span>
      );
    }

    const purchaseDate = new Date(transaction.date);
    const exemptionDate = new Date(purchaseDate);
    exemptionDate.setFullYear(exemptionDate.getFullYear() + 1); // Add 1 year
    
    const now = new Date();
    const timeDiff = exemptionDate.getTime() - now.getTime();
    
    if (timeDiff <= 0) {
      return (
        <span className="px-2 py-1 text-xs rounded bg-green-600 text-green-100">
          🟢 Steuerfrei
        </span>
      );
    }
    
    // Calculate remaining time
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    let timeText = '';
    if (days > 0) {
      timeText = `${days}d ${hours}h`;
    } else if (hours > 0) {
      timeText = `${hours}h`;
    } else {
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
      timeText = `${minutes}m`;
    }
    
    return (
      <span className="px-2 py-1 text-xs rounded bg-yellow-600 text-yellow-100">
        🟡 {timeText}
      </span>
    );
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/transactions`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setTransactions(response.data);
      setError(null);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setError("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  const searchCoins = async (query) => {
    if (!query) {
      setCoins([]);
      return;
    }
    
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/coins?query=${query}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setCoins(response.data);
      
      // Log successful coin search for debugging
      console.log(`Found ${response.data.length} coins matching "${query}"`);
    } catch (error) {
      console.error("Error searching coins:", error);
    }
  };

  const handleSelectCoin = (coin) => {
    console.log("Selected coin:", coin);
    setSelectedCoin(coin);
    setCoins([]);
    setSearchTerm(coin.name);
    setPricePerCoin(coin.current_price || "");
    calculateTotal();
  };

  const calculateTotal = () => {
    if (amount && pricePerCoin) {
      const total = parseFloat(amount) * parseFloat(pricePerCoin);
      setTotalSpent(total.toFixed(2));
    } else {
      setTotalSpent("");
    }
  };

  useEffect(() => {
    calculateTotal();
  }, [amount, pricePerCoin]);

  const handleAmountChange = (e) => {
    setAmount(e.target.value);
  };

  const handlePriceChange = (e) => {
    setPricePerCoin(e.target.value);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    searchCoins(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);
    
    if (!selectedCoin) {
      setFormError("Please select a coin");
      return;
    }
    
    if (!amount || parseFloat(amount) <= 0) {
      setFormError("Please enter a valid amount");
      return;
    }
    
    if (!pricePerCoin || parseFloat(pricePerCoin) <= 0) {
      setFormError("Please enter a valid price per coin");
      return;
    }
    
    const transactionData = {
      coin_id: selectedCoin.id,
      transaction_type: transactionType,
      date: transactionDate,
      amount: parseFloat(amount),
      price_per_coin: parseFloat(pricePerCoin),
      total_spent: parseFloat(totalSpent),
      notes: notes || null,
      tax_exemption_enabled: taxExemptionEnabled
    };
    
    // Log for debugging
    console.log("Submitting transaction data:", transactionData);
    
    try {
      setIsSubmitting(true);
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/transactions`, transactionData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      console.log("Transaction created successfully:", response.data);
      
      // Reset form
      setSelectedCoin(null);
      setSearchTerm("");
      setTransactionType("buy");
      setAmount("");
      setPricePerCoin("");
      setTotalSpent("");
      setNotes("");
      setTaxExemptionEnabled(false);
      
      // Show success message and refresh transactions
      setFormSuccess("Transaction added successfully");
      fetchTransactions();
      
    } catch (error) {
      console.error("Error creating transaction:", error.response?.data || error.message);
      setFormError(error.response?.data?.detail || "Failed to create transaction. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTransaction = async (id) => {
    if (!window.confirm("Sind Sie sicher, dass Sie diese Transaktion löschen möchten?")) {
      return;
    }
    
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/transactions/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Remove from state
      setTransactions(transactions.filter(tx => tx.id !== id));
      
    } catch (error) {
      console.error("Error deleting transaction:", error);
      alert("Transaktion konnte nicht gelöscht werden");
    }
  };

  // Edit transaction functions
  const handleEditTransaction = (transaction) => {
    setIsEditing(true);
    setEditingTransactionId(transaction.id);
    
    // Populate form with transaction data
    setSelectedCoin({
      id: transaction.coin_id,
      symbol: transaction.coin_id.toUpperCase(),
      name: transaction.coin_id
    });
    setTransactionType(transaction.transaction_type);
    setTransactionDate(transaction.date ? transaction.date.split('T')[0] : new Date().toISOString().split('T')[0]);
    setAmount(transaction.amount.toString());
    setPricePerCoin(transaction.price_per_coin.toString());
    setTotalSpent(transaction.total_spent.toString());
    setNotes(transaction.notes || "");
    setTaxExemptionEnabled(transaction.tax_exemption_enabled || false);
    
    // Scroll to form
    document.getElementById('transaction-form').scrollIntoView({ behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingTransactionId(null);
    
    // Reset form
    setSelectedCoin(null);
    setSearchTerm("");
    setTransactionType("buy");
    setTransactionDate(new Date().toISOString().split('T')[0]);
    setAmount("");
    setPricePerCoin("");
    setTotalSpent("");
    setNotes("");
    setTaxExemptionEnabled(false);
    setFormError(null);
    setFormSuccess(null);
  };

  const handleUpdateTransaction = async () => {
    if (!selectedCoin || !amount || !pricePerCoin) {
      setFormError("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    setFormError(null);
    setFormSuccess(null);

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/transactions/${editingTransactionId}`,
        {
          coin_id: selectedCoin.id,
          transaction_type: transactionType,
          date: transactionDate,
          amount: parseFloat(amount),
          price_per_coin: parseFloat(pricePerCoin),
          total_spent: parseFloat(totalSpent || (parseFloat(amount) * parseFloat(pricePerCoin))),
          notes: notes,
          tax_exemption_enabled: taxExemptionEnabled
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log("Transaction updated successfully:", response.data);
      
      // Reset edit state and form
      handleCancelEdit();
      
      // Show success message and refresh transactions
      setFormSuccess("Transaktion erfolgreich aktualisiert");
      fetchTransactions();
      
    } catch (error) {
      console.error("Error updating transaction:", error.response?.data || error.message);
      setFormError(error.response?.data?.detail || "Transaktion konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  return (
    <div className="dashboard-container">
      <Sidebar active="transactions" />
      <div className="dashboard-content">
        <div className="p-6">
          <h1 className="text-2xl font-bold text-white mb-6">Transaktionen</h1>
          
          <div className="bg-gray-800 p-6 rounded-lg shadow mb-6" id="transaction-form">
            <h2 className="text-xl font-bold text-white mb-4">
              {isEditing ? "Transaktion bearbeiten" : "Transaktion hinzufügen"}
            </h2>
            
            <form onSubmit={handleSubmit}>
              {formError && (
                <div className="bg-red-500 text-white p-3 rounded mb-4">
                  {formError}
                </div>
              )}
              
              {formSuccess && (
                <div className="bg-green-500 text-white p-3 rounded mb-4">
                  {formSuccess}
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="relative">
                  <label className="block text-gray-400 text-sm mb-2">Coin</label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search for a coin"
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
                  />
                  {coins.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-gray-700 border border-gray-600 rounded shadow-lg max-h-60 overflow-auto">
                      {coins.map((coin) => (
                        <div
                          key={coin.id}
                          onClick={() => handleSelectCoin(coin)}
                          className="flex items-center p-2 hover:bg-gray-600 cursor-pointer"
                        >
                          {coin.image && (
                            <img src={coin.image} alt={coin.name} className="w-6 h-6 mr-2 rounded-full" />
                          )}
                          <div>
                            <div className="text-white">{coin.name} ({coin.symbol.toUpperCase()})</div>
                            <div className="text-xs text-gray-400">${coin.current_price?.toFixed(2) || "N/A"}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Transaction Type</label>
                  <select
                    value={transactionType}
                    onChange={(e) => setTransactionType(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-indigo-500"
                  >
                    <option value="buy">Buy</option>
                    <option value="sell">Sell</option>
                    <option value="transfer">Transfer</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Kaufdatum</label>
                  <input
                    type="date"
                    value={transactionDate}
                    onChange={(e) => setTransactionDate(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Amount</label>
                  <input
                    type="number"
                    step="any"
                    value={amount}
                    onChange={handleAmountChange}
                    placeholder="0.00"
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Price per Coin (USD)</label>
                  <input
                    type="number"
                    step="any"
                    value={pricePerCoin}
                    onChange={handlePriceChange}
                    placeholder="0.00"
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Total (USD)</label>
                  <input
                    type="number"
                    step="any"
                    value={totalSpent}
                    onChange={(e) => setTotalSpent(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-gray-400 text-sm mb-2">Notes (Optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notizen zu dieser Transaktion hinzufügen"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
                  rows="2"
                ></textarea>
              </div>

              <div className="mb-4">
                <label className="flex items-center space-x-2 text-gray-400">
                  <input
                    type="checkbox"
                    checked={taxExemptionEnabled}
                    onChange={(e) => setTaxExemptionEnabled(e.target.checked)}
                    className="form-checkbox h-4 w-4 text-yellow-600 rounded focus:ring-yellow-500 focus:ring-offset-gray-800"
                  />
                  <span className="text-sm">
                    Verjährungsfrist aktivieren
                    <span className="block text-xs text-gray-500">
                      Startet 1-Jahres-Timer ab Kaufdatum für steuerfreien Verkauf
                    </span>
                  </span>
                </label>
              </div>
              
              <div className="flex gap-4">
                {isEditing ? (
                  <>
                    <button
                      type="button"
                      onClick={handleUpdateTransaction}
                      disabled={isSubmitting || !selectedCoin}
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? "Wird aktualisiert..." : "Transaktion aktualisieren"}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      disabled={isSubmitting}
                      className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:bg-gray-700"
                    >
                      Abbrechen
                    </button>
                  </>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50"
                  >
                    {isSubmitting ? "Wird hinzugefügt..." : "Transaktion hinzufügen"}
                  </button>
                )}
              </div>
            </form>
          </div>
          
          <div className="bg-gray-800 p-6 rounded-lg shadow">
            <h2 className="text-xl font-bold text-white mb-4">Transaktionsverlauf</h2>
            
            {loading ? (
              <div className="text-gray-400">Transaktionen werden geladen...</div>
            ) : error ? (
              <div className="bg-red-500 text-white p-3 rounded">
                {error}
              </div>
            ) : transactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Date
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Coin
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Type
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Amount
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Price
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Total
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Realized P&L
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Steuerstatus
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {transactions.map((tx) => (
                      <tr key={tx.id}>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-300">
                          {new Date(tx.date).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-white">
                          {tx.coin_id}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded ${
                            tx.transaction_type === "buy"
                              ? "bg-green-800 text-green-100"
                              : tx.transaction_type === "sell"
                              ? "bg-red-800 text-red-100"
                              : "bg-blue-800 text-blue-100"
                          }`}>
                            {tx.transaction_type}
                          </span>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-white">
                          {tx.amount.toFixed(6)}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-white">
                          ${tx.price_per_coin.toFixed(2)}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-white">
                          ${tx.total_spent.toFixed(2)}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm">
                          {tx.transaction_type === "sell" && (tx.realized_pnl !== undefined && tx.realized_pnl !== null) ? (
                            <div>
                              <div className={`font-medium ${tx.realized_pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                ${tx.realized_pnl >= 0 ? '+' : ''}${tx.realized_pnl.toFixed(2)}
                              </div>
                              {tx.realized_pnl_percentage !== undefined && tx.realized_pnl_percentage !== null && (
                                <div className={`text-xs ${tx.realized_pnl_percentage >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  ({tx.realized_pnl_percentage >= 0 ? '+' : ''}{tx.realized_pnl_percentage.toFixed(2)}%)
                                </div>
                              )}
                            </div>
                          ) : tx.transaction_type === "sell" ? (
                            <span className="text-gray-500 text-xs">Calculating...</span>
                          ) : (
                            <span className="text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm">
                          {getTaxExemptionStatus(tx)}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEditTransaction(tx)}
                              className="text-blue-400 hover:text-blue-300"
                              title="Transaktion bearbeiten"
                            >
                              <FiEdit />
                            </button>
                            <button
                              onClick={() => handleOpenNotesPopup(tx)}
                              className="text-yellow-400 hover:text-yellow-300"
                              title="Notizen anzeigen"
                            >
                              <FiFileText />
                            </button>
                            <button
                              onClick={() => handleDeleteTransaction(tx.id)}
                              className="text-red-400 hover:text-red-300"
                              title="Transaktion löschen"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-gray-400">No transactions found</div>
            )}
          </div>
        </div>
        
        {/* Notes Popup Modal */}
        {isNotesPopupOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={handleCloseNotesPopup}
          >
            <div 
              className="bg-gray-800 p-6 rounded-lg shadow-lg max-w-md w-full mx-4 max-h-96 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-white">Notizen</h3>
                <button
                  onClick={handleCloseNotesPopup}
                  className="text-gray-400 hover:text-white transition-colors"
                  title="Schließen"
                >
                  <FiX size={20} />
                </button>
              </div>
              <div className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                {selectedTransactionNotes}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Portfolio Component
const Portfolio = () => {
  return (
    <div className="dashboard-container">
      <Sidebar active="portfolio" />
      <div className="dashboard-content">
        <div className="p-6">
          <h1 className="text-2xl font-bold text-white mb-6">Portfolio</h1>
          <div className="bg-gray-800 p-6 rounded-lg shadow">
            <p className="text-gray-400">Portfolio analysis coming soon...</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Debt Tracker Component
const DebtTracker = () => {
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const token = localStorage.getItem("token");

  const fetchDebts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/debt`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setDebts(response.data);
      setError(null);
    } catch (error) {
      console.error("Error fetching debts:", error);
      setError("Failed to load debt data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);
    
    if (!amount || parseFloat(amount) <= 0) {
      setFormError("Please enter a valid amount");
      return;
    }
    
    if (!description) {
      setFormError("Please enter a description");
      return;
    }
    
    const debtData = {
      amount: parseFloat(amount),
      description
    };
    
    // Log for debugging
    console.log("Submitting debt data:", debtData);
    
    try {
      setIsSubmitting(true);
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/debt`, debtData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      console.log("Debt entry created successfully:", response.data);
      
      // Reset form
      setAmount("");
      setDescription("");
      
      // Show success message and refresh debts
      setFormSuccess("Debt entry added successfully");
      fetchDebts();
      
    } catch (error) {
      console.error("Error creating debt entry:", error.response?.data || error.message);
      setFormError(error.response?.data?.detail || "Failed to create debt entry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDebt = async (id) => {
    if (!window.confirm("Sind Sie sicher, dass Sie diesen Schuldeneintrag löschen möchten?")) {
      return;
    }
    
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/debt/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Remove from state
      setDebts(debts.filter(debt => debt.id !== id));
      
    } catch (error) {
      console.error("Error deleting debt entry:", error);
      alert("Schuldeneintrag konnte nicht gelöscht werden");
    }
  };

  useEffect(() => {
    fetchDebts();
  }, []);

  const totalDebt = debts.reduce((sum, debt) => sum + debt.amount, 0);

  return (
    <div className="dashboard-container">
      <Sidebar active="debt" />
      <div className="dashboard-content">
        <div className="p-6">
          <h1 className="text-2xl font-bold text-white mb-6">Schulden-Tracker</h1>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-gray-800 p-6 rounded-lg shadow col-span-1 md:col-span-2">
              <h2 className="text-xl font-bold text-white mb-4">Schuldeneintrag hinzufügen</h2>
              
              <form onSubmit={handleSubmit}>
                {formError && (
                  <div className="bg-red-500 text-white p-3 rounded mb-4">
                    {formError}
                  </div>
                )}
                
                {formSuccess && (
                  <div className="bg-green-500 text-white p-3 rounded mb-4">
                    {formSuccess}
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Amount (USD)</label>
                    <input
                      type="number"
                      step="any"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Description</label>
                    <input
                      type="text"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="e.g., Credit Card Debt, Mortgage, etc."
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
                    />
                  </div>
                </div>
                
                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50"
                  >
                    {isSubmitting ? "Wird hinzugefügt..." : "Schuldeneintrag hinzufügen"}
                  </button>
                </div>
              </form>
            </div>
            
            <div className="bg-gray-800 p-6 rounded-lg shadow">
              <h2 className="text-xl font-bold text-white mb-4">Total Debt</h2>
              <div className="text-4xl font-bold text-red-500">${totalDebt.toFixed(2)}</div>
              <div className="text-sm text-gray-400 mt-2">Across {debts.length} entries</div>
            </div>
          </div>
          
          <div className="bg-gray-800 p-6 rounded-lg shadow">
            <h2 className="text-xl font-bold text-white mb-4">Schuldeneinträge</h2>
            
            {loading ? (
              <div className="text-gray-400">Schuldeneinträge werden geladen...</div>
            ) : error ? (
              <div className="bg-red-500 text-white p-3 rounded">
                {error}
              </div>
            ) : debts.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Date
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Description
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Amount
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {debts.map((debt) => (
                      <tr key={debt.id}>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-300">
                          {new Date(debt.date).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-white">
                          {debt.description}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-white">
                          ${debt.amount.toFixed(2)}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleDeleteDebt(debt.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-gray-400">No debt entries found</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// DeFi Position Component - New Component
const DefiPosition = () => {
  const [supportedAssets, setSupportedAssets] = useState([]);
  const [selectedCollateral, setSelectedCollateral] = useState({});
  const [selectedDebt, setSelectedDebt] = useState({});
  const [collateralAmount, setCollateralAmount] = useState("");
  const [collateralPurchasePrice, setCollateralPurchasePrice] = useState("");
  const [collateralPurchaseDate, setCollateralPurchaseDate] = useState(
    new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD format
  );
  
  const [debtAmount, setDebtAmount] = useState("");
  const [debtPurchasePrice, setDebtPurchasePrice] = useState("");
  const [debtPurchaseDate, setDebtPurchaseDate] = useState(
    new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD format
  );
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [savedPositions, setSavedPositions] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPositionId, setEditingPositionId] = useState(null);
  const [editedPositionName, setEditedPositionName] = useState("");
  
  // Search states for collateral and debt
  const [collateralSearchTerm, setCollateralSearchTerm] = useState("");
  const [debtSearchTerm, setDebtSearchTerm] = useState("");
  const [collateralCoins, setCollateralCoins] = useState([]);
  const [debtCoins, setDebtCoins] = useState([]);
  
  // Master list of all coins that have been searched
  const [allSearchedCoins, setAllSearchedCoins] = useState({});
  
  const token = localStorage.getItem("token");

  // Search coins from CoinGecko for collateral
  const searchCollateralCoins = async (query) => {
    if (!query) {
      setCollateralCoins([]);
      return;
    }
    
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/coins?query=${query}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setCollateralCoins(response.data);
      
      // Add coins to the master list
      const updatedCoins = {...allSearchedCoins};
      response.data.forEach(coin => {
        updatedCoins[coin.id] = coin;
      });
      setAllSearchedCoins(updatedCoins);
      
      console.log(`Found ${response.data.length} coins matching "${query}" for collateral`);
    } catch (error) {
      console.error("Error searching collateral coins:", error);
    }
  };

  // Search coins from CoinGecko for debt
  const searchDebtCoins = async (query) => {
    if (!query) {
      setDebtCoins([]);
      return;
    }
    
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/coins?query=${query}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setDebtCoins(response.data);
      
      // Add coins to the master list
      const updatedCoins = {...allSearchedCoins};
      response.data.forEach(coin => {
        updatedCoins[coin.id] = coin;
      });
      setAllSearchedCoins(updatedCoins);
      
      console.log(`Found ${response.data.length} coins matching "${query}" for debt`);
    } catch (error) {
      console.error("Error searching debt coins:", error);
    }
  };

  // Handle collateral search input change
  const handleCollateralSearchChange = (e) => {
    const value = e.target.value;
    setCollateralSearchTerm(value);
    searchCollateralCoins(value);
  };

  // Handle debt search input change
  const handleDebtSearchChange = (e) => {
    const value = e.target.value;
    setDebtSearchTerm(value);
    searchDebtCoins(value);
  };

  // Handle selecting a collateral coin
  const handleSelectCollateralCoin = (coin) => {
    console.log("Selected collateral coin:", coin);
    setSelectedCollateral({
      id: coin.id,
      symbol: coin.symbol,
      name: coin.name,
      current_price: coin.current_price,
      image: coin.image
    });
    setCollateralCoins([]);
    setCollateralSearchTerm(coin.name);
    setCollateralPurchasePrice(coin.current_price?.toString() || "");
  };

  // Handle selecting a debt coin
  const handleSelectDebtCoin = (coin) => {
    console.log("Selected debt coin:", coin);
    setSelectedDebt({
      id: coin.id,
      symbol: coin.symbol,
      name: coin.name,
      current_price: coin.current_price,
      image: coin.image
    });
    setDebtCoins([]);
    setDebtSearchTerm(coin.name);
    setDebtPurchasePrice(coin.current_price?.toString() || "");
  };

  // Fetch supported Aave assets (kept for backward compatibility)
  const fetchSupportedAssets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/aave/assets`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setSupportedAssets(response.data);
      setError(null);
    } catch (error) {
      console.error("Error fetching Aave assets:", error);
      setError("Failed to load supported assets");
    } finally {
      setLoading(false);
    }
  };

  // Fetch saved positions
  const fetchSavedPositions = async () => {
    try {
      setLoadingPositions(true);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/aave/positions`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setSavedPositions(response.data);
      
      // Get all asset IDs from positions
      const allAssetIds = new Set();
      response.data.forEach(position => {
        position.collateral.forEach(coll => allAssetIds.add(coll.asset_id));
        position.debt.forEach(debt => allAssetIds.add(debt.asset_id));
      });
      
      // Fetch coin details for all asset IDs and add to master list
      if (allAssetIds.size > 0) {
        try {
          const updatedCoins = {...allSearchedCoins};
          
          // Batch fetch coin details from backend
          for (const assetId of allAssetIds) {
            // Skip if already in our master list
            if (updatedCoins[assetId]) continue;
            
            try {
              const coinResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/coins/${assetId}`, {
                headers: {
                  Authorization: `Bearer ${token}`
                }
              });
              if (coinResponse.data) {
                updatedCoins[assetId] = coinResponse.data;
              }
            } catch (coinError) {
              console.error(`Error fetching details for coin ${assetId}:`, coinError);
            }
          }
          
          setAllSearchedCoins(updatedCoins);
        } catch (coinError) {
          console.error("Error fetching coin details:", coinError);
        }
      }
    } catch (error) {
      console.error("Error fetching saved positions:", error);
    } finally {
      setLoadingPositions(false);
    }
  };

  // Delete a position
  const handleDeletePosition = async (positionId) => {
    if (!window.confirm("Sind Sie sicher, dass Sie diese Position löschen möchten?")) {
      return;
    }
    
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/aave/positions/${positionId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Remove from state
      setSavedPositions(savedPositions.filter(p => p.id !== positionId));
      
      // Show success message
      setDeleteSuccess(true);
      setTimeout(() => setDeleteSuccess(false), 3000);
      
    } catch (error) {
      console.error("Error deleting position:", error);
      setError("Position konnte nicht gelöscht werden");
    }
  };

  // Add collateral to the position
  const addCollateral = () => {
    if (!selectedCollateral.id || !collateralAmount || parseFloat(collateralAmount) <= 0) {
      alert("Please select an asset and enter a valid amount");
      return;
    }

    const newCollateral = {
      asset_id: selectedCollateral.id,
      amount: parseFloat(collateralAmount),
      purchase_price: collateralPurchasePrice ? parseFloat(collateralPurchasePrice) : selectedCollateral.current_price,
      purchase_date: collateralPurchaseDate ? new Date(collateralPurchaseDate).toISOString() : new Date().toISOString()
    };

    // Check if already exists
    const existingIndex = position?.collateral.findIndex(c => c.asset_id === newCollateral.asset_id);
    if (existingIndex >= 0) {
      const updatedCollateral = [...position.collateral];
      updatedCollateral[existingIndex].amount += newCollateral.amount;
      
      setPosition({
        ...position,
        collateral: updatedCollateral
      });
    } else {
      setPosition({
        ...position,
        collateral: [...(position?.collateral || []), newCollateral]
      });
    }

    // Reset form
    setSelectedCollateral({});
    setCollateralAmount("");
    setCollateralPurchasePrice("");
  };

  // Add debt to the position
  const addDebt = () => {
    if (!selectedDebt.id || !debtAmount || parseFloat(debtAmount) <= 0) {
      alert("Please select an asset and enter a valid amount");
      return;
    }

    const newDebt = {
      asset_id: selectedDebt.id,
      amount: parseFloat(debtAmount),
      purchase_price: debtPurchasePrice ? parseFloat(debtPurchasePrice) : selectedDebt.current_price,
      purchase_date: debtPurchaseDate ? new Date(debtPurchaseDate).toISOString() : new Date().toISOString()
    };

    // Check if already exists
    const existingIndex = position?.debt.findIndex(d => d.asset_id === newDebt.asset_id);
    if (existingIndex >= 0) {
      const updatedDebt = [...position.debt];
      updatedDebt[existingIndex].amount += newDebt.amount;
      
      setPosition({
        ...position,
        debt: updatedDebt
      });
    } else {
      setPosition({
        ...position,
        debt: [...(position?.debt || []), newDebt]
      });
    }

    // Reset form
    setSelectedDebt({});
    setDebtAmount("");
    setDebtPurchasePrice("");
  };

  // Remove collateral from the position
  const removeCollateral = (assetId) => {
    if (!position) return;
    
    setPosition({
      ...position,
      collateral: position.collateral.filter(c => c.asset_id !== assetId)
    });
  };

  // Remove debt from the position
  const removeDebt = (assetId) => {
    if (!position) return;
    
    setPosition({
      ...position,
      debt: position.debt.filter(d => d.asset_id !== assetId)
    });
  };

  // Calculate position metrics
  const calculatePosition = async () => {
    if (!position || position.collateral.length === 0 || position.debt.length === 0) {
      alert("Please add at least one collateral and one debt asset");
      return;
    }
    
    try {
      setCalculating(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/aave/calculate-position`,
        {
          collateral: position.collateral,
          debt: position.debt
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      setPosition(response.data);
      setError(null);
    } catch (error) {
      console.error("Error calculating position:", error);
      setError("Failed to calculate position metrics");
    } finally {
      setCalculating(false);
    }
  };

  // Save position to database
  const [positionName, setPositionName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  const handleSavePosition = async () => {
    if (!position || position.health_factor === 0 || !positionName) {
      alert("Please calculate position metrics and enter a position name");
      return;
    }
    
    try {
      setIsSaving(true);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/aave/positions`,
        {
          name: positionName,
          collateral: position.collateral,
          debt: position.debt
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      // Show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
      // Reset form
      setPositionName("");
      
      // Refresh saved positions
      fetchSavedPositions();
      
    } catch (error) {
      console.error("Error saving position:", error);
      setError("Failed to save position");
    } finally {
      setIsSaving(false);
    }
  };
  // Handle editing a position
  const handleEditPosition = (positionId) => {
    // Find the position to edit
    const positionToEdit = savedPositions.find(p => p.id === positionId);
    if (!positionToEdit) return;
    
    // Set to editing mode
    setIsEditing(true);
    setEditingPositionId(positionId);
    setEditedPositionName(positionToEdit.name);
    
    // Load the position data into the form
    setPosition({
      ...positionToEdit,
      collateral: [...positionToEdit.collateral],
      debt: [...positionToEdit.debt]
    });
    
    // Scroll to the top to see the form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Cancel editing
  const cancelEditing = () => {
    setIsEditing(false);
    setEditingPositionId(null);
    setEditedPositionName("");
    
    // Reset position
    setPosition({
      collateral: [],
      debt: [],
      health_factor: 0,
      liquidation_price: {}
    });
  };
  
  // Update an existing position
  const updatePosition = async () => {
    if (!isEditing || !editingPositionId) return;
    
    // Calculate position metrics first
    try {
      setCalculating(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/aave/calculate-position`,
        {
          collateral: position.collateral,
          debt: position.debt
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      // Update position with calculated metrics
      const updatedPosition = {
        ...position,
        name: editedPositionName,
        health_factor: response.data.health_factor,
        liquidation_price: response.data.liquidation_price
      };
      
      // Save the updated position
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/aave/positions/${editingPositionId}`,
        {
          name: editedPositionName,
          collateral: position.collateral,
          debt: position.debt
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      // Exit editing mode
      setIsEditing(false);
      setEditingPositionId(null);
      
      // Refresh positions
      fetchSavedPositions();
      
      // Reset form
      setEditedPositionName("");
      setPosition({
        collateral: [],
        debt: [],
        health_factor: 0,
        liquidation_price: {}
      });
      
      // Show success message
      alert("Position updated successfully");
      
    } catch (error) {
      console.error("Error updating position:", error);
      setError("Failed to update position");
    } finally {
      setCalculating(false);
    }
  };

  // Initialize with empty position
  useEffect(() => {
    fetchSupportedAssets();
    fetchSavedPositions();
    setPosition({
      collateral: [],
      debt: [],
      health_factor: 0,
      liquidation_price: {}
    });
  }, []);

  // Find asset details by id
  const getAssetById = (assetId) => {
    // First look in supported assets
    const supportedAsset = supportedAssets.find(asset => asset.id === assetId);
    if (supportedAsset) {
      return supportedAsset;
    }
    
    // If not found, check in our master list of all searched coins
    if (allSearchedCoins[assetId]) {
      return allSearchedCoins[assetId];
    }
    
    // If still not found, return null
    return null;
  };

  // Calculate total value of collateral or debt
  const calculateTotalValue = (items) => {
    return items.reduce((total, item) => {
      const asset = getAssetById(item.asset_id);
      return total + (item.amount * (asset?.current_price || 0));
    }, 0);
  };

  return (
    <div className="dashboard-container">
      <Sidebar active="defi-position" />
      <div className="dashboard-content">
        <div className="p-6">
          <h1 className="text-2xl font-bold text-white mb-6">DeFi Position</h1>
          
          {error && (
            <div className="bg-red-500 text-white p-3 rounded mb-6">
              {error}
            </div>
          )}

          {deleteSuccess && (
            <div className="bg-green-500 text-white p-3 rounded mb-6">
              Position erfolgreich gelöscht!
            </div>
          )}
          
          {loading ? (
            <div className="bg-gray-800 p-6 rounded-lg shadow">
              <div className="text-gray-400">Aave Assets werden geladen...</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Collateral Section */}
              <div className="bg-gray-800 p-6 rounded-lg shadow">
                <h2 className="text-xl font-bold text-white mb-4">Collateral</h2>
                
                <div className="mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="md:col-span-2">
                      <label className="block text-gray-400 text-sm mb-2">Select Asset</label>
                      <div className="relative">
                        <input
                          type="text"
                          value={collateralSearchTerm}
                          onChange={handleCollateralSearchChange}
                          placeholder="Search for a collateral asset"
                          className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
                        />
                        {collateralCoins.length > 0 && (
                          <div className="absolute z-10 mt-1 w-full bg-gray-700 border border-gray-600 rounded shadow-lg max-h-60 overflow-auto">
                            {collateralCoins.map((coin) => (
                              <div
                                key={coin.id}
                                onClick={() => handleSelectCollateralCoin(coin)}
                                className="flex items-center p-2 hover:bg-gray-600 cursor-pointer"
                              >
                                {coin.image && (
                                  <img src={coin.image} alt={coin.name} className="w-6 h-6 mr-2 rounded-full" />
                                )}
                                <div>
                                  <div className="text-white">{coin.name} ({coin.symbol.toUpperCase()})</div>
                                  <div className="text-xs text-gray-400">${coin.current_price?.toFixed(2) || "N/A"}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {selectedCollateral.id && (
                          <div className="mt-2 flex items-center">
                            {selectedCollateral.image && (
                              <img src={selectedCollateral.image} alt={selectedCollateral.name} className="w-6 h-6 mr-2 rounded-full" />
                            )}
                            <span className="text-white">{selectedCollateral.name} ({selectedCollateral.symbol?.toUpperCase()})</span>
                            <span className="text-sm text-gray-400 ml-2">${selectedCollateral.current_price?.toFixed(2) || "N/A"}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">Amount</label>
                      <input
                        type="number"
                        step="any"
                        value={collateralAmount}
                        onChange={(e) => setCollateralAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">Purchase Price (USD)</label>
                      <input
                        type="number"
                        step="any"
                        value={collateralPurchasePrice}
                        onChange={(e) => setCollateralPurchasePrice(e.target.value)}
                        placeholder="0.00"
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">Purchase Date</label>
                      <input
                        type="date"
                        value={collateralPurchaseDate}
                        onChange={(e) => setCollateralPurchaseDate(e.target.value)}
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                  </div>
                  
                  <button
                    onClick={addCollateral}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <FiPlusCircle className="inline mr-2" />
                    Sicherheit hinzufügen
                  </button>
                </div>
                
                {position?.collateral.length > 0 ? (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Your Collateral</h3>
                    <div className="bg-gray-700 rounded p-4">
                      <div className="space-y-3">
                        {position.collateral.map((coll, index) => {
                          const asset = getAssetById(coll.asset_id);
                          return (
                            <div key={index} className="flex items-center justify-between">
                              <div className="flex items-center">
                                {asset?.image && (
                                  <img
                                    src={asset.image}
                                    alt={asset.name}
                                    className="w-6 h-6 mr-2 rounded-full"
                                  />
                                )}
                                <div>
                                  <div className="text-white">{asset?.symbol || coll.asset_id}</div>
                                  <div className="text-xs text-gray-400">
                                    {coll.amount.toFixed(6)} (${(coll.amount * (asset?.current_price || 0)).toFixed(2)})
                                  </div>
                                  {coll.purchase_price && (
                                    <div className="text-xs text-indigo-400">
                                      Purchased at ${coll.purchase_price.toFixed(2)} on {new Date(coll.purchase_date).toLocaleDateString()}
                                      {asset?.current_price && (
                                        <span className={asset.current_price > coll.purchase_price ? " text-green-400" : asset.current_price < coll.purchase_price ? " text-red-400" : ""}>
                                          {" "}
                                          ({asset.current_price > coll.purchase_price ? "+" : ""}
                                          {(((asset.current_price - coll.purchase_price) / coll.purchase_price) * 100).toFixed(2)}%)
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => removeCollateral(coll.asset_id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <FiMinusCircle />
                              </button>
                            </div>
                          );
                        })}
                        <div className="pt-2 border-t border-gray-600">
                          <div className="flex justify-between text-white">
                            <span>Total Value:</span>
                            <span>${calculateTotalValue(position.collateral).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-400">No collateral added yet</div>
                )}
              </div>
              
              {/* Debt Section */}
              <div className="bg-gray-800 p-6 rounded-lg shadow">
                <h2 className="text-xl font-bold text-white mb-4">Schulden</h2>
                
                <div className="mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="md:col-span-2">
                      <label className="block text-gray-400 text-sm mb-2">Asset auswählen</label>
                      <div className="relative">
                        <input
                          type="text"
                          value={debtSearchTerm}
                          onChange={handleDebtSearchChange}
                          placeholder="Nach einem Schulden-Asset suchen"
                          className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
                        />
                        {debtCoins.length > 0 && (
                          <div className="absolute z-10 mt-1 w-full bg-gray-700 border border-gray-600 rounded shadow-lg max-h-60 overflow-auto">
                            {debtCoins.map((coin) => (
                              <div
                                key={coin.id}
                                onClick={() => handleSelectDebtCoin(coin)}
                                className="flex items-center p-2 hover:bg-gray-600 cursor-pointer"
                              >
                                {coin.image && (
                                  <img src={coin.image} alt={coin.name} className="w-6 h-6 mr-2 rounded-full" />
                                )}
                                <div>
                                  <div className="text-white">{coin.name} ({coin.symbol.toUpperCase()})</div>
                                  <div className="text-xs text-gray-400">${coin.current_price?.toFixed(2) || "N/A"}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {selectedDebt.id && (
                          <div className="mt-2 flex items-center">
                            {selectedDebt.image && (
                              <img src={selectedDebt.image} alt={selectedDebt.name} className="w-6 h-6 mr-2 rounded-full" />
                            )}
                            <span className="text-white">{selectedDebt.name} ({selectedDebt.symbol?.toUpperCase()})</span>
                            <span className="text-sm text-gray-400 ml-2">${selectedDebt.current_price?.toFixed(2) || "N/A"}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">Amount</label>
                      <input
                        type="number"
                        step="any"
                        value={debtAmount}
                        onChange={(e) => setDebtAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">Purchase Price (USD)</label>
                      <input
                        type="number"
                        step="any"
                        value={debtPurchasePrice}
                        onChange={(e) => setDebtPurchasePrice(e.target.value)}
                        placeholder="0.00"
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">Purchase Date</label>
                      <input
                        type="date"
                        value={debtPurchaseDate}
                        onChange={(e) => setDebtPurchaseDate(e.target.value)}
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                  </div>
                  
                  <button
                    onClick={addDebt}
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <FiPlusCircle className="inline mr-2" />
                    Schulden hinzufügen
                  </button>
                </div>
                
                {position?.debt.length > 0 ? (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Your Debt</h3>
                    <div className="bg-gray-700 rounded p-4">
                      <div className="space-y-3">
                        {position.debt.map((debt, index) => {
                          const asset = getAssetById(debt.asset_id);
                          return (
                            <div key={index} className="flex items-center justify-between">
                              <div className="flex items-center">
                                {asset?.image && (
                                  <img
                                    src={asset.image}
                                    alt={asset.name}
                                    className="w-6 h-6 mr-2 rounded-full"
                                  />
                                )}
                                <div>
                                  <div className="text-white">{asset?.symbol || debt.asset_id}</div>
                                  <div className="text-xs text-gray-400">
                                    {debt.amount.toFixed(6)} (${(debt.amount * (asset?.current_price || 0)).toFixed(2)})
                                  </div>
                                  {debt.purchase_price && (
                                    <div className="text-xs text-indigo-400">
                                      Erworben für ${debt.purchase_price.toFixed(2)} am {new Date(debt.purchase_date).toLocaleDateString()}
                                      {asset?.current_price && (
                                        <span className={debt.purchase_price > asset.current_price ? " text-green-400" : debt.purchase_price < asset.current_price ? " text-red-400" : ""}>
                                          {" "}
                                          ({debt.purchase_price > asset.current_price ? "+" : ""}
                                          {(((asset.current_price - debt.purchase_price) / debt.purchase_price) * -100).toFixed(2)}%)
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => removeDebt(debt.asset_id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <FiMinusCircle />
                              </button>
                            </div>
                          );
                        })}
                        <div className="pt-2 border-t border-gray-600">
                          <div className="flex justify-between text-white">
                            <span>Total Value:</span>
                            <span>${calculateTotalValue(position.debt).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-400">No debt added yet</div>
                )}
              </div>
            </div>
          )}
          
          <div className="flex justify-center mb-6">
            <button
              onClick={calculatePosition}
              disabled={calculating || loading || !position || position.collateral.length === 0 || position.debt.length === 0}
              className="bg-yellow-600 text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50"
            >
              {calculating ? "Wird berechnet..." : "Positionskennzahlen berechnen"}
            </button>
          </div>
          
          {position && position.health_factor !== 0 && (
            <div className="bg-gray-800 p-6 rounded-lg shadow">
              <h2 className="text-xl font-bold text-white mb-4">Position Metrics</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-white mb-2">Gesundheitsfaktor</h3>
                    <div className={`text-4xl font-bold ${
                      position.health_factor >= 1.5 ? "text-green-500" : 
                      position.health_factor >= 1.0 ? "text-yellow-500" : 
                      "text-red-500"
                    }`}>
                      {position.health_factor === Infinity ? "∞" : position.health_factor.toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-400 mt-1">
                      {position.health_factor >= 1.5 ? "Safe position" : 
                       position.health_factor >= 1.0 ? "Caution: nearing liquidation threshold" : 
                       "Danger: below liquidation threshold"}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Position Summary</h3>
                    <div className="bg-gray-700 rounded p-4">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Total Collateral Value:</span>
                          <span className="text-white">${calculateTotalValue(position.collateral).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Total Debt Value:</span>
                          <span className="text-white">${calculateTotalValue(position.debt).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between border-t border-gray-600 pt-2 mt-2">
                          <span className="text-gray-400">Collateralization Ratio:</span>
                          <span className="text-white">
                            {calculateTotalValue(position.debt) > 0 
                              ? ((calculateTotalValue(position.collateral) / calculateTotalValue(position.debt)) * 100).toFixed(2) 
                              : "∞"}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Liquidation Prices</h3>
                  {Object.keys(position.liquidation_price || {}).length > 0 ? (
                    <div className="bg-gray-700 rounded p-4">
                      <div className="space-y-3">
                        {Object.entries(position.liquidation_price).map(([assetId, price]) => {
                          const asset = getAssetById(assetId);
                          const currentPrice = asset?.current_price || 0;
                          const percentageToLiquidation = ((currentPrice - price) / currentPrice) * 100;
                          
                          return (
                            <div key={assetId} className="pb-3 border-b border-gray-600 last:border-0">
                              <div className="flex items-center mb-1">
                                {asset?.image && (
                                  <img
                                    src={asset.image}
                                    alt={asset.name}
                                    className="w-5 h-5 mr-2 rounded-full"
                                  />
                                )}
                                <span className="text-white font-medium">{asset?.symbol || assetId}</span>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                <div>
                                  <span className="text-gray-400">Current Price:</span>
                                  <span className="text-white ml-2">${currentPrice.toFixed(2)}</span>
                                </div>
                                <div>
                                  <span className="text-gray-400">Liquidation Price:</span>
                                  <span className="text-white ml-2">${price.toFixed(2)}</span>
                                </div>
                              </div>
                              
                              <div className="mt-2">
                                <div className="text-xs text-gray-400 mb-1">Distance to Liquidation</div>
                                <div className="w-full bg-gray-600 rounded-full h-2">
                                  <div
                                    className={`h-2 rounded-full ${
                                      percentageToLiquidation >= 50 ? "bg-green-500" :
                                      percentageToLiquidation >= 20 ? "bg-yellow-500" :
                                      "bg-red-500"
                                    }`}
                                    style={{ width: `${Math.max(0, Math.min(100, percentageToLiquidation))}%` }}
                                  ></div>
                                </div>
                                <div className="text-xs mt-1 text-right">
                                  <span className={
                                    percentageToLiquidation >= 50 ? "text-green-500" :
                                    percentageToLiquidation >= 20 ? "text-yellow-500" :
                                    "text-red-500"
                                  }>
                                    {percentageToLiquidation.toFixed(2)}% buffer
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-400">
                      {position.collateral.length === 0 || position.debt.length === 0
                        ? "Fügen Sie sowohl Sicherheiten als auch Schulden hinzu, um Liquidationspreise zu sehen"
                        : "Keine Liquidationspreise verfügbar"}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="mt-6 border-t border-gray-700 pt-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  {isEditing ? "Position aktualisieren" : "Position speichern"}
                </h3>
                <div className="flex items-end gap-4">
                  <div className="flex-grow">
                    <label className="block text-gray-400 text-sm mb-2">Positionsname</label>
                    <input
                      type="text"
                      value={isEditing ? editedPositionName : positionName}
                      onChange={(e) => isEditing ? setEditedPositionName(e.target.value) : setPositionName(e.target.value)}
                      placeholder="My Aave Position"
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
                    />
                  </div>
                  {isEditing ? (
                    <>
                      <button
                        onClick={updatePosition}
                        disabled={isSaving || !editedPositionName}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        {isSaving ? "Updating..." : "Update Position"}
                      </button>
                      <button
                        onClick={cancelEditing}
                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                      >
                        Abbrechen
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleSavePosition}
                      disabled={isSaving || !positionName}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                    >
                      {isSaving ? "Saving..." : "Create Position"}
                    </button>
                  )}
                </div>
                {saveSuccess && !isEditing && (
                  <div className="mt-2 text-green-500">
                    Position erfolgreich gespeichert! Sie wird in Ihrem Dashboard angezeigt.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Saved Positions Section */}
          <div className="mt-8">
            <h2 className="text-2xl font-bold text-white mb-4">Ihre gespeicherten Positionen</h2>
            
            {loadingPositions ? (
              <div className="bg-gray-800 p-6 rounded-lg shadow">
                <div className="text-gray-400">Gespeicherte Positionen werden geladen...</div>
              </div>
            ) : savedPositions.length > 0 ? (
              <div className="bg-gray-800 p-6 rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Positionsname</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Gesundheitsfaktor</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Sicherheit</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Schulden</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Erstellt am</th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {savedPositions.map((savedPosition) => {
                        // Calculate totals
                        const totalCollateral = savedPosition.collateral.reduce((total, coll) => {
                          const asset = getAssetById(coll.asset_id);
                          return total + (coll.amount * (asset?.current_price || 0));
                        }, 0);
                        
                        const totalDebt = savedPosition.debt.reduce((total, debt) => {
                          const asset = getAssetById(debt.asset_id);
                          return total + (debt.amount * (asset?.current_price || 0));
                        }, 0);

                        return (
                          <tr key={savedPosition.id}>
                            <td className="px-3 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-white">{savedPosition.name}</div>
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap">
                              <div className={`text-sm font-medium ${
                                savedPosition.health_factor >= 1.5 ? "text-green-500" : 
                                savedPosition.health_factor >= 1.0 ? "text-yellow-500" : 
                                "text-red-500"
                              }`}>
                                {savedPosition.health_factor.toFixed(2)}
                              </div>
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap">
                              <div className="text-sm text-white">
                                ${totalCollateral.toFixed(2)}
                                <div className="text-xs text-gray-400 mt-1">
                                  {savedPosition.collateral.map((coll) => {
                                    const asset = getAssetById(coll.asset_id);
                                    return asset ? (
                                      <div key={coll.asset_id} className="flex items-center mb-1">
                                        <img 
                                          src={asset.image} 
                                          alt={asset.symbol} 
                                          className="w-4 h-4 mr-1 rounded-full"
                                        />
                                        <span>{coll.amount.toFixed(4)} {asset.symbol.toUpperCase()}</span>
                                      </div>
                                    ) : null;
                                  })}
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap">
                              <div className="text-sm text-white">
                                ${totalDebt.toFixed(2)}
                                <div className="text-xs text-gray-400 mt-1">
                                  {savedPosition.debt.map((debt) => {
                                    const asset = getAssetById(debt.asset_id);
                                    return asset ? (
                                      <div key={debt.asset_id} className="flex items-center mb-1">
                                        <img 
                                          src={asset.image} 
                                          alt={asset.symbol} 
                                          className="w-4 h-4 mr-1 rounded-full"
                                        />
                                        <span>{debt.amount.toFixed(4)} {asset.symbol.toUpperCase()}</span>
                                      </div>
                                    ) : null;
                                  })}
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-400">
                                {new Date(savedPosition.created_at).toLocaleDateString()}
                              </div>
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-right">
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => handleEditPosition(savedPosition.id)}
                                  className="text-blue-400 hover:text-blue-300 transition-colors"
                                  title="Position bearbeiten"
                                >
                                  <FiEdit size={18} />
                                </button>
                                <button
                                  onClick={() => handleDeletePosition(savedPosition.id)}
                                  className="text-red-400 hover:text-red-300 transition-colors"
                                  title="Delete position"
                                >
                                  <FiTrash2 size={18} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-gray-800 p-8 rounded-lg shadow text-center">
                <div className="text-gray-400 mb-4">Sie haben noch keine gespeicherten Positionen.</div>
                <div className="text-sm text-gray-500">
                  Erstellen Sie oben eine Position und speichern Sie sie, um sie hier zu sehen.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Profile Component
const Profile = () => {
  const { user, token, logout } = useAuth();
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setEmail(user.email);
    }
  }, [user]);

  const handleEmailChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setError("");

    if (!currentPassword) {
      setError("Current password is required to update email");
      setLoading(false);
      return;
    }

    try {
      // Create form data
      const formData = new FormData();
      formData.append("email", user.email); // Current email
      formData.append("new_email", email); // New email
      formData.append("current_password", currentPassword);
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/update`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );

      if (response.status === 200) {
        setMessage("Email updated successfully! Please log in again with your new email.");
        // Clear password field
        setCurrentPassword("");
        
        // Force logout after a short delay to refresh user data
        setTimeout(() => {
          logout();
        }, 3000);
      }
    } catch (err) {
      console.error("Email update error:", err.response?.data || err.message);
      setError("Failed to update email. " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setError("");

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match");
      setLoading(false);
      return;
    }

    try {
      // Create form data
      const formData = new FormData();
      formData.append("email", email);
      formData.append("current_password", currentPassword);
      formData.append("new_password", newPassword);
      formData.append("confirm_password", confirmPassword);
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/update`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );

      if (response.status === 200) {
        setMessage("Password updated successfully!");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      }
    } catch (err) {
      console.error("Password update error:", err.response?.data || err.message);
      setError("Failed to update password. " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar active="profile" />
      <div className="dashboard-content">
        <h1 className="text-2xl font-bold mb-6">Profil</h1>
        
        {message && (
          <div className="bg-green-900 border-l-4 border-green-500 text-green-300 p-4 mb-4">
            {message}
          </div>
        )}
        
        {error && (
          <div className="bg-red-900 border-l-4 border-red-500 text-red-300 p-4 mb-4">
            {error}
          </div>
        )}
        
        <div className="bg-gray-900 rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-white mb-4">Email ändern</h2>
          <form onSubmit={handleEmailChange}>
            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-2 border border-gray-700 bg-gray-800 text-white rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Aktuelles Passwort</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full p-2 border border-gray-700 bg-gray-800 text-white rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
            <button
              type="submit"
              className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700"
              disabled={loading}
            >
              {loading ? "Updating..." : "Email aktualisieren"}
            </button>
          </form>
        </div>
        
        <div className="bg-gray-900 rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Passwort ändern</h2>
          <form onSubmit={handlePasswordChange}>
            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Aktuelles Passwort</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full p-2 border border-gray-700 bg-gray-800 text-white rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Neues Passwort</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full p-2 border border-gray-700 bg-gray-800 text-white rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Neues Passwort bestätigen</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full p-2 border border-gray-700 bg-gray-800 text-white rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
            <button
              type="submit"
              className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700"
              disabled={loading}
            >
              {loading ? "Updating..." : "Passwort aktualisieren"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Indicators Component
const Indicators = () => {
  const [selectedIndicator, setSelectedIndicator] = useState("pi-cycle-top");
  const [indicatorData, setIndicatorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("token");
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const resetZoom = () => {
    if (chartInstance.current) {
      chartInstance.current.resetZoom();
    }
  };

  const indicators = [
    { id: "pi-cycle-top", name: "Pi-Cycle-Top-Indikator" },
    { id: "golden-ratio-multiplier", name: "Golden Ratio Multiplier" },
    { id: "macd-weekly", name: "MACD Weekly" },
    { id: "bitcoin-power-law", name: "Bitcoin Power Law" },
    { id: "eth-btc-rotationline", name: "ETH/BTC Rotationline" }
    // Weitere Indikatoren können hier hinzugefügt werden
  ];

  useEffect(() => {
    const loadIndicator = async () => {
      try {
        setError(null);
        setLoading(true);
        setIndicatorData(null); // Bestehende Daten zurücksetzen, um doppelte Anzeige zu vermeiden
        
        const data = await fetchIndicatorData(selectedIndicator);
        if (data) {
          setIndicatorData(data);
          console.log(`${selectedIndicator} data loaded successfully:`, data);
        } else {
          setError("Fehler beim Laden des Indikators. Bitte versuchen Sie es später erneut.");
        }
      } catch (error) {
        console.error(`Error loading ${selectedIndicator}:`, error);
        setError("Fehler beim Laden des Indikators. Bitte versuchen Sie es später erneut.");
      } finally {
        setLoading(false);
      }
    };
    
    loadIndicator();
  }, [selectedIndicator]);

  const fetchIndicatorData = async (indicatorId) => {
    try {
      console.log(`Fetching ${indicatorId} indicator data...`);
      
      // Definieren der möglichen Quellen für die Indikatordaten mit Fallback-Priorität
      const dataSources = [
        // 1. Primärquelle: Authentifizierte API
        async () => {
          const token = localStorage.getItem("token");
          if (!token) return null;
          
          console.log(`Attempting authenticated request to ${process.env.REACT_APP_BACKEND_URL}/api/indicators/${indicatorId}`);
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/indicators/${indicatorId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 5000 // 5 Sekunden Timeout
            }
          );
          return response.data;
        },
        
        // 2. Alternative: Nicht-authentifizierte Test-API
        async () => {
          console.log(`Attempting unauthenticated request to ${process.env.REACT_APP_BACKEND_URL}/api/indicators/${indicatorId}-test`);
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/indicators/${indicatorId}-test`,
            { timeout: 5000 }
          );
          return response.data;
        },
        
        // 3. Notfall-Fallback: Statische Daten aus dem public-Ordner
        async () => {
          console.log(`Attempting to load static data from /data/${indicatorId.replace('-', '_')}.json`);
          const response = await axios.get(`/data/${indicatorId.replace('-', '_')}.json`);
          return response.data;
        }
      ];
      
      // Versuche nacheinander alle Datenquellen, bis eine erfolgreich ist
      let lastError = null;
      for (const source of dataSources) {
        try {
          const data = await source();
          if (data) {
            console.log(`Successfully loaded ${indicatorId} data:`, data);
            // Debug MACD-Struktur
            if (indicatorId === "macd-weekly") {
              console.log("MACD Weekly data.data keys:", Object.keys(data.data));
              // Umbenenne die Schlüssel von Backend-Format (macd, signal) zu Frontend-Format (macd_line, signal_line)
              if (data.data.macd && !data.data.macd_line) {
                data.data.macd_line = data.data.macd;
              }
              if (data.data.signal && !data.data.signal_line) {
                data.data.signal_line = data.data.signal;
              }
              // Überprüfe, ob die Daten jetzt in der erwarteten Struktur sind
              console.log("Umbenannte MACD Weekly data.data keys:", Object.keys(data.data));
            }
            
            // Datenformat transformieren, um es mit der Chart-Komponente kompatibel zu machen
            let transformedData;
            
            // Prüfen, ob die Daten im Format der Backend-API oder im Fallback-Format vorliegen
            if (data.data && (data.data.btc_price || data.data.sma_111_x2 || data.data.sma_350)) {
              console.log(`Transforming data from fallback format...`);
              
              if (indicatorId === "pi-cycle-top") {
                // Extrahieren der Datenpunkte aus den Arrays
                const btcPrice = data.data.btc_price || [];
                const sma111x2 = data.data.sma_111_x2 || [];
                const sma350 = data.data.sma_350 || [];
                
                // Erstellen der transformierten Datenstruktur
                transformedData = {
                  dates: btcPrice.map(item => item[0]),
                  prices: btcPrice.map(item => item[1]),
                  sma_111: sma111x2.map(item => item[1] / 2), // Teilen durch 2, da wir sma_111_x2 haben
                  sma_350x2: sma350.map(item => item[1] * 2)  // Multiplizieren mit 2 für sma_350x2
                };
              } else if (indicatorId === "golden-ratio-multiplier") {
                // Extrahieren der Datenpunkte aus den Arrays
                const btcPrice = data.data.btc_price || [];
                const sma350 = data.data.sma_350 || [];
                const sma350x16 = data.data.sma_350_x_1_6 || data.data.sma_350_x_1_6 || [];
                const sma350x2 = data.data.sma_350_x_2 || [];
                const sma350x3 = data.data.sma_350_x_3 || [];
                
                // Erstellen der transformierten Datenstruktur
                transformedData = {
                  dates: btcPrice.map(item => item[0]),
                  prices: btcPrice.map(item => item[1]),
                  sma350: sma350.map(item => item[1]),
                  sma350x16: sma350x16.map(item => item[1]),
                  sma350x2: sma350x2.map(item => item[1]),
                  sma350x3: sma350x3.map(item => item[1])
                };
              } else if (indicatorId === "macd-weekly") {
                // Extrahieren der Datenpunkte aus den Arrays für MACD Weekly
                const btcPrice = data.data.btc_price || [];
                // MACD hat unterschiedliche Schlüssel im Backend (macd, signal, histogram)
                // und im Frontend (macd_line, signal_line, histogram)
                const macdLine = data.data.macd_line || data.data.macd || [];
                const signalLine = data.data.signal_line || data.data.signal || [];
                const histogram = data.data.histogram || [];
                
                // Aktuelle Werte für die Detailanzeige extrahieren (letzter Datenpunkt)
                const lastIndex = btcPrice.length - 1;
                const currentBtcPrice = lastIndex >= 0 ? btcPrice[lastIndex][1] : null;
                const currentMacdLine = lastIndex >= 0 ? macdLine[lastIndex][1] : null;
                const currentSignalLine = lastIndex >= 0 ? signalLine[lastIndex][1] : null;
                const currentHistogram = lastIndex >= 0 ? histogram[lastIndex][1] : null;
                
                // Signal bestimmen (Kaufsignal wenn MACD über Signal-Linie, Verkaufssignal wenn darunter)
                let signal = "neutral";
                if (currentMacdLine !== null && currentSignalLine !== null) {
                  if (currentMacdLine > currentSignalLine) {
                    signal = "buy";
                  } else if (currentMacdLine < currentSignalLine) {
                    signal = "sell";
                  }
                }
                
                // Erstellen der transformierten Datenstruktur für MACD Weekly
                transformedData = {
                  dates: btcPrice.map(item => item[0]),
                  prices: btcPrice.map(item => item[1]),
                  macdLine: macdLine.map(item => item[1]),
                  signalLine: signalLine.map(item => item[1]),
                  histogram: histogram.map(item => item[1]),
                  // Aktuelle Werte für die Detailanzeige
                  current: {
                    btc_price: currentBtcPrice,
                    macd_line: currentMacdLine,
                    signal_line: currentSignalLine,
                    histogram: currentHistogram,
                    signal: signal
                  }
                };
              } else if (indicatorId === "bitcoin-power-law") {
                // Extrahieren der Datenpunkte aus den Arrays für Bitcoin Power Law
                const btcPrice = data.data.btc_price || [];
                const powerLawSupport = data.data.power_law_support || [];
                const powerLawRobust = data.data.power_law_robust || [];
                const powerLawCentre = data.data.power_law_centre || [];
                const powerLawResistance = data.data.power_law_resistance || [];
                const powerLawPosition = data.data.power_law_position || [];
                
                // Aktuelle Werte für die Detailanzeige extrahieren (letzter Datenpunkt)
                const lastIndex = btcPrice.length - 1;
                const currentBtcPrice = lastIndex >= 0 ? btcPrice[lastIndex][1] : null;
                const currentSupport = lastIndex >= 0 ? powerLawSupport[lastIndex][1] : null;
                const currentRobust = lastIndex >= 0 ? powerLawRobust[lastIndex][1] : null;
                const currentCentre = lastIndex >= 0 ? powerLawCentre[lastIndex][1] : null;
                const currentResistance = lastIndex >= 0 ? powerLawResistance[lastIndex][1] : null;
                const currentPosition = lastIndex >= 0 ? powerLawPosition[lastIndex][1] : null;
                
                // Erstellen der transformierten Datenstruktur für Bitcoin Power Law
                transformedData = {
                  dates: btcPrice.map(item => item[0]),
                  prices: btcPrice.map(item => item[1]),
                  powerLawSupport: powerLawSupport.map(item => item[1]),
                  powerLawRobust: powerLawRobust.map(item => item[1]),
                  powerLawCentre: powerLawCentre.map(item => item[1]),
                  powerLawResistance: powerLawResistance.map(item => item[1]),
                  powerLawPosition: powerLawPosition.map(item => item[1]),
                  // Aktuelle Werte für die Detailanzeige
                  current: {
                    btc_price: currentBtcPrice,
                    support: currentSupport,
                    robust: currentRobust,
                    centre: currentCentre,
                    resistance: currentResistance,
                    position: currentPosition
                  }
                };
              }
            } else {
              // Daten sind bereits im erwarteten Format oder in einem unbekannten Format
              transformedData = data;
            }
            
            console.log(`Transformed data:`, transformedData);
            return transformedData;
          }
        } catch (error) {
          console.error(`Data source failed:`, error.message);
          lastError = error;
          // Weiter zur nächsten Quelle
        }
      }
      
      // Wenn alle Quellen fehlschlagen, wirf den letzten Fehler
      throw lastError || new Error("All data sources failed");
    } catch (error) {
      console.error(`Error fetching ${indicatorId} indicator data:`, error);
      
      // Letzte Notlösung: Hardcodierte Fallback-Daten im Code
      if (indicatorId === "pi-cycle-top") {
        console.log("Using hardcoded Pi-Cycle-Top fallback data");
        return {
          dates: ["2021-01-01", "2021-03-01", "2021-04-01"],
          prices: [29000, 45000, 59000],
          sma_111: [9000, 17000, 21000],
          sma_350x2: [20000, 36000, 44000]
        };
      } else if (indicatorId === "golden-ratio-multiplier") {
        console.log("Using hardcoded Golden Ratio Multiplier fallback data");
        return {
          dates: ["2021-01-01", "2021-03-01", "2021-04-01"],
          prices: [29000, 45000, 59000],
          sma350: [10000, 18000, 22000],
          sma350x16: [16000, 28800, 35200],
          sma350x2: [20000, 36000, 44000],
          sma350x3: [30000, 54000, 66000]
        };
      } else if (indicatorId === "macd-weekly") {
        console.log("Using hardcoded MACD Weekly fallback data");
        return {
          dates: ["2021-01-01", "2021-03-01", "2021-04-01"],
          prices: [29000, 45000, 59000],
          macdLine: [500, 1200, 800],
          signalLine: [400, 900, 900],
          histogram: [100, 300, -100]
        };
      }
      
      return null;
    }
  };

  useEffect(() => {
    if (indicatorData && chartRef.current) {
      // Bestehende Chart-Instanz zerstören, falls vorhanden
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      // Prüfen, ob alle benötigten Daten vorhanden sind
      if (selectedIndicator === "eth-btc-rotationline") {
        // Für ETH/BTC Rotationline sind dates und prices erforderlich (wie bei anderen Indikatoren)
        if (!indicatorData.dates || !indicatorData.prices) {
          console.error("Missing dates/prices data for ETH/BTC Rotationline indicator:", indicatorData);
          return;
        }
      } else if (!indicatorData.dates || !indicatorData.prices) {
        console.error("Missing basic required chart data:", indicatorData);
        return;
      }
      
      // Zusätzliche Validierung basierend auf dem ausgewählten Indikator
      if (selectedIndicator === "pi-cycle-top" && (!indicatorData.sma_111 || !indicatorData.sma_350x2)) {
        console.error("Missing required data for Pi-Cycle-Top indicator:", indicatorData);
        return;
      } else if (selectedIndicator === "golden-ratio-multiplier" && !indicatorData.sma350) {
        console.error("Missing required data for Golden Ratio Multiplier indicator:", indicatorData);
        return;
      } else if (selectedIndicator === "macd-weekly" && (!indicatorData.macdLine || !indicatorData.signalLine)) {
        console.error("Missing required data for MACD Weekly indicator:", indicatorData);
        return;
      }

      const ctx = chartRef.current.getContext("2d");
      
      // Indikatoren-spezifische Datenvorbereitung
      let data = { datasets: [] };
      
      if (selectedIndicator === "pi-cycle-top") {
        // Pi-Cycle-Top-Indikator
        data = {
          labels: indicatorData.dates,
          datasets: [
            {
              label: "Bitcoin-Preis",
              data: indicatorData.dates.map((date, index) => ({
                x: new Date(date),
                y: indicatorData.prices[index]
              })),
              borderColor: "#ffffff",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              borderWidth: 1,
              pointRadius: 0,
              yAxisID: "y",
            },
            {
              label: "SMA-111",
              data: indicatorData.dates.map((date, index) => ({
                x: new Date(date),
                y: indicatorData.sma_111[index]
              })),
              borderColor: "#f59e0b",
              backgroundColor: "rgba(245, 158, 11, 0.1)",
              borderWidth: 2,
              pointRadius: 0,
              yAxisID: "y",
            },
            {
              label: "2 × SMA-350",
              data: indicatorData.dates.map((date, index) => ({
                x: new Date(date),
                y: indicatorData.sma_350x2[index]
              })),
              borderColor: "#10b981",
              backgroundColor: "rgba(16, 185, 129, 0.1)",
              borderWidth: 2,
              pointRadius: 0,
              yAxisID: "y",
            }
          ]
        };
        
        // Pi-Cycle-Top-Punkte markieren
        if (indicatorData.tops && indicatorData.tops.length > 0) {
          const topPoints = indicatorData.tops.map(date => {
            const index = indicatorData.dates.indexOf(date);
            return index > -1 ? {
              x: new Date(date),
              y: indicatorData.prices[index]
            } : null;
          }).filter(point => point !== null);

          if (topPoints.length > 0) {
            data.datasets.push({
              label: "Pi-Cycle-Tops",
              data: topPoints,
              backgroundColor: "#ef4444",
              borderColor: "#ef4444",
              pointRadius: 6,
              pointHoverRadius: 8,
              showLine: false,
              yAxisID: "y",
            });
          }
        }
      } else if (selectedIndicator === "golden-ratio-multiplier") {
        // Daten für Golden Ratio Multiplier
        data = {
          datasets: [
            {
              label: "Bitcoin-Preis",
              data: indicatorData.dates.map((date, index) => ({
                x: new Date(date),
                y: indicatorData.prices[index]
              })),
              borderColor: "#ffffff",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              borderWidth: 1,
              pointRadius: 0,
              yAxisID: "y",
            }
          ]
        };
        
        // 350-Tage-SMA
        data.datasets.push({
          label: "SMA-350",
          data: indicatorData.dates.map((date, index) => ({
            x: new Date(date),
            y: indicatorData.sma350[index]
          })),
          borderColor: "#f5f5f5", // Weiß/Silber
          backgroundColor: "rgba(245, 245, 245, 0.1)",
          borderWidth: 2,
          pointRadius: 0,
          yAxisID: "y",
        });
        
        // Multiplikatoren hinzufügen
        if (indicatorData.multipliers) {
          // Nur 1.6 und 2 als Multiplikatoren hinzufügen
          const allowedMultipliers = ["1.6", "2"];
          
          // Farben für die Multiplikatoren definieren
          const multiplierColors = {
            "1": "#f5f5f5", // Weiß/Silber
            "1_6": "#f59e0b", // Orange
            "2": "#10b981", // Grün
            "3": "#3b82f6", // Blau
            "5": "#8b5cf6", // Lila
            "8": "#ec4899", // Pink
            "13": "#ef4444", // Rot
            "21": "#7c3aed" // Dunkel-Lila
          };
          
          indicatorData.multipliers.forEach(multiplier => {
            const multiplierStr = String(multiplier);
            if (allowedMultipliers.includes(multiplierStr)) {
              const key = multiplierStr.replace(".", "_");
              const color = multiplierColors[key] || "#ffffff";
              
              data.datasets.push({
                label: `${multiplier}× SMA-350`,
                data: indicatorData.dates.map((date, index) => ({
                  x: new Date(date),
                  y: indicatorData[`sma350x${key}`][index]
                })),
                borderColor: color,
                backgroundColor: `${color}33`, // Transparente Version der Farbe
                borderWidth: 2,
                pointRadius: 0,
                yAxisID: "y",
              });
            }
          });
        }
      } else if (selectedIndicator === "macd-weekly") {
        // Daten für MACD Weekly
        data = {
          datasets: [
            {
              label: "Bitcoin-Preis",
              data: indicatorData.dates.map((date, index) => ({
                x: new Date(date),
                y: indicatorData.prices[index]
              })),
              borderColor: "#ffffff",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              borderWidth: 1,
              pointRadius: 0,
              yAxisID: "y",
              order: 3 // Niedrigere Priorität für die Anzeige (im Hintergrund)
            },
            {
              label: "MACD-Linie",
              data: indicatorData.dates.map((date, index) => ({
                x: new Date(date),
                y: indicatorData.macdLine[index]
              })),
              borderColor: "#3b82f6", // Blau
              backgroundColor: "rgba(59, 130, 246, 0.1)",
              borderWidth: 2.5, // Angepasste Linienbreite
              pointRadius: 0,
              yAxisID: "y1",
              order: 1, // Höhere Priorität für die Anzeige
              tension: 0.3 // Glättung der Linie
            },
            {
              label: "Signal-Linie",
              data: indicatorData.dates.map((date, index) => ({
                x: new Date(date),
                y: indicatorData.signalLine[index]
              })),
              borderColor: "#ef4444", // Rot
              backgroundColor: "rgba(239, 68, 68, 0.1)",
              borderWidth: 2.5, // Angepasste Linienbreite
              pointRadius: 0,
              yAxisID: "y1",
              order: 0, // Höhere Priorität für die Anzeige
              tension: 0.3 // Glättung der Linie
            }
          ]
        };
        
        // Histogram als Balken hinzufügen
        const histogramData = indicatorData.dates.map((date, index) => ({
          x: new Date(date),
          y: indicatorData.histogram[index]
        }));
        
        // Separate positive and negative histogram values for different colors
        const positiveHistogram = histogramData.map(item => ({
          x: item.x,
          y: item.y > 0 ? item.y : null
        }));
        
        const negativeHistogram = histogramData.map(item => ({
          x: item.x,
          y: item.y < 0 ? item.y : null
        }));
        
        // Add positive histogram bars
        data.datasets.push({
          label: "Histogram (Positiv)",
          data: positiveHistogram,
          backgroundColor: "rgba(16, 185, 129, 0.6)", // Grün
          borderColor: "rgba(16, 185, 129, 0.8)",
          borderWidth: 1,
          pointRadius: 0,
          type: "bar",
          yAxisID: "y1",
        });
        
        // Add negative histogram bars
        data.datasets.push({
          label: "Histogram (Negativ)",
          data: negativeHistogram,
          backgroundColor: "rgba(239, 68, 68, 0.6)", // Rot
          borderColor: "rgba(239, 68, 68, 0.8)",
          borderWidth: 1,
          pointRadius: 0,
          type: "bar",
          yAxisID: "y1",
        });
      }

      // Bitcoin Power Law Datenaufbereitung
      if (selectedIndicator === "bitcoin-power-law") {
        data = {
          datasets: [
            {
              label: "Bitcoin-Preis",
              data: indicatorData.dates.map((date, index) => ({
                x: new Date(date),
                y: indicatorData.prices[index]
              })),
              borderColor: "#ffffff",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              borderWidth: 2,
              pointRadius: 0,
              yAxisID: "y",
              fill: false,
              tension: 0.1
            },
            {
              label: "Support",
              data: indicatorData.dates.map((date, index) => ({
                x: new Date(date),
                y: indicatorData.powerLawSupport[index]
              })),
              borderColor: "#32cd32",
              backgroundColor: "rgba(50, 205, 50, 0.1)",
              borderWidth: 2,
              pointRadius: 0,
              yAxisID: "y",
              fill: false,
              tension: 0.1
            },
            {
              label: "Robust Fit",
              data: indicatorData.dates.map((date, index) => ({
                x: new Date(date),
                y: indicatorData.powerLawRobust[index]
              })),
              borderColor: "#9932cc",
              backgroundColor: "rgba(153, 50, 204, 0.1)",
              borderWidth: 2,
              pointRadius: 0,
              yAxisID: "y",
              fill: false,
              tension: 0.1
            },
            {
              label: "Centre",
              data: indicatorData.dates.map((date, index) => ({
                x: new Date(date),
                y: indicatorData.powerLawCentre[index]
              })),
              borderColor: "#808080",
              backgroundColor: "rgba(128, 128, 128, 0.1)",
              borderWidth: 2,
              pointRadius: 0,
              yAxisID: "y",
              fill: false,
              tension: 0.1
            },
            {
              label: "Resistance",
              data: indicatorData.dates.map((date, index) => ({
                x: new Date(date),
                y: indicatorData.powerLawResistance[index]
              })),
              borderColor: "#ff0000",
              backgroundColor: "rgba(255, 0, 0, 0.1)",
              borderWidth: 2,
              pointRadius: 0,
              yAxisID: "y",
              fill: false,
              tension: 0.1
            }
          ]
        };
      }

      // ETH/BTC Rotationline Datenaufbereitung (einfacher BTC Chart wie Pi-Cycle)
      if (selectedIndicator === "eth-btc-rotationline") {
        data = {
          datasets: [
            {
              label: "Bitcoin-Preis",
              data: indicatorData.dates.map((date, index) => ({
                x: new Date(date),
                y: indicatorData.prices[index]
              })),
              borderColor: "#ffffff",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              borderWidth: 2,
              pointRadius: 0,
              yAxisID: "y",
              fill: false,
              tension: 0.1
            }
          ]
        };
      }

      // Chart erstellen
      chartInstance.current = new Chart(ctx, {
        type: "line",
        data: data,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              type: "time",
              time: {
                unit: "month",
                displayFormats: {
                  month: "MMM yyyy"
                }
              },
              adapters: {
                date: {
                  locale: de
                }
              },
              grid: {
                color: "rgba(255, 255, 255, 0.1)"
              },
              ticks: {
                color: "#9ca3af",
                maxRotation: 45,
                minRotation: 45
              }
            },
            y: {
              type: "logarithmic",
              position: "left",
              grid: {
                color: "rgba(255, 255, 255, 0.1)"
              },
              ticks: {
                color: "#9ca3af",
                callback: function(value, index, values) {
                  return "$" + value.toLocaleString();
                }
              }
            },
            y1: selectedIndicator === "macd-weekly" ? {
              type: "linear",
              position: "right",
              grid: {
                color: "rgba(255, 255, 255, 0.05)",
                drawOnChartArea: false
              },
              ticks: {
                color: "#9ca3af",
                font: {
                  weight: 'bold' // Fettere Schrift für bessere Sichtbarkeit
                }
              },
              display: true,
              // Stellen Sie sicher, dass die Y-Achse des MACD immer sichtbar ist
              afterFit: function(scaleInstance) {
                scaleInstance.width = 50; // Konstante Breite für die rechte Y-Achse
              }
            } : undefined
          },
          plugins: {
            legend: {
              position: "top",
              labels: {
                color: "#f9fafb",
                usePointStyle: true,
                padding: 20
              }
            },
            tooltip: {
              mode: "index",
              intersect: false,
              backgroundColor: "#1f2937",
              titleColor: "#f9fafb",
              bodyColor: "#f9fafb",
              borderColor: "#374151",
              borderWidth: 1,
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label || "";
                  if (label) {
                    label += ": ";
                  }
                  if (context.parsed.y !== null) {
                    label += "$" + context.parsed.y.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    });
                  }
                  return label;
                }
              }
            },
            zoom: {
              pan: {
                enabled: true,
                mode: 'x',
                // Entfernen des modifierKey, damit Pan mit linker Maustaste ohne Ctrl funktioniert
              },
              zoom: {
                wheel: {
                  enabled: true, // Zoom nur über Mausrad
                },
                pinch: {
                  enabled: true // Touch-Pinch für mobile Geräte
                },
                mode: 'x',
                // Drag-Zoom deaktivieren, da wir Drag für Pan verwenden
                drag: {
                  enabled: false
                }
              }
            }
          }
        }
      });
    }
  }, [indicatorData]);

  return (
    <div className="dashboard-container">
      <Sidebar active="indicators" />
      <div className="dashboard-content">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-white">Indikatoren</h1>
            <div className="flex items-center space-x-4">
              <button 
                onClick={resetZoom} 
                className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded flex items-center"
              >
                <FiRefreshCw className="mr-2" /> Reset Zoom
              </button>
              
              {indicatorData && (
                <div className="flex items-center bg-gray-800 text-gray-300 px-3 py-1 rounded border border-gray-700">
                  <FiClock className="mr-2 text-gray-400" size={14} />
                  <span className="text-gray-300 text-sm">
                    Aktualisiert: {new Date().toLocaleTimeString('de-DE')}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-white mb-4">Indikator auswählen</h2>
              <div className="space-y-4">
                {indicators.map(indicator => (
                  <div 
                    key={indicator.id}
                    className={`p-3 rounded cursor-pointer transition-colors ${
                      selectedIndicator === indicator.id 
                        ? "bg-yellow-600 text-white" 
                        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    }`}
                    onClick={() => setSelectedIndicator(indicator.id)}
                  >
                    {indicator.name}
                  </div>
                ))}
              </div>

              {indicatorData && indicatorData.current && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-white mb-3">Aktuelle Werte</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Bitcoin-Preis:</span>
                      <span className="text-white font-medium">
                        ${indicatorData.current && indicatorData.current.price !== undefined ? 
                          indicatorData.current.price.toLocaleString('de-DE', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }) : "N/A"}
                      </span>
                    </div>
                    
                    {selectedIndicator === "pi-cycle-top" && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-400">SMA-111:</span>
                          <span className="text-yellow-500 font-medium">
                            ${indicatorData.current && indicatorData.current.sma_111 !== undefined ? 
                              indicatorData.current.sma_111.toLocaleString('de-DE', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }) : "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">2 × SMA-350:</span>
                          <span className="text-green-500 font-medium">
                            ${indicatorData.current && indicatorData.current.sma_350x2 !== undefined ? 
                              indicatorData.current.sma_350x2.toLocaleString('de-DE', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }) : "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Distanz:</span>
                          <span className={`font-medium ${
                            indicatorData.current && indicatorData.current.distance_percentage !== undefined ?
                              (indicatorData.current.distance_percentage > 0 
                                ? "text-green-500" 
                                : "text-red-500")
                              : "text-gray-400"
                          }`}>
                            {indicatorData.current && indicatorData.current.distance_percentage !== undefined ? 
                              indicatorData.current.distance_percentage.toLocaleString('de-DE', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }) + "%" : "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Status:</span>
                          <span className={`font-medium ${
                            indicatorData.current && indicatorData.current.is_top !== undefined ?
                              (indicatorData.current.is_top 
                                ? "text-red-500" 
                                : "text-green-500")
                              : "text-gray-400"
                          }`}>
                            {indicatorData.current && indicatorData.current.is_top !== undefined ? 
                              (indicatorData.current.is_top ? "Pi-Cycle-Top erreicht!" : "Kein Pi-Cycle-Top") : "N/A"}
                          </span>
                        </div>
                      </>
                    )}
                    
                    {selectedIndicator === "golden-ratio-multiplier" && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-400">SMA-350:</span>
                          <span className="text-gray-200 font-medium">
                            ${indicatorData.current && indicatorData.current.sma350 !== undefined ? indicatorData.current.sma350.toLocaleString('de-DE', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }) : "N/A"}
                          </span>
                        </div>
                        
                        <div className="mt-3 mb-1 text-gray-400 text-sm">Multiplikatoren:</div>
                        
                        {indicatorData.multipliers && indicatorData.multipliers
                          // Filtern der Multiplikatoren auf 1, 1.6 und 2
                          .filter(multiplier => ["1", "1.6", "2"].includes(String(multiplier)))
                          .map(multiplier => {
                          const key = String(multiplier).replace(".", "_");
                          const multiplierData = indicatorData.current.multipliers[key];
                          
                          // Farben entsprechend dem Chart
                          const multiplierColors = {
                            "1": "#f5f5f5", // Weiß/Silber
                            "1_6": "#f59e0b", // Orange
                            "2": "#10b981", // Grün
                            "3": "#3b82f6", // Blau
                            "5": "#8b5cf6", // Lila
                            "8": "#ec4899", // Pink
                            "13": "#ef4444", // Rot
                            "21": "#7c3aed" // Dunkel-Lila
                          };
                          
                          return (
                            <div key={key} className="flex justify-between">
                              <span className="text-gray-400">{multiplier}× SMA-350:</span>
                              <div className="flex flex-col items-end">
                                <span style={{ color: multiplierColors[key] || "#ffffff" }} className="font-medium">
                                  ${multiplierData && multiplierData.value !== undefined ? multiplierData.value.toLocaleString('de-DE', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  }) : "N/A"}
                                </span>
                                <span className={`text-xs ${
                                  multiplierData && multiplierData.distance_percentage > 0 
                                    ? "text-green-500" 
                                    : "text-red-500"
                                }`}>
                                  {multiplierData && multiplierData.distance_percentage > 0 ? "+" : ""}
                                  {multiplierData && multiplierData.distance_percentage !== undefined ? multiplierData.distance_percentage.toLocaleString('de-DE', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  }) : "N/A"}%
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}

                    {selectedIndicator === "macd-weekly" && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Bitcoin-Preis:</span>
                          <span className="text-gray-200 font-medium">
                            ${indicatorData.current && indicatorData.current.btc_price !== undefined ? indicatorData.current.btc_price.toLocaleString('de-DE', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }) : "N/A"}
                          </span>
                        </div>
                        
                        <div className="mt-3 mb-1 text-gray-400 text-sm">MACD Werte:</div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-400">MACD-Linie:</span>
                          <span className="text-blue-500 font-medium">
                            {indicatorData.current && indicatorData.current.macd_line !== undefined ? indicatorData.current.macd_line.toLocaleString('de-DE', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }) : "N/A"}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-400">Signal-Linie:</span>
                          <span className="text-red-500 font-medium">
                            {indicatorData.current && indicatorData.current.signal_line !== undefined ? indicatorData.current.signal_line.toLocaleString('de-DE', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }) : "N/A"}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-400">Histogram:</span>
                          <span className={`font-medium ${
                            indicatorData.current && indicatorData.current.histogram !== undefined ?
                              (indicatorData.current.histogram > 0 
                                ? "text-green-500" 
                                : "text-red-500")
                              : "text-gray-400"
                          }`}>
                            {indicatorData.current && indicatorData.current.histogram !== undefined ? indicatorData.current.histogram.toLocaleString('de-DE', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }) : "N/A"}
                          </span>
                        </div>
                        
                        <div className="mt-3 mb-1 text-gray-400 text-sm">Aktueller Status:</div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-400">Signal:</span>
                          <span className={`font-medium ${
                            indicatorData.current && indicatorData.current.signal !== undefined ?
                              (indicatorData.current.signal === 'buy' 
                                ? "text-green-500" 
                                : indicatorData.current.signal === 'sell'
                                ? "text-red-500"
                                : "text-yellow-500")
                              : "text-gray-400"
                          }`}>
                            {indicatorData.current && indicatorData.current.signal !== undefined ? 
                              (indicatorData.current.signal === 'buy' 
                                ? "Kaufsignal" 
                                : indicatorData.current.signal === 'sell'
                                ? "Verkaufssignal"
                                : "Neutral") : "N/A"}
                          </span>
                        </div>
                      </>
                    )}

                    {selectedIndicator === "bitcoin-power-law" && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Bitcoin-Preis:</span>
                          <span className="text-white font-medium">
                            ${indicatorData.current && indicatorData.current.btc_price !== undefined ? indicatorData.current.btc_price.toLocaleString('de-DE', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }) : "N/A"}
                          </span>
                        </div>
                        
                        <div className="mt-3 mb-1 text-gray-400 text-sm">Power Law Corridor:</div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-400">Support:</span>
                          <span className="text-green-500 font-medium">
                            ${indicatorData.current && indicatorData.current.support !== undefined ? indicatorData.current.support.toLocaleString('de-DE', {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0
                            }) : "N/A"}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-400">Robust Fit:</span>
                          <span className="text-purple-500 font-medium">
                            ${indicatorData.current && indicatorData.current.robust !== undefined ? indicatorData.current.robust.toLocaleString('de-DE', {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0
                            }) : "N/A"}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-400">Centre:</span>
                          <span className="text-gray-500 font-medium">
                            ${indicatorData.current && indicatorData.current.centre !== undefined ? indicatorData.current.centre.toLocaleString('de-DE', {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0
                            }) : "N/A"}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-400">Resistance:</span>
                          <span className="text-red-500 font-medium">
                            ${indicatorData.current && indicatorData.current.resistance !== undefined ? indicatorData.current.resistance.toLocaleString('de-DE', {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0
                            }) : "N/A"}
                          </span>
                        </div>
                        
                        <div className="mt-3 mb-1 text-gray-400 text-sm">Power Law Position:</div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-400">Position im Korridor:</span>
                          <span className={`font-medium ${
                            indicatorData.current && indicatorData.current.position !== undefined ?
                              (indicatorData.current.position < 0.3 
                                ? "text-green-500" 
                                : indicatorData.current.position > 0.7
                                ? "text-red-500"
                                : "text-yellow-500")
                              : "text-gray-400"
                          }`}>
                            {indicatorData.current && indicatorData.current.position !== undefined ? 
                              `${(indicatorData.current.position * 100).toFixed(1)}%` : "N/A"}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-400">Signal:</span>
                          <span className={`font-medium ${
                            indicatorData.current && indicatorData.current.position !== undefined ?
                              (indicatorData.current.position < 0.3 
                                ? "text-green-500" 
                                : indicatorData.current.position > 0.7
                                ? "text-red-500"
                                : "text-yellow-500")
                              : "text-gray-400"
                          }`}>
                            {indicatorData.current && indicatorData.current.position !== undefined ? 
                              (indicatorData.current.position < 0.3 
                                ? "Günstiger Bereich" 
                                : indicatorData.current.position > 0.7
                                ? "Teurer Bereich"
                                : "Neutraler Bereich") : "N/A"}
                          </span>
                        </div>
                      </>
                    )}

                    {selectedIndicator === "eth-btc-rotationline" && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-400">ETH-Preis:</span>
                          <span className="text-blue-400 font-medium">
                            ${indicatorData.current && indicatorData.current.eth_price !== undefined ? indicatorData.current.eth_price.toLocaleString('de-DE', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }) : "N/A"}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-400">BTC-Preis:</span>
                          <span className="text-orange-400 font-medium">
                            ${indicatorData.current && indicatorData.current.btc_price !== undefined ? indicatorData.current.btc_price.toLocaleString('de-DE', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }) : "N/A"}
                          </span>
                        </div>
                        
                        <div className="mt-3 mb-1 text-gray-400 text-sm">ETH/BTC Verhältnis:</div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-400">Aktuelles Verhältnis:</span>
                          <span className="text-white font-bold text-lg">
                            {indicatorData.current && indicatorData.current.ratio !== undefined ? 
                              indicatorData.current.ratio.toFixed(6) : "N/A"}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-400">BTC Top Ziel:</span>
                          <span className="text-green-400 font-medium">
                            0.046500
                          </span>
                        </div>
                        
                        <div className="mt-3 mb-1 text-gray-400 text-sm">Rotation Status:</div>
                        
                        {/* Progress Bar für Verhältnis */}
                        <div className="mb-3">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-400">Fortschritt zum BTC Top:</span>
                            <span className="text-white">
                              {indicatorData.current && indicatorData.current.ratio !== undefined ? 
                                Math.min(100, (indicatorData.current.ratio / 0.0465 * 100)).toFixed(1) : "0"}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-700 rounded-full h-3">
                            <div 
                              className={`h-3 rounded-full transition-all duration-500 ${
                                indicatorData.current && indicatorData.current.ratio >= 0.0465 
                                  ? 'bg-green-400' 
                                  : indicatorData.current && indicatorData.current.ratio >= 0.035
                                  ? 'bg-yellow-400'
                                  : 'bg-blue-400'
                              }`}
                              style={{
                                width: `${indicatorData.current && indicatorData.current.ratio !== undefined ? 
                                  Math.min(100, (indicatorData.current.ratio / 0.0465 * 100)) : 0}%`
                              }}
                            ></div>
                          </div>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-400">Market Phase:</span>
                          <span className={`font-bold ${
                            indicatorData.current && indicatorData.current.ratio >= 0.0465 
                              ? "text-green-400" 
                              : indicatorData.current && indicatorData.current.ratio >= 0.035
                              ? "text-yellow-400"
                              : "text-blue-400"
                          }`}>
                            {indicatorData.current && indicatorData.current.ratio >= 0.0465 
                              ? "🚀 BTC TOP & ALT SEASON!" 
                              : indicatorData.current && indicatorData.current.ratio >= 0.035
                              ? "⚡ Approaching BTC Top"
                              : "📈 BTC Dominance Phase"}
                          </span>
                        </div>
                        
                        {indicatorData.current && indicatorData.current.ratio >= 0.0465 && (
                          <div className="mt-3 p-3 bg-green-600 bg-opacity-20 border border-green-500 rounded-lg">
                            <div className="text-green-400 font-bold text-center">
                              🎯 BTC TOP ERREICHT!
                            </div>
                            <div className="text-green-300 text-sm text-center mt-1">
                              Alt Season beginnt - Zeit für Altcoins!
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="md:col-span-3 bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-white mb-4">
                {selectedIndicator === "pi-cycle-top" 
                  ? "Pi-Cycle-Top-Indikator" 
                  : selectedIndicator === "golden-ratio-multiplier"
                  ? "Golden Ratio Multiplier"
                  : selectedIndicator === "macd-weekly"
                  ? "MACD Weekly Indikator"
                  : selectedIndicator === "bitcoin-power-law"
                  ? "Bitcoin Power Law"
                  : selectedIndicator === "eth-btc-rotationline"
                  ? "ETH/BTC Rotationline"
                  : "Indikator Chart"}
              </h2>
              
              {loading ? (
                <div className="flex items-center justify-center h-80">
                  <div className="loading-spinner"></div>
                </div>
              ) : error ? (
                <div className="bg-red-500 bg-opacity-20 border border-red-500 text-red-500 p-4 rounded">
                  {error}
                </div>
              ) : indicatorData ? (
                <div className="h-[500px]">
                  <canvas ref={chartRef}></canvas>
                </div>
              ) : (
                <div className="flex items-center justify-center h-80 text-gray-400">
                  Keine Daten verfügbar
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Trade Journal Component
const TradeJournal = () => {
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingTrade, setEditingTrade] = useState(null);
  
  // Form modal states
  const [showAddTradeModal, setShowAddTradeModal] = useState(false);
  
  // Partial close modal states
  const [showPartialCloseModal, setShowPartialCloseModal] = useState(false);
  const [partialCloseTrade, setPartialCloseTrade] = useState(null);
  const [partialQuantity, setPartialQuantity] = useState("");
  const [partialExitPrice, setPartialExitPrice] = useState("");
  const [partialFees, setPartialFees] = useState("");
  
  // Edit notes modal states
  const [showEditNotesModal, setShowEditNotesModal] = useState(false);
  const [editNotesTrade, setEditNotesTrade] = useState(null);
  const [editNotesText, setEditNotesText] = useState("");
  
  // Trade Info Modal states (expanded from notes)
  const [tradeInfoData, setTradeInfoData] = useState({
    notes: "",
    session: "",
    session_other: "",
    confirmations: [],
    confirmations_other: "",
    context: "",
    preparation: "",
    emotions: [],
    emotions_other: "",
    sttp: null,
    rating: 0,
    key_takeaway: "",
    chart_url_1: "",
    chart_url_2: ""
  });

  // Trade Overview Modal states
  const [showTradeOverviewModal, setShowTradeOverviewModal] = useState(false);
  const [overviewTrade, setOverviewTrade] = useState(null);

  // Sorting states
  const [sortField, setSortField] = useState('entry_date');
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'
  
  // New journal modal states
  const [showNewJournalModal, setShowNewJournalModal] = useState(false);
  const [newJournalName, setNewJournalName] = useState("");
  
  // Add margin modal states
  const [showAddMarginModal, setShowAddMarginModal] = useState(false);
  const [marginAmount, setMarginAmount] = useState("");
  const [marginDescription, setMarginDescription] = useState("");
  const [marginType, setMarginType] = useState("add"); // "add" or "remove"
  
  // Journal management states
  const [journals, setJournals] = useState([]);
  const [currentJournal, setCurrentJournal] = useState(null);
  const [loadingJournals, setLoadingJournals] = useState(true);
  
  // Margin data states
  const [marginData, setMarginData] = useState({
    total_deposited: 0,
    total_pnl: 0,
    current_balance: 0,
    margin_count: 0
  });
  
  // Live price tracking
  const [livePrices, setLivePrices] = useState({});
  
  // Form states
  const [symbol, setSymbol] = useState("");
  const [tradeType, setTradeType] = useState("long");
  const [entryPrice, setEntryPrice] = useState("");
  const [exitPrice, setExitPrice] = useState("");
  const [quantity, setQuantity] = useState("");
  const [stopLoss, setStopLoss] = useState("");
  const [takeProfit, setTakeProfit] = useState("");
  const [fees, setFees] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("open");
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Coin search states (for symbol field)
  const [coinsForTrade, setCoinsForTrade] = useState([]);
  const [coinSearchTerm, setCoinSearchTerm] = useState("");
  const [selectedTradeCoin, setSelectedTradeCoin] = useState(null);
  const [showCoinDropdown, setShowCoinDropdown] = useState(false);
  
  const token = localStorage.getItem("token");

  // Coin search functions (adapted from Transactions component)
  const searchCoinsForTrade = async (query) => {
    if (!query || query.length < 2) {
      setCoinsForTrade([]);
      setShowCoinDropdown(false);
      return;
    }
    
    try {
      // Increased limit to get more results
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/coins?query=${query}&limit=500`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setCoinsForTrade(response.data);
      setShowCoinDropdown(response.data.length > 0);
      
      console.log(`Found ${response.data.length} coins for trade matching "${query}"`);
      
      // Log first few results for debugging
      if (response.data.length > 0) {
        console.log("First 5 results:", response.data.slice(0, 5).map(coin => ({
          name: coin.name,
          symbol: coin.symbol,
          rank: coin.market_cap_rank
        })));
      }
    } catch (error) {
      console.error("Error searching coins for trade:", error);
      setCoinsForTrade([]);
      setShowCoinDropdown(false);
    }
  };

  const handleSelectTradeCoin = (coin) => {
    console.log("Selected trade coin:", coin);
    setSelectedTradeCoin(coin);
    setSymbol(coin.symbol.toUpperCase());
    setCoinSearchTerm(coin.name);
    setEntryPrice(coin.current_price || "");
    setCoinsForTrade([]);
    setShowCoinDropdown(false);
  };

  // Handle symbol input change
  const handleSymbolInputChange = (value) => {
    setCoinSearchTerm(value);
    searchCoinsForTrade(value);
    
    // If user types directly, also update symbol
    if (!selectedTradeCoin || value !== selectedTradeCoin.name) {
      setSymbol(value.toUpperCase());
    }
  };

  const fetchTrades = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/trade-journal`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setTrades(response.data);
      setError(null);
    } catch (error) {
      console.error("Error fetching trades:", error);
      setError("Failed to load trades");
    }
  };

  // Switch journal
  const handleJournalChange = (journalId) => {
    const journal = journals.find(j => j.id === journalId);
    if (journal) {
      setCurrentJournal(journal);
    }
  };

  // Create new journal
  const handleCreateNewJournal = async () => {
    if (!newJournalName.trim()) {
      alert("Bitte geben Sie einen Namen für das Journal ein.");
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/journals`,
        { name: newJournalName },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Add new journal to list and set as current
      setJournals(prev => [...prev, response.data]);
      setCurrentJournal(response.data);
      
      setShowNewJournalModal(false);
      setNewJournalName("");
      alert(`Journal "${newJournalName}" erfolgreich erstellt!`);
    } catch (error) {
      console.error("Error creating journal:", error);
      alert("Fehler beim Erstellen des Journals: " + (error.response?.data?.detail || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Also need a simple loadData function for after form submissions
  const loadData = async () => {
    if (currentJournal && token) {
      try {
        setLoading(true);
        
        // Load both trades and stats in parallel
        const [tradesResponse, statsResponse] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/trade-journal?journal_id=${currentJournal.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/trade-journal/stats?journal_id=${currentJournal.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
        ]);
        
        setTrades(tradesResponse.data);
        setStats(statsResponse.data);
        
        // Load margin data with the correct stats
        await loadMarginDataWithStats(statsResponse.data);
      } catch (error) {
        console.error("Error loading data:", error);
        alert("Fehler beim Laden der Daten: " + (error.response?.data?.detail || error.message));
      } finally {
        setLoading(false);
      }
    }
  };

  // Live price updates for open trades
  useEffect(() => {
    if (!currentJournal || !trades.length) return;
    
    const updateLivePrices = async () => {
      try {
        // Get all unique symbols from open trades (including partial)
        const openTrades = trades.filter(trade => trade.status === 'open' || trade.status === 'partial');
        console.log("All trades for live price updates:", openTrades.map(t => ({
          symbol: t.symbol,
          status: t.status,
          remaining_quantity: t.remaining_quantity
        })));
        
        const symbols = [...new Set(openTrades.map(trade => trade.symbol?.toLowerCase()))].filter(Boolean);
        
        if (symbols.length === 0) {
          console.log("No open trades found for live price updates");
          return;
        }
        
        console.log(`Updating live prices for ${symbols.length} symbols:`, symbols);
        
        // Fetch current prices for all symbols
        for (const symbol of symbols) {
          try {
            const response = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/coins?query=${symbol}&limit=1`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (response.data.length > 0) {
              const coin = response.data[0];
              console.log(`Live price for ${symbol.toUpperCase()}: $${coin.current_price}`);
              
              // Update live prices state
              setLivePrices(prev => ({
                ...prev,
                [symbol.toUpperCase()]: coin.current_price
              }));
            } else {
              console.log(`No price data found for ${symbol}`);
            }
          } catch (error) {
            console.error(`Error fetching price for ${symbol}:`, error);
          }
        }
        
        console.log("Current live prices state:", livePrices);
      } catch (error) {
        console.error("Error updating live prices:", error);
      }
    };
    
    // Initial price update
    updateLivePrices();
    
    // Set up interval for live updates every 60 seconds
    const priceUpdateInterval = setInterval(updateLivePrices, 60000);
    
    return () => clearInterval(priceUpdateInterval);
  }, [trades, currentJournal, token]);

  // Load journals on component mount
  useEffect(() => {
    const initializeJournals = async () => {
      try {
        setLoadingJournals(true);
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/journals`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setJournals(response.data);
        
        // Set current journal to the first one if none selected
        if (!currentJournal && response.data.length > 0) {
          setCurrentJournal(response.data[0]);
        }
      } catch (error) {
        console.error("Error loading journals:", error);
        alert("Fehler beim Laden der Journals: " + (error.response?.data?.detail || error.message));
      } finally {
        setLoadingJournals(false);
      }
    };
    
    if (token) {
      initializeJournals();
    }
  }, [token]); // Only depend on token, not currentJournal

  // Calculate unrealized P&L for open trades
  const calculateUnrealizedPnL = () => {
    let unrealizedPnL = 0;
    
    // Include both 'open' and 'partial' trades
    const openTrades = trades.filter(trade => trade.status === 'open' || trade.status === 'partial');
    
    console.log("Calculating unrealized P&L for trades:", openTrades.map(t => ({
      symbol: t.symbol,
      status: t.status,
      remaining_quantity: t.remaining_quantity,
      current_price: livePrices[t.symbol]
    })));
    
    for (const trade of openTrades) {
      const currentPrice = livePrices[trade.symbol];
      
      if (currentPrice && trade.entry_price && trade.remaining_quantity) {
        const entryPrice = trade.entry_price;
        const quantity = trade.remaining_quantity;
        const isLong = trade.trade_type === 'long';
        
        // Calculate unrealized P&L based on position type
        let tradeUnrealizedPnL;
        if (isLong) {
          tradeUnrealizedPnL = (currentPrice - entryPrice) * quantity;
        } else {
          tradeUnrealizedPnL = (entryPrice - currentPrice) * quantity;
        }
        
        console.log(`Unrealized P&L for ${trade.symbol}: $${tradeUnrealizedPnL.toFixed(2)} (${quantity} × (${currentPrice} - ${entryPrice}))`);
        
        unrealizedPnL += tradeUnrealizedPnL;
      } else {
        console.log(`Missing data for ${trade.symbol}: currentPrice=${currentPrice}, entry_price=${trade.entry_price}, remaining_quantity=${trade.remaining_quantity}`);
      }
    }
    
    console.log(`Total Unrealized P&L: $${unrealizedPnL.toFixed(2)}`);
    return unrealizedPnL;
  };

  // Load margin data for current journal
  const loadMarginData = async () => {
    if (currentJournal && token) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/journals/${currentJournal.id}/margin/total`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Get the total_pnl from stats (which works correctly) instead of margin API
        const total_pnl_from_stats = stats ? stats.total_pnl : 0;
        
        // Override the margin data with correct P&L from stats
        const correctedMarginData = {
          ...response.data,
          total_pnl: total_pnl_from_stats,
          current_balance: response.data.total_deposited + total_pnl_from_stats
        };
        
        console.log("Raw margin data:", response.data);
        console.log("Stats P&L:", total_pnl_from_stats);
        console.log("Corrected margin data:", correctedMarginData);
        
        setMarginData(correctedMarginData);
      } catch (error) {
        console.error("Error loading margin data:", error);
        // Don't alert for margin data errors, just log them
      }
    }
  };

  // Add margin function
  const handleAddMargin = async () => {
    if (!marginAmount.trim() || isNaN(parseFloat(marginAmount))) {
      alert("Bitte geben Sie einen gültigen Betrag ein.");
      return;
    }

    if (!currentJournal) {
      alert("Bitte wählen Sie ein Journal aus.");
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Calculate the final amount based on margin type
      const finalAmount = marginType === "add" ? parseFloat(marginAmount) : -parseFloat(marginAmount);
      
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/journals/${currentJournal.id}/margin`,
        {
          amount: finalAmount,
          description: marginDescription || null
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Reload margin data
      await loadMarginData();
      
      setShowAddMarginModal(false);
      setMarginAmount("");
      setMarginDescription("");
      setMarginType("add"); // Reset to default
      
      const actionText = marginType === "add" ? "hinzugefügt" : "abgezogen";
      const amountText = `€${parseFloat(marginAmount).toFixed(2)}`;
      alert(`Margin von ${amountText} erfolgreich ${actionText}!`);
    } catch (error) {
      console.error("Error managing margin:", error);
      let errorMessage = "Unbekannter Fehler";
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => err.msg || err).join(', ');
        } else {
          errorMessage = JSON.stringify(error.response.data.detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert("Fehler beim Hinzufügen der Margin: " + errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load trades when current journal changes  
  useEffect(() => {
    if (currentJournal && token) {
      const loadTradeData = async () => {
        try {
          setLoading(true);
          
          // Load trades and stats first, then margin data
          const [tradesResponse, statsResponse] = await Promise.all([
            axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/trade-journal?journal_id=${currentJournal.id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            ),
            axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/trade-journal/stats?journal_id=${currentJournal.id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            )
          ]);
          
          setTrades(tradesResponse.data);
          setStats(statsResponse.data);
          
          // Load margin data AFTER stats are loaded, so we can use stats.total_pnl
          await loadMarginDataWithStats(statsResponse.data);
        } catch (error) {
          console.error("Error loading data:", error);
          alert("Fehler beim Laden der Daten: " + (error.response?.data?.detail || error.message));
        } finally {
          setLoading(false);
        }
      };
      loadTradeData();
    } else if (!currentJournal) {
      setLoading(false);
    }
  }, [currentJournal, token]);

  // Helper function to load margin data with stats
  const loadMarginDataWithStats = async (statsData) => {
    if (currentJournal && token) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/journals/${currentJournal.id}/margin/total`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Use the P&L from stats (which works correctly)
        const total_pnl_from_stats = statsData ? statsData.total_pnl : 0;
        
        // Override the margin data with correct P&L from stats
        const correctedMarginData = {
          ...response.data,
          total_pnl: total_pnl_from_stats,
          current_balance: response.data.total_deposited + total_pnl_from_stats
        };
        
        console.log("Raw margin data:", response.data);
        console.log("Stats P&L:", total_pnl_from_stats);
        console.log("Corrected margin data:", correctedMarginData);
        
        setMarginData(correctedMarginData);
      } catch (error) {
        console.error("Error loading margin data:", error);
      }
    }
  };

  const resetForm = async () => {
    setSymbol("");
    setTradeType("long");
    setEntryPrice("");
    setExitPrice("");
    setQuantity("");
    setStopLoss("");
    setTakeProfit("");
    setFees("");
    setNotes("");
    setStatus("open");
    setEntryDate(new Date().toISOString().split('T')[0]);
    setEditingTrade(null);
    setShowAddTradeModal(false);
    
    // Reset coin search states
    setCoinSearchTerm("");
    setSelectedTradeCoin(null);
    setCoinsForTrade([]);
    setShowCoinDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError(null);
    setFormSuccess(null);

    try {
      // Check if journal is selected
      if (!currentJournal) {
        setFormError("Bitte wählen Sie ein Journal aus oder erstellen Sie ein neues.");
        return;
      }

      const tradeData = {
        journal_id: currentJournal.id,  // Add journal_id to trade data
        symbol: symbol.toUpperCase(),
        trade_type: tradeType,
        entry_price: parseFloat(entryPrice),
        exit_price: exitPrice ? parseFloat(exitPrice) : null,
        quantity: parseFloat(quantity),
        stop_loss: stopLoss ? parseFloat(stopLoss) : null,
        take_profit: takeProfit ? parseFloat(takeProfit) : null,
        fees: fees ? parseFloat(fees) : null,
        notes: null,
        status: "open",
        entry_date: new Date(entryDate).toISOString()
      };

      if (editingTrade) {
        // Update existing trade
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/trade-journal/${editingTrade.id}`,
          tradeData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        setFormSuccess("Trade updated successfully!");
      } else {
        // Create new trade
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/trade-journal`,
          tradeData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        setFormSuccess("Trade added successfully!");
      }

      await loadData();
      
      resetForm();
    } catch (error) {
      console.error("Error saving trade:", error);
      setFormError("Failed to save trade: " + (error.response?.data?.detail || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (trade) => {
    setEditingTrade(trade);
    setSymbol(trade.symbol);
    setCoinSearchTerm(trade.symbol.toUpperCase()); // Set the search term to show the symbol
    setShowCoinDropdown(false); // Hide dropdown for edit mode
    setTradeType(trade.trade_type);
    setEntryPrice(trade.entry_price.toString());
    setExitPrice(trade.exit_price ? trade.exit_price.toString() : "");
    setQuantity(trade.initial_quantity.toString()); // Use initial_quantity for editing
    setStopLoss(trade.stop_loss ? trade.stop_loss.toString() : "");
    setTakeProfit(trade.take_profit ? trade.take_profit.toString() : "");
    setFees(trade.fees ? trade.fees.toString() : "");
    setNotes(trade.notes || "");
    setStatus(trade.status);
    setEntryDate(trade.entry_date.split('T')[0]);
    setShowAddTradeModal(true);
  };

  const handleDelete = async (tradeId) => {
    if (!window.confirm("Are you sure you want to delete this trade?")) {
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/trade-journal/${tradeId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      await loadData();
    } catch (error) {
      console.error("Error deleting trade:", error);
      alert("Failed to delete trade");
    }
  };

  const handlePartialClose = (trade) => {
    setPartialCloseTrade(trade);
    setPartialQuantity("");
    setPartialExitPrice("");
    setPartialFees("");
    setShowPartialCloseModal(true);
  };

  const submitPartialClose = async () => {
    if (!partialQuantity || !partialExitPrice) {
      alert("Please fill in quantity and exit price");
      return;
    }

    const quantity = parseFloat(partialQuantity);
    const exitPrice = parseFloat(partialExitPrice);

    if (quantity <= 0 || quantity > partialCloseTrade.remaining_quantity) {
      alert(`Invalid quantity. Must be between 0 and ${partialCloseTrade.remaining_quantity}`);
      return;
    }

    try {
      setIsSubmitting(true);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/trade-journal/${partialCloseTrade.id}/partial-close`,
        {
          quantity: quantity,
          exit_price: exitPrice,
          fees: partialFees ? parseFloat(partialFees) : null,
          close_date: new Date().toISOString()
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setShowPartialCloseModal(false);
      setPartialCloseTrade(null);
      await loadData();
    } catch (error) {
      console.error("Error partial closing trade:", error);
      alert("Failed to partial close trade: " + (error.response?.data?.detail || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditNotes = (trade) => {
    setEditNotesTrade(trade);
    // Populate trade info data with existing values
    setTradeInfoData({
      notes: trade.notes || "",
      session: trade.session || "",
      session_other: trade.session_other || "",
      confirmations: trade.confirmations || [],
      confirmations_other: trade.confirmations_other || "",
      context: trade.context || "",
      preparation: trade.preparation || "",
      emotions: trade.emotions || [],
      emotions_other: trade.emotions_other || "",
      sttp: trade.sttp,
      rating: trade.rating || 0,
      key_takeaway: trade.key_takeaway || "",
      chart_url_1: trade.chart_url_1 || "",
      chart_url_2: trade.chart_url_2 || ""
    });
    setShowEditNotesModal(true);
  };

  const submitEditNotes = async () => {
    try {
      setIsSubmitting(true);
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/trade-journal/${editNotesTrade.id}/trade-info`,
        tradeInfoData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setShowEditNotesModal(false);
      setEditNotesTrade(null);
      await loadData();
    } catch (error) {
      console.error("Error updating trade info:", error);
      alert("Failed to update trade info: " + (error.response?.data?.detail || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper functions for Trade Info
  const handleDropdownChange = (field, value) => {
    setTradeInfoData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMultiSelectChange = (field, value, isChecked) => {
    setTradeInfoData(prev => {
      const currentArray = prev[field] || [];
      if (isChecked) {
        return {
          ...prev,
          [field]: [...currentArray, value]
        };
      } else {
        return {
          ...prev,
          [field]: currentArray.filter(item => item !== value)
        };
      }
    });
  };

  const openChartUrl = (url) => {
    if (url && url.trim()) {
      window.open(url, '_blank');
    }
  };

  // Trade Overview handlers
  const handleTradeOverview = (trade) => {
    setOverviewTrade(trade);
    setShowTradeOverviewModal(true);
  };

  // Sorting handlers
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortedTrades = (trades) => {
    return [...trades].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle different data types
      if (sortField === 'entry_date' || sortField === 'final_exit_date') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (sortField === 'symbol') {
        aValue = aValue?.toString().toLowerCase() || '';
        bValue = bValue?.toString().toLowerCase() || '';
      } else if (typeof aValue === 'number' || typeof bValue === 'number') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else {
        aValue = aValue?.toString().toLowerCase() || '';
        bValue = bValue?.toString().toLowerCase() || '';
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return '↕️'; // Default unsorted icon
    }
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // Dropdown options
  const sessionOptions = [
    "Pre-LDN 07:00-08:00",
    "LDN 08:00-13:30", 
    "Pre-NY 13:30-14:30",
    "NY AM 14:30-19:00",
    "Asia 00:00-05:00",
    "Weekend",
    "Mkt. Open Sun 22:00",
    "Other"
  ];

  const confirmationOptions = [
    "None",
    "RTZ",
    "RI", 
    "S/D Creation",
    "BIV",
    "Other"
  ];

  const contextOptions = [
    "Pro",
    "Counter",
    "Neutral",
    "WW",
    "PO3"
  ];

  const preparationOptions = [
    "Pre Planned",
    "Just on time",
    "Rushed"
  ];

  const emotionOptions = [
    "None i am a robot",
    "Excitement",
    "Confidence", 
    "Fear",
    "Doubt",
    "Hope",
    "Anxiety",
    "Eagerness",
    "Tension",
    "Greed",
    "Impatience",
    "Frustration",
    "Relief",
    "Euphoria",
    "Disappointment",
    "Regret",
    "Acceptance",
    "Pride",
    "Shame",
    "Determination",
    "Others"
  ];

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "N/A";
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return "N/A";
    return `${value.toFixed(2)}%`;
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <Sidebar active="trade-journal" />
        <div className="dashboard-content flex items-center justify-center">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Sidebar active="trade-journal" />
      <div className="dashboard-content">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold">Trade Journal</h1>
              
              {/* Journal Selector Dropdown */}
              {!loadingJournals && journals.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 text-sm">Journal:</span>
                  <select
                    value={currentJournal?.id || ""}
                    onChange={(e) => handleJournalChange(e.target.value)}
                    className="bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    {journals.map(journal => (
                      <option key={journal.id} value={journal.id}>
                        {journal.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {loadingJournals && (
                <div className="text-gray-400 text-sm">Lade Journals...</div>
              )}
            </div>
            
            <button
              onClick={() => setShowNewJournalModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Neues Journal hinzufügen
            </button>
          </div>

        {/* Statistics Overview */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-7 gap-6 mb-6">
            <div className="bg-gray-800 rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold text-white mb-2">Total Trades</h3>
              <p className="text-2xl font-bold text-blue-500">{stats.total_trades}</p>
            </div>
            <div className="bg-gray-800 rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold text-white mb-2">Account Balance</h3>
              <p className="text-2xl font-bold text-purple-500">{formatCurrency(marginData.current_balance)}</p>
              <div className="text-xs text-gray-400 mt-1">
                <div>Eingezahlt: {formatCurrency(marginData.total_deposited)}</div>
                <div>Durch Trades: {formatCurrency(marginData.total_pnl)}</div>
              </div>
            </div>
            <div className="bg-gray-800 rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold text-white mb-2">Win Rate</h3>
              <p className="text-2xl font-bold text-green-500">{formatPercentage(stats.win_rate)}</p>
            </div>
            <div className="bg-gray-800 rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold text-white mb-2">Total P&L</h3>
              <p className={`text-2xl font-bold ${stats.total_pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {formatCurrency(stats.total_pnl)}
              </p>
            </div>
            <div className="bg-gray-800 rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold text-white mb-2">Unrealized P&L</h3>
              <p className={`text-2xl font-bold ${calculateUnrealizedPnL() >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {formatCurrency(calculateUnrealizedPnL())}
              </p>
              <div className="text-xs text-gray-400 mt-1">
                {trades.filter(trade => trade.status === 'open' || trade.status === 'partial').length} offene Trades
              </div>
            </div>
            <div className="bg-gray-800 rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold text-white mb-2">Open Trades</h3>
              <p className="text-2xl font-bold text-yellow-500">{stats.open_trades}</p>
            </div>
            <div className="bg-gray-800 rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold text-white mb-2">Partial Trades</h3>
              <p className="text-2xl font-bold text-orange-500">{stats.partial_trades}</p>
            </div>
          </div>
        )}

        {/* Add New Trade and Add Margin Buttons */}
        <div className="mb-6 flex gap-4">
          <button
            onClick={() => {
              resetForm(); // Reset form first
              setShowAddTradeModal(true); // Then open modal
            }}
            className="bg-yellow-600 text-white px-6 py-3 rounded-lg hover:bg-yellow-700 font-semibold flex items-center gap-2"
          >
            <FiPlus size={20} />
            Add New Trade
          </button>
          
          <button
            onClick={() => setShowAddMarginModal(true)}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-semibold flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
            Manage Margin
          </button>
        </div>

        {/* Trades List */}
        <div className="bg-gray-800 rounded-lg shadow">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Trade History</h2>
            
            {error && (
              <div className="bg-red-900 border-l-4 border-red-500 text-red-300 p-4 mb-4">
                {error}
              </div>
            )}

            {trades.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No trades found. Add your first trade above!</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-300">
                  <thead className="text-xs text-gray-400 uppercase bg-gray-700">
                    <tr>
                      <th 
                        className="px-6 py-3 cursor-pointer hover:text-white select-none"
                        onClick={() => handleSort('symbol')}
                      >
                        Symbol {getSortIcon('symbol')}
                      </th>
                      <th 
                        className="px-6 py-3 cursor-pointer hover:text-white select-none"
                        onClick={() => handleSort('trade_type')}
                      >
                        Type {getSortIcon('trade_type')}
                      </th>
                      <th 
                        className="px-6 py-3 cursor-pointer hover:text-white select-none"
                        onClick={() => handleSort('entry_date')}
                      >
                        Entry Date {getSortIcon('entry_date')}
                      </th>
                      <th 
                        className="px-6 py-3 cursor-pointer hover:text-white select-none"
                        onClick={() => handleSort('entry_price')}
                      >
                        Entry Price {getSortIcon('entry_price')}
                      </th>
                      <th className="px-6 py-3">Current Price</th>
                      <th 
                        className="px-6 py-3 cursor-pointer hover:text-white select-none"
                        onClick={() => handleSort('initial_quantity')}
                      >
                        Initial/Remaining Qty {getSortIcon('initial_quantity')}
                      </th>
                      <th 
                        className="px-6 py-3 cursor-pointer hover:text-white select-none"
                        onClick={() => handleSort('total_profit_loss')}
                      >
                        Total P&L {getSortIcon('total_profit_loss')}
                      </th>
                      <th 
                        className="px-6 py-3 cursor-pointer hover:text-white select-none"
                        onClick={() => handleSort('status')}
                      >
                        Status {getSortIcon('status')}
                      </th>
                      <th 
                        className="px-6 py-3 cursor-pointer hover:text-white select-none"
                        onClick={() => handleSort('stop_loss')}
                      >
                        Stop Loss {getSortIcon('stop_loss')}
                      </th>
                      <th 
                        className="px-6 py-3 cursor-pointer hover:text-white select-none"
                        onClick={() => handleSort('take_profit')}
                      >
                        Take Profit {getSortIcon('take_profit')}
                      </th>
                      <th className="px-6 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedTrades(trades).map((trade) => (
                      <tr key={trade.id} className="bg-gray-800 border-b border-gray-700 hover:bg-gray-700">
                        <td className="px-6 py-4 font-medium text-white">{trade.symbol}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded text-xs ${
                            trade.trade_type === 'long' ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
                          }`}>
                            {trade.trade_type.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4">{new Date(trade.entry_date).toLocaleDateString()}</td>
                        <td className="px-6 py-4">{formatCurrency(trade.entry_price)}</td>
                        <td className="px-6 py-4">
                          {(trade.status === 'open' || trade.status === 'partial') && livePrices[trade.symbol] ? (
                            <div>
                              <div className="text-white">{formatCurrency(livePrices[trade.symbol])}</div>
                              {(() => {
                                const currentPrice = livePrices[trade.symbol];
                                const entryPrice = trade.entry_price;
                                const remainingQty = trade.remaining_quantity;
                                const isLong = trade.trade_type === 'long';
                                
                                // Percentage change calculation
                                const change = isLong ? 
                                  ((currentPrice - entryPrice) / entryPrice) * 100 :
                                  ((entryPrice - currentPrice) / entryPrice) * 100;
                                
                                // Dollar value calculation for unrealized P&L
                                const unrealizedPnL = isLong ? 
                                  (currentPrice - entryPrice) * remainingQty :
                                  (entryPrice - currentPrice) * remainingQty;
                                
                                const isPositive = change >= 0;
                                return (
                                  <div>
                                    <div className={`text-xs ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                                      {isPositive ? '+' : ''}{change.toFixed(2)}%
                                    </div>
                                    <div className={`text-xs ${unrealizedPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                      {unrealizedPnL >= 0 ? '+' : ''}{formatCurrency(unrealizedPnL)}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          ) : (
                            <div className="text-gray-400">
                              {(trade.status === 'open' || trade.status === 'partial') ? 'Loading...' : formatCurrency(trade.exit_price || 0)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <div>Initial: {trade.initial_quantity}</div>
                            <div className="text-yellow-400">Remaining: {trade.remaining_quantity}</div>
                            {trade.partial_closes && trade.partial_closes.length > 0 && (
                              <div className="text-gray-400 text-xs">
                                Partial closes: {trade.partial_closes.length}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className={`px-6 py-4 ${
                          trade.total_profit_loss === null ? 'text-gray-400' :
                          trade.total_profit_loss >= 0 ? 'text-green-500' : 'text-red-500'
                        }`}>
                          <div className="text-sm">
                            <div className="font-medium">
                              {trade.total_profit_loss !== null ? formatCurrency(trade.total_profit_loss) : "N/A"}
                            </div>
                            <div className={`text-xs ${
                              trade.total_profit_loss_percentage === null ? 'text-gray-400' :
                              trade.total_profit_loss_percentage >= 0 ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {trade.total_profit_loss_percentage !== null ? formatPercentage(trade.total_profit_loss_percentage) : ""}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded text-xs ${
                            trade.status === 'open' ? 'bg-yellow-900 text-yellow-300' :
                            trade.status === 'closed' ? 'bg-gray-900 text-gray-300' :
                            'bg-orange-900 text-orange-300'
                          }`}>
                            {trade.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {trade.stop_loss ? (
                            <div className="text-sm">
                              <div className="text-red-400 font-medium">{formatCurrency(trade.stop_loss)}</div>
                              {(trade.status === 'open' || trade.status === 'partial') && livePrices[trade.symbol] && (
                                <div className={`text-xs ${
                                  trade.trade_type === 'long' ? 
                                    (livePrices[trade.symbol] > trade.stop_loss ? 'text-gray-400' : 'text-red-300') :
                                    (livePrices[trade.symbol] < trade.stop_loss ? 'text-gray-400' : 'text-red-300')
                                }`}>
                                  {(() => {
                                    const diff = Math.abs(livePrices[trade.symbol] - trade.stop_loss);
                                    const percentage = (diff / trade.stop_loss) * 100;
                                    return `${formatCurrency(diff)} (${percentage.toFixed(1)}%)`;
                                  })()}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-gray-400 text-sm">-</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {trade.take_profit ? (
                            <div className="text-sm">
                              <div className="text-green-400 font-medium">{formatCurrency(trade.take_profit)}</div>
                              
                              {/* Risk/Reward Display */}
                              <div className="mt-1 space-y-1">
                                {trade.max_risk_reward && (
                                  <div className="text-xs text-blue-300">
                                    MAX RR: 1:{trade.max_risk_reward}
                                  </div>
                                )}
                                {trade.real_risk_reward && (
                                  <div className="text-xs text-orange-300">
                                    Real RR: 1:{trade.real_risk_reward}
                                  </div>
                                )}
                              </div>
                              
                              {(trade.status === 'open' || trade.status === 'partial') && livePrices[trade.symbol] && (
                                <div className={`text-xs mt-1 ${
                                  trade.trade_type === 'long' ? 
                                    (livePrices[trade.symbol] < trade.take_profit ? 'text-gray-400' : 'text-green-300') :
                                    (livePrices[trade.symbol] > trade.take_profit ? 'text-gray-400' : 'text-green-300')
                                }`}>
                                  {(() => {
                                    const diff = Math.abs(livePrices[trade.symbol] - trade.take_profit);
                                    const percentage = (diff / trade.take_profit) * 100;
                                    return `${formatCurrency(diff)} (${percentage.toFixed(1)}%)`;
                                  })()}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-gray-400 text-sm">
                              <div>-</div>
                              {/* Show RR even without take profit if we have stop loss */}
                              {trade.stop_loss && (
                                <div className="mt-1 space-y-1">
                                  {trade.max_risk_reward && (
                                    <div className="text-xs text-blue-300">
                                      MAX RR: 1:{trade.max_risk_reward}
                                    </div>
                                  )}
                                  {trade.real_risk_reward && (
                                    <div className="text-xs text-orange-300">
                                      Real RR: 1:{trade.real_risk_reward}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            {(trade.status === 'open' || trade.status === 'partial') && trade.remaining_quantity > 0 && (
                              <button
                                onClick={() => handlePartialClose(trade)}
                                className="text-yellow-500 hover:text-yellow-400 text-xs px-2 py-1 border border-yellow-500 rounded"
                                title="Partial Close"
                              >
                                Partial Close
                              </button>
                            )}
                            <button
                              onClick={() => handleEditNotes(trade)}
                              className="text-purple-500 hover:text-purple-400 text-xs px-2 py-1 border border-purple-500 rounded"
                              title="Edit Notes"
                            >
                              Notes
                            </button>
                            {trade.status === 'closed' && (
                              <button
                                onClick={() => handleTradeOverview(trade)}
                                className="text-green-500 hover:text-green-400 text-xs px-2 py-1 border border-green-500 rounded"
                                title="Trade Overview"
                              >
                                Trade Overview
                              </button>
                            )}
                            <button
                              onClick={() => handleEdit(trade)}
                              className="text-blue-500 hover:text-blue-400"
                              title="Edit"
                            >
                              <FiEdit size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(trade.id)}
                              className="text-red-500 hover:text-red-400"
                              title="Delete"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Add/Edit Trade Modal */}
        {showAddTradeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-semibold text-white mb-4">
                {editingTrade ? "Edit Trade" : "Add New Trade"}
              </h3>
              
              {formError && (
                <div className="bg-red-900 border-l-4 border-red-500 text-red-300 p-4 mb-4">
                  {formError}
                </div>
              )}
              
              {formSuccess && (
                <div className="bg-green-900 border-l-4 border-green-500 text-green-300 p-4 mb-4">
                  {formSuccess}
                </div>
              )}

              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative">
                  <label className="block text-gray-300 mb-2">Symbol *</label>
                  {editingTrade ? (
                    // Read-only display for editing existing trades
                    <div className="w-full p-2 border border-gray-600 bg-gray-600 text-gray-300 rounded">
                      <div className="flex items-center">
                        <span className="font-medium">{editingTrade.symbol.toUpperCase()}</span>
                        <span className="ml-2 text-sm text-gray-400">(Cannot be changed)</span>
                      </div>
                    </div>
                  ) : (
                    // Editable search for new trades
                    <>
                      <input
                        type="text"
                        value={coinSearchTerm}
                        onChange={(e) => handleSymbolInputChange(e.target.value)}
                        className="w-full p-2 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="Suche Bitcoin, Ethereum..."
                        required
                      />
                      
                      {/* Coin Dropdown */}
                      {showCoinDropdown && coinsForTrade.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                          {/* Show total results count */}
                          <div className="text-xs text-gray-400 p-2 border-b border-gray-700">
                            {coinsForTrade.length} Coins gefunden {coinsForTrade.length >= 250 && "(zeige erste 250)"}
                          </div>
                          
                          {coinsForTrade.slice(0, 20).map((coin) => (
                            <div
                              key={coin.id}
                              onClick={() => handleSelectTradeCoin(coin)}
                              className="flex items-center p-3 hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-b-0"
                            >
                              <img src={coin.image} alt={coin.name} className="w-6 h-6 mr-3" />
                              <div className="flex-1">
                                <div className="flex justify-between items-center">
                                  <span className="text-white font-medium">{coin.name}</span>
                                  <div className="text-right">
                                    <span className="text-gray-400 text-sm uppercase block">{coin.symbol}</span>
                                    {coin.market_cap_rank && (
                                      <span className="text-gray-500 text-xs">#{coin.market_cap_rank}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="text-green-400 text-sm">
                                  ${coin.current_price?.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 8 }) || 'N/A'}
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          {/* Show "load more" hint if there are many results */}
                          {coinsForTrade.length > 20 && (
                            <div className="text-xs text-gray-400 p-2 text-center border-t border-gray-700">
                              {coinsForTrade.length - 20} weitere Coins verfügbar - verfeinere deine Suche
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Show selected coin info */}
                  {selectedTradeCoin && (
                    <div className="mt-2 p-2 bg-gray-700 rounded flex items-center">
                      <img src={selectedTradeCoin.image} alt={selectedTradeCoin.name} className="w-5 h-5 mr-2" />
                      <span className="text-sm text-gray-300">
                        {selectedTradeCoin.name} ({selectedTradeCoin.symbol.toUpperCase()}) - 
                        ${selectedTradeCoin.current_price?.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">Trade Type *</label>
                  <select
                    value={tradeType}
                    onChange={(e) => setTradeType(e.target.value)}
                    className="w-full p-2 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="long">Long</option>
                    <option value="short">Short</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">Entry Date *</label>
                  <input
                    type="date"
                    value={entryDate}
                    onChange={(e) => setEntryDate(e.target.value)}
                    className="w-full p-2 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">Entry Price *</label>
                  <input
                    type="number"
                    step="0.00000001"
                    value={entryPrice}
                    onChange={(e) => setEntryPrice(e.target.value)}
                    className="w-full p-2 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="0.00"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">Exit Price</label>
                  <input
                    type="number"
                    step="0.00000001"
                    value={exitPrice}
                    onChange={(e) => setExitPrice(e.target.value)}
                    className="w-full p-2 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">Quantity *</label>
                  <input
                    type="number"
                    step="0.00000001"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className="w-full p-2 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="0.00"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">Stop Loss</label>
                  <input
                    type="number"
                    step="0.00000001"
                    value={stopLoss}
                    onChange={(e) => setStopLoss(e.target.value)}
                    className="w-full p-2 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">Take Profit</label>
                  <input
                    type="number"
                    step="0.00000001"
                    value={takeProfit}
                    onChange={(e) => setTakeProfit(e.target.value)}
                    className="w-full p-2 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">Fees</label>
                  <input
                    type="number"
                    step="0.01"
                    value={fees}
                    onChange={(e) => setFees(e.target.value)}
                    className="w-full p-2 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="0.00"
                  />
                </div>



                <div className="md:col-span-3 flex gap-4">
                  <button
                    type="submit"
                    className="bg-yellow-600 text-white px-6 py-2 rounded hover:bg-yellow-700 disabled:opacity-50"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : editingTrade ? "Update Trade" : "Add Trade"}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setShowAddTradeModal(false)}
                    className="bg-gray-600 text-white px-6 py-2 rounded hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Partial Close Modal */}
        {showPartialCloseModal && partialCloseTrade && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-semibold text-white mb-4">
                Partial Close: {partialCloseTrade.symbol}
              </h3>
              
              <div className="mb-4 text-gray-300">
                <p>Initial Quantity: {partialCloseTrade.initial_quantity}</p>
                <p>Remaining Quantity: {partialCloseTrade.remaining_quantity}</p>
                <p>Entry Price: {formatCurrency(partialCloseTrade.entry_price)}</p>
              </div>

              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Quantity to Close *</label>
                <input
                  type="number"
                  step="0.00000001"
                  value={partialQuantity}
                  onChange={(e) => setPartialQuantity(e.target.value)}
                  className="w-full p-2 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder={`Max: ${partialCloseTrade.remaining_quantity}`}
                  max={partialCloseTrade.remaining_quantity}
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Exit Price *</label>
                <input
                  type="number"
                  step="0.00000001"
                  value={partialExitPrice}
                  onChange={(e) => setPartialExitPrice(e.target.value)}
                  className="w-full p-2 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="0.00"
                />
              </div>

              <div className="mb-6">
                <label className="block text-gray-300 mb-2">Fees</label>
                <input
                  type="number"
                  step="0.01"
                  value={partialFees}
                  onChange={(e) => setPartialFees(e.target.value)}
                  className="w-full p-2 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="0.00"
                />
              </div>

              <div className="flex gap-4">
                <button
                  onClick={submitPartialClose}
                  className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Closing..." : "Partial Close"}
                </button>
                <button
                  onClick={() => setShowPartialCloseModal(false)}
                  className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* New Journal Modal */}
        {showNewJournalModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-semibold text-white mb-4">
                Neues Journal erstellen
              </h3>
              
              <div className="mb-6">
                <label className="block text-gray-300 mb-2">Journal Name</label>
                <input
                  type="text"
                  value={newJournalName}
                  onChange={(e) => setNewJournalName(e.target.value)}
                  className="w-full p-3 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="z.B. Crypto Trading 2024"
                  maxLength={50}
                />
                <p className="text-gray-400 text-sm mt-1">Maximal 50 Zeichen</p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleCreateNewJournal}
                  disabled={isSubmitting || !newJournalName.trim()}
                  className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded transition-colors duration-200"
                >
                  {isSubmitting ? "Erstelle..." : "Journal erstellen"}
                </button>
                <button
                  onClick={() => {
                    setShowNewJournalModal(false);
                    setNewJournalName("");
                  }}
                  className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Manage Margin Modal */}
        {showAddMarginModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-semibold text-white mb-4">
                Margin verwalten
              </h3>
              
              {/* Margin Type Selection */}
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Aktion wählen</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setMarginType("add")}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      marginType === "add" 
                        ? "bg-green-600 text-white" 
                        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Hinzufügen
                    </div>
                  </button>
                  <button
                    onClick={() => setMarginType("remove")}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      marginType === "remove" 
                        ? "bg-red-600 text-white" 
                        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                      Abziehen
                    </div>
                  </button>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">
                  Betrag (EUR) {marginType === "add" ? "hinzufügen" : "abziehen"}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={marginAmount}
                  onChange={(e) => setMarginAmount(e.target.value)}
                  className="w-full p-3 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="z.B. 1000.00"
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-300 mb-2">Beschreibung (optional)</label>
                <input
                  type="text"
                  value={marginDescription}
                  onChange={(e) => setMarginDescription(e.target.value)}
                  className="w-full p-3 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={marginType === "add" ? "z.B. Einzahlung vom Bankkonto" : "z.B. Auszahlung zum Bankkonto"}
                  maxLength={100}
                />
              </div>
              
              {/* Preview */}
              {marginAmount && !isNaN(parseFloat(marginAmount)) && (
                <div className={`mb-4 p-3 rounded-lg ${marginType === "add" ? "bg-green-900" : "bg-red-900"}`}>
                  <div className={`text-sm ${marginType === "add" ? "text-green-300" : "text-red-300"}`}>
                    <strong>Vorschau:</strong> €{parseFloat(marginAmount).toFixed(2)} wird {marginType === "add" ? "hinzugefügt" : "abgezogen"}
                  </div>
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={handleAddMargin}
                  disabled={isSubmitting || !marginAmount.trim() || parseFloat(marginAmount) <= 0}
                  className={`flex-1 hover:opacity-90 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded transition-colors duration-200 ${
                    marginType === "add" ? "bg-green-600" : "bg-red-600"
                  }`}
                >
                  {isSubmitting 
                    ? (marginType === "add" ? "Hinzufügen..." : "Abziehen...") 
                    : (marginType === "add" ? "Margin hinzufügen" : "Margin abziehen")
                  }
                </button>
                <button
                  onClick={() => {
                    setShowAddMarginModal(false);
                    setMarginAmount("");
                    setMarginDescription("");
                    setMarginType("add");
                  }}
                  className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Trade Info Modal (Extended Notes) */}
        {showEditNotesModal && editNotesTrade && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl font-semibold text-white mb-6">
                Trade Info: {editNotesTrade.symbol}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Left Column */}
                <div className="space-y-4">
                  
                  {/* Notes */}
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">Notes</label>
                    <textarea
                      value={tradeInfoData.notes}
                      onChange={(e) => handleDropdownChange('notes', e.target.value)}
                      className="w-full p-3 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="Trade notes..."
                      rows="3"
                    />
                  </div>

                  {/* Session */}
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">Session</label>
                    <select
                      value={tradeInfoData.session}
                      onChange={(e) => handleDropdownChange('session', e.target.value)}
                      className="w-full p-3 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    >
                      <option value="">Select Session</option>
                      {sessionOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    {tradeInfoData.session === "Other" && (
                      <input
                        type="text"
                        value={tradeInfoData.session_other}
                        onChange={(e) => handleDropdownChange('session_other', e.target.value)}
                        className="w-full mt-2 p-3 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="Custom session..."
                      />
                    )}
                  </div>

                  {/* Confirmations */}
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">Confirmations (Multiple)</label>
                    <div className="grid grid-cols-2 gap-2">
                      {confirmationOptions.map(option => (
                        <label key={option} className="flex items-center space-x-2 text-sm">
                          <input
                            type="checkbox"
                            checked={tradeInfoData.confirmations.includes(option)}
                            onChange={(e) => handleMultiSelectChange('confirmations', option, e.target.checked)}
                            className="rounded bg-gray-700 border-gray-600 text-yellow-500 focus:ring-yellow-500"
                          />
                          <span className="text-gray-300">{option}</span>
                        </label>
                      ))}
                    </div>
                    {tradeInfoData.confirmations.includes("Other") && (
                      <input
                        type="text"
                        value={tradeInfoData.confirmations_other}
                        onChange={(e) => handleDropdownChange('confirmations_other', e.target.value)}
                        className="w-full mt-2 p-3 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="Custom confirmations..."
                      />
                    )}
                  </div>

                  {/* Context */}
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">Context</label>
                    <select
                      value={tradeInfoData.context}
                      onChange={(e) => handleDropdownChange('context', e.target.value)}
                      className="w-full p-3 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    >
                      <option value="">Select Context</option>
                      {contextOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  {/* Preparation */}
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">Preparation</label>
                    <select
                      value={tradeInfoData.preparation}
                      onChange={(e) => handleDropdownChange('preparation', e.target.value)}
                      className="w-full p-3 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    >
                      <option value="">Select Preparation</option>
                      {preparationOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                </div>

                {/* Right Column */}
                <div className="space-y-4">

                  {/* Emotions */}
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">Emotions (Multiple)</label>
                    <div className="max-h-32 overflow-y-auto border border-gray-700 rounded p-2 bg-gray-700">
                      <div className="grid grid-cols-1 gap-1">
                        {emotionOptions.map(option => (
                          <label key={option} className="flex items-center space-x-2 text-sm">
                            <input
                              type="checkbox"
                              checked={tradeInfoData.emotions.includes(option)}
                              onChange={(e) => handleMultiSelectChange('emotions', option, e.target.checked)}
                              className="rounded bg-gray-600 border-gray-500 text-yellow-500 focus:ring-yellow-500"
                            />
                            <span className="text-gray-300">{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    {tradeInfoData.emotions.includes("Others") && (
                      <input
                        type="text"
                        value={tradeInfoData.emotions_other}
                        onChange={(e) => handleDropdownChange('emotions_other', e.target.value)}
                        className="w-full mt-2 p-3 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="Custom emotions..."
                      />
                    )}
                  </div>

                  {/* STTP */}
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">STTP</label>
                    <div className="flex gap-4">
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="sttp"
                          checked={tradeInfoData.sttp === true}
                          onChange={() => handleDropdownChange('sttp', true)}
                          className="text-yellow-500 focus:ring-yellow-500"
                        />
                        <span className="text-gray-300">YES</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="sttp"
                          checked={tradeInfoData.sttp === false}
                          onChange={() => handleDropdownChange('sttp', false)}
                          className="text-yellow-500 focus:ring-yellow-500"
                        />
                        <span className="text-gray-300">NO</span>
                      </label>
                    </div>
                  </div>

                  {/* Rating */}
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">Rating (1-5 Stars)</label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => handleDropdownChange('rating', star)}
                          className={`text-2xl ${star <= tradeInfoData.rating ? 'text-yellow-400' : 'text-gray-600'} hover:text-yellow-300`}
                        >
                          ★
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Key Takeaway */}
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">Single Key Takeaway</label>
                    <textarea
                      value={tradeInfoData.key_takeaway}
                      onChange={(e) => handleDropdownChange('key_takeaway', e.target.value)}
                      className="w-full p-3 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="Key learning from this trade..."
                      rows="3"
                    />
                  </div>

                  {/* Chart URLs */}
                  <div className="space-y-3">
                    <div>
                      <label className="block text-gray-300 mb-2 font-medium">Chart URL 1</label>
                      <div className="flex gap-2">
                        <input
                          type="url"
                          value={tradeInfoData.chart_url_1}
                          onChange={(e) => handleDropdownChange('chart_url_1', e.target.value)}
                          className="flex-1 p-3 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          placeholder="https://tradingview.com/..."
                        />
                        {tradeInfoData.chart_url_1 && (
                          <button
                            type="button"
                            onClick={() => openChartUrl(tradeInfoData.chart_url_1)}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Open
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-gray-300 mb-2 font-medium">Chart URL 2</label>
                      <div className="flex gap-2">
                        <input
                          type="url"
                          value={tradeInfoData.chart_url_2}
                          onChange={(e) => handleDropdownChange('chart_url_2', e.target.value)}
                          className="flex-1 p-3 border border-gray-700 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          placeholder="https://tradingview.com/..."
                        />
                        {tradeInfoData.chart_url_2 && (
                          <button
                            type="button"
                            onClick={() => openChartUrl(tradeInfoData.chart_url_2)}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Open
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex gap-4 mt-8 pt-6 border-t border-gray-700">
                <button
                  onClick={submitEditNotes}
                  className="bg-yellow-600 text-white px-6 py-3 rounded hover:bg-yellow-700 disabled:opacity-50 font-medium"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save Trade Info"}
                </button>
                <button
                  onClick={() => setShowEditNotesModal(false)}
                  className="bg-gray-600 text-white px-6 py-3 rounded hover:bg-gray-700 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Trade Overview Modal */}
        {showTradeOverviewModal && overviewTrade && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg p-6 max-w-5xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-semibold text-white">
                  Trade Report: {overviewTrade.symbol}
                </h3>
                <button
                  onClick={() => setShowTradeOverviewModal(false)}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  ×
                </button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                
                {/* Left Column - Trade Basics & Performance */}
                <div className="space-y-6">
                  
                  {/* Basic Trade Information */}
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-yellow-400 mb-3">Trade Details</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-400">Symbol:</span>
                        <span className="text-white ml-2 font-medium">{overviewTrade.symbol}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Type:</span>
                        <span className={`ml-2 font-medium ${overviewTrade.trade_type === 'long' ? 'text-green-400' : 'text-red-400'}`}>
                          {overviewTrade.trade_type?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400">Entry Date:</span>
                        <span className="text-white ml-2">{new Date(overviewTrade.entry_date).toLocaleDateString()}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Exit Date:</span>
                        <span className="text-white ml-2">{overviewTrade.final_exit_date ? new Date(overviewTrade.final_exit_date).toLocaleDateString() : 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Entry Price:</span>
                        <span className="text-white ml-2">{formatCurrency(overviewTrade.entry_price)}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Exit Price:</span>
                        <span className="text-white ml-2">{overviewTrade.exit_price ? formatCurrency(overviewTrade.exit_price) : 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Initial Quantity:</span>
                        <span className="text-white ml-2">{overviewTrade.initial_quantity}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Status:</span>
                        <span className="text-green-400 ml-2 font-medium">CLOSED</span>
                      </div>
                    </div>
                  </div>

                  {/* Risk Management */}
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-yellow-400 mb-3">Risk Management</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-400">Stop Loss:</span>
                        <span className="text-red-400 ml-2">{overviewTrade.stop_loss ? formatCurrency(overviewTrade.stop_loss) : 'Not Set'}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Take Profit:</span>
                        <span className="text-green-400 ml-2">{overviewTrade.take_profit ? formatCurrency(overviewTrade.take_profit) : 'Not Set'}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">MAX RR:</span>
                        <span className="text-blue-400 ml-2 font-medium">
                          {overviewTrade.max_risk_reward ? `1:${overviewTrade.max_risk_reward}` : 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400">Real RR:</span>
                        <span className="text-orange-400 ml-2 font-medium">
                          {overviewTrade.real_risk_reward ? `1:${overviewTrade.real_risk_reward}` : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Performance */}
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-yellow-400 mb-3">Performance</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Total P&L:</span>
                        <span className={`font-bold text-lg ${overviewTrade.total_profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {overviewTrade.total_profit_loss ? formatCurrency(overviewTrade.total_profit_loss) : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">P&L Percentage:</span>
                        <span className={`font-bold ${overviewTrade.total_profit_loss_percentage >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {overviewTrade.total_profit_loss_percentage ? `${overviewTrade.total_profit_loss_percentage.toFixed(2)}%` : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Fees:</span>
                        <span className="text-white">{overviewTrade.fees ? formatCurrency(overviewTrade.fees) : 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Partial Closes */}
                  {overviewTrade.partial_closes && overviewTrade.partial_closes.length > 0 && (
                    <div className="bg-gray-700 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-yellow-400 mb-3">
                        Partial Closes ({overviewTrade.partial_closes.length})
                      </h4>
                      <div className="space-y-2">
                        {overviewTrade.partial_closes.map((close, index) => (
                          <div key={index} className="bg-gray-600 rounded p-3 text-sm">
                            <div className="grid grid-cols-3 gap-3">
                              <div>
                                <span className="text-gray-400">Date:</span>
                                <div className="text-white">{new Date(close.close_date).toLocaleDateString()}</div>
                              </div>
                              <div>
                                <span className="text-gray-400">Price:</span>
                                <div className="text-white">{formatCurrency(close.exit_price)}</div>
                              </div>
                              <div>
                                <span className="text-gray-400">Quantity:</span>
                                <div className="text-white">{close.quantity}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                </div>

                {/* Right Column - Trade Psychology & Analysis */}
                <div className="space-y-6">

                  {/* Trading Session & Setup */}
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-yellow-400 mb-3">Trading Context</h4>
                    <div className="space-y-3 text-sm">
                      {overviewTrade.session && (
                        <div>
                          <span className="text-gray-400">Session:</span>
                          <span className="text-white ml-2">{overviewTrade.session}</span>
                          {overviewTrade.session_other && (
                            <span className="text-gray-300 ml-1">({overviewTrade.session_other})</span>
                          )}
                        </div>
                      )}
                      {overviewTrade.context && (
                        <div>
                          <span className="text-gray-400">Context:</span>
                          <span className="text-white ml-2">{overviewTrade.context}</span>
                        </div>
                      )}
                      {overviewTrade.preparation && (
                        <div>
                          <span className="text-gray-400">Preparation:</span>
                          <span className="text-white ml-2">{overviewTrade.preparation}</span>
                        </div>
                      )}
                      {overviewTrade.sttp !== null && (
                        <div>
                          <span className="text-gray-400">STTP:</span>
                          <span className={`ml-2 font-medium ${overviewTrade.sttp ? 'text-green-400' : 'text-red-400'}`}>
                            {overviewTrade.sttp ? 'YES' : 'NO'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Confirmations */}
                  {overviewTrade.confirmations && overviewTrade.confirmations.length > 0 && (
                    <div className="bg-gray-700 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-yellow-400 mb-3">Confirmations</h4>
                      <div className="flex flex-wrap gap-2">
                        {overviewTrade.confirmations.map((confirmation, index) => (
                          <span key={index} className="bg-blue-600 text-white text-xs px-2 py-1 rounded">
                            {confirmation}
                          </span>
                        ))}
                        {overviewTrade.confirmations_other && (
                          <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded">
                            {overviewTrade.confirmations_other}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Emotions */}
                  {overviewTrade.emotions && overviewTrade.emotions.length > 0 && (
                    <div className="bg-gray-700 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-yellow-400 mb-3">Emotions</h4>
                      <div className="flex flex-wrap gap-2">
                        {overviewTrade.emotions.map((emotion, index) => (
                          <span key={index} className="bg-purple-600 text-white text-xs px-2 py-1 rounded">
                            {emotion}
                          </span>
                        ))}
                        {overviewTrade.emotions_other && (
                          <span className="bg-purple-500 text-white text-xs px-2 py-1 rounded">
                            {overviewTrade.emotions_other}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Rating */}
                  {overviewTrade.rating && overviewTrade.rating > 0 && (
                    <div className="bg-gray-700 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-yellow-400 mb-3">Trade Rating</h4>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map(star => (
                          <span key={star} className={`text-2xl ${star <= overviewTrade.rating ? 'text-yellow-400' : 'text-gray-600'}`}>
                            ★
                          </span>
                        ))}
                        <span className="ml-2 text-white">({overviewTrade.rating}/5)</span>
                      </div>
                    </div>
                  )}

                  {/* Key Takeaway */}
                  {overviewTrade.key_takeaway && (
                    <div className="bg-gray-700 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-yellow-400 mb-3">Key Takeaway</h4>
                      <p className="text-white text-sm leading-relaxed">{overviewTrade.key_takeaway}</p>
                    </div>
                  )}

                  {/* Chart Links */}
                  {(overviewTrade.chart_url_1 || overviewTrade.chart_url_2) && (
                    <div className="bg-gray-700 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-yellow-400 mb-3">Chart Analysis</h4>
                      <div className="space-y-2">
                        {overviewTrade.chart_url_1 && (
                          <button
                            onClick={() => openChartUrl(overviewTrade.chart_url_1)}
                            className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 text-sm"
                          >
                            Open Chart Analysis 1
                          </button>
                        )}
                        {overviewTrade.chart_url_2 && (
                          <button
                            onClick={() => openChartUrl(overviewTrade.chart_url_2)}
                            className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 text-sm"
                          >
                            Open Chart Analysis 2
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {overviewTrade.notes && (
                    <div className="bg-gray-700 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-yellow-400 mb-3">Notes</h4>
                      <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">{overviewTrade.notes}</p>
                    </div>
                  )}

                </div>
              </div>

              {/* Close Button */}
              <div className="flex justify-center mt-8 pt-6 border-t border-gray-700">
                <button
                  onClick={() => setShowTradeOverviewModal(false)}
                  className="bg-gray-600 text-white px-8 py-3 rounded hover:bg-gray-700 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <GoogleOAuthProvider clientId="319761858374-feuhjbij90hqc9boii5q312du4qki7oc.apps.googleusercontent.com">
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route
              path="/"
              element={<PrivateRoute element={<Dashboard />} />}
            />
            <Route
              path="/transactions"
              element={<PrivateRoute element={<Transactions />} />}
            />
            <Route
              path="/portfolio"
              element={<PrivateRoute element={<Portfolio />} />}
            />
            <Route
              path="/debt"
              element={<PrivateRoute element={<DebtTracker />} />}
            />
            <Route
              path="/defi-position"
              element={<PrivateRoute element={<DefiPosition />} />}
            />
            <Route
              path="/vault-tracking"
              element={<PrivateRoute element={<VaultTracking />} />}
            />
            <Route
              path="/preis-alarme"
              element={<PrivateRoute element={<PreisAlarme />} />}
            />
            <Route
              path="/indicators"
              element={<PrivateRoute element={<Indicators />} />}
            />
            <Route
              path="/trade-journal"
              element={<PrivateRoute element={<TradeJournal />} />}
            />
            <Route
              path="/marktstruktur"
              element={<PrivateRoute element={<Marktstruktur />} />}
            />
            <Route
              path="/admin"
              element={<PrivateRoute element={<AdminPanel />} />}
            />
            <Route
              path="/profile"
              element={<PrivateRoute element={<Profile />} />}
            />
          </Routes>
        </Router>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

// PreisAlarme Component
const PreisAlarme = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAlert, setNewAlert] = useState({
    coin_id: '',
    coin_symbol: '',
    condition: 'above',
    target_price: ''
  });

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/preis-alarme`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch alerts');
      }
      
      const data = await response.json();
      setAlerts(data.alerts);
    } catch (err) {
      setError('Fehler beim Laden der Preis-Alarme');
      console.error('Error fetching alerts:', err);
    } finally {
      setLoading(false);
    }
  };

  const createAlert = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/preis-alarme`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAlert)
      });
      
      if (!response.ok) {
        throw new Error('Failed to create alert');
      }
      
      setNewAlert({ coin_id: '', coin_symbol: '', condition: 'above', target_price: '' });
      setShowCreateForm(false);
      fetchAlerts();
    } catch (err) {
      setError('Fehler beim Erstellen des Preis-Alarms');
      console.error('Error creating alert:', err);
    }
  };

  const deleteAlert = async (alertId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/preis-alarme/${alertId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete alert');
      }
      
      fetchAlerts();
    } catch (err) {
      setError('Fehler beim Löschen des Preis-Alarms');
      console.error('Error deleting alert:', err);
    }
  };

  const toggleAlert = async (alertId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/preis-alarme/${alertId}/toggle`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to toggle alert');
      }
      
      fetchAlerts();
    } catch (err) {
      setError('Fehler beim Ändern des Preis-Alarms');
      console.error('Error toggling alert:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar active="preis-alarme" />
        <div className="flex-1 p-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-400">Lädt Preis-Alarme...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <Sidebar active="preis-alarme" />
      <div className="flex-1 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Preis-Alarme</h1>
              <p className="text-gray-400">Verwalten Sie Ihre Cryptocurrency Preis-Benachrichtigungen</p>
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded flex items-center gap-2 transition-colors"
            >
              <FiPlus className="w-4 h-4" />
              Neuer Alarm
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-900/50 border border-red-700 text-red-400 p-4 rounded mb-6">
              {error}
            </div>
          )}

          {/* Create Form Modal */}
          {showCreateForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-gray-800 p-6 rounded-lg w-full max-w-md">
                <h3 className="text-xl font-bold text-white mb-4">Neuer Preis-Alarm</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-400 mb-2">Coin ID</label>
                    <input
                      type="text"
                      value={newAlert.coin_id}
                      onChange={(e) => setNewAlert({ ...newAlert, coin_id: e.target.value })}
                      placeholder="z.B. bitcoin, ethereum"
                      className="w-full bg-gray-700 text-white p-2 rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-400 mb-2">Symbol</label>
                    <input
                      type="text"
                      value={newAlert.coin_symbol}
                      onChange={(e) => setNewAlert({ ...newAlert, coin_symbol: e.target.value })}
                      placeholder="z.B. BTC, ETH"
                      className="w-full bg-gray-700 text-white p-2 rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-400 mb-2">Bedingung</label>
                    <select
                      value={newAlert.condition}
                      onChange={(e) => setNewAlert({ ...newAlert, condition: e.target.value })}
                      className="w-full bg-gray-700 text-white p-2 rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
                    >
                      <option value="above">Über</option>
                      <option value="below">Unter</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-gray-400 mb-2">Zielpreis ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newAlert.target_price}
                      onChange={(e) => setNewAlert({ ...newAlert, target_price: e.target.value })}
                      placeholder="0.00"
                      className="w-full bg-gray-700 text-white p-2 rounded border border-gray-600 focus:border-yellow-500 focus:outline-none"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={createAlert}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded flex-1 transition-colors"
                  >
                    Erstellen
                  </button>
                  <button
                    onClick={() => setShowCreateForm(false)}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded flex-1 transition-colors"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Alerts List */}
          <div className="space-y-4">
            {alerts.length === 0 ? (
              <div className="text-center py-12">
                <FiClock className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-400 mb-2">Keine Preis-Alarme</h3>
                <p className="text-gray-500 mb-4">Sie haben noch keine Preis-Alarme erstellt.</p>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-2 rounded transition-colors"
                >
                  Ersten Alarm erstellen
                </button>
              </div>
            ) : (
              alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`bg-gray-800 p-6 rounded-lg border ${
                    alert.is_active ? 'border-gray-700' : 'border-gray-600 opacity-60'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-3 h-3 rounded-full ${alert.is_active ? 'bg-green-500' : 'bg-gray-500'}`} />
                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          {alert.coin_symbol.toUpperCase()}
                        </h3>
                        <p className="text-gray-400">
                          {alert.condition === 'above' ? 'Über' : 'Unter'} ${alert.target_price.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => toggleAlert(alert.id)}
                        className={`px-3 py-1 rounded text-sm transition-colors ${
                          alert.is_active 
                            ? 'bg-green-600 hover:bg-green-700 text-white' 
                            : 'bg-gray-600 hover:bg-gray-700 text-white'
                        }`}
                      >
                        {alert.is_active ? 'Aktiv' : 'Inaktiv'}
                      </button>
                      <button
                        onClick={() => deleteAlert(alert.id)}
                        className="text-red-400 hover:text-red-300 p-2 transition-colors"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="mt-3 text-sm text-gray-500">
                    Erstellt: {new Date(alert.created_at).toLocaleDateString('de-DE')}
                    {alert.triggered_at && (
                      <span className="ml-4">
                        Ausgelöst: {new Date(alert.triggered_at).toLocaleDateString('de-DE')}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;